/**
 * portfolioKit.ts
 *
 * Portfolio Kit (Product #3) — content layer for both tracks.
 *
 * Track B — Portfolio Projects Playbook:
 *   3 project archetypes (RAG Agent / Multi-Agent Researcher / HITL Workflow Agent),
 *   each mapped to the 7-domain framework + a prescriptive GitHub README template
 *   + a deployment & observability checklist.
 *
 * Track A — Portfolio Site Builder:
 *   Lovable prompt kit + Supabase admin-panel schema for the AI-chatbot / Honest-Fit-
 *   Assessment pattern. Public exemplar site at /portfolio/demo.
 *
 * Design principles:
 *   - Hiring managers in 2026 spend more time on a candidate's GitHub than their
 *     resume. The Playbook pushes users to build production-shaped artifacts, not
 *     LLM-course completions.
 *   - Every archetype has: problem statement, architecture sketch, eval harness,
 *     HITL pattern, failure-mode list, 7-domain evidence rollup.
 *   - Free tier sees: overview + basic README template.
 *   - All Access tier unlocks: full archetype deep-dives + Lovable prompt +
 *     admin-panel schema + deployment checklist.
 */

import { Localized, L } from "../utils/localized";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type TierGate = "free" | "all_access";

export interface ArchitectureNode {
  id: string;
  label: string;
  role: string; // "LLM", "Tool", "Store", "Human", "Observer"
}

export interface ArchitectureEdge {
  from: string;
  to: string;
  label?: string;
}

export interface EvalMetric {
  name: string;
  what: string;
  howMeasured: string;
  targetRange: string;
}

export interface FailureMode {
  name: string;
  trigger: string;
  mitigation: string;
  severity: "low" | "medium" | "high" | "critical";
}

export interface DomainEvidence {
  domain: "D1" | "D2" | "D3" | "D4" | "D5" | "D6" | "D7";
  domainName: string;
  howThisProjectShows: string;
}

export interface ProjectArchetype {
  slug: string;
  title: string;
  tagline: string;
  tier: TierGate;
  estimatedBuildTime: string; // "2–3 weekends"
  difficulty: "intermediate" | "senior" | "staff";
  callbackSignal: "high" | "very_high"; // how strongly this gets recruiter callbacks in 2026

  problem: {
    headline: string;
    detail: string;
    whyItMatters: string;
  };

  architecture: {
    summary: string;
    mermaidDiagram: string; // raw mermaid string for the component to render
    components: ArchitectureNode[];
    keyEdges: ArchitectureEdge[];
  };

  techStack: {
    required: string[];
    optional: string[];
    avoidIfNovice: string[];
  };

  buildPhases: {
    phase: string;
    goal: string;
    durationEstimate: string;
  }[];

  evalHarness: {
    summary: string;
    metrics: EvalMetric[];
    datasetGuidance: string;
  };

  hitlPattern?: {
    triggers: string[]; // events that pause for human review
    humanDecisions: string[]; // what the human chooses
    autoResumeRules: string; // what happens after human decides
  };

  failureModes: FailureMode[];

  deployment: {
    minimumViable: string;
    observabilityMinimum: string[];
    costCeiling: string;
  };

  domainEvidence: DomainEvidence[];

  portfolioFraming: {
    oneLineDescription: string;  // for the portfolio card
    metricsToHighlight: string[]; // what to show on the card
    antiPattern: string;          // what NOT to claim
  };

  repoStructure: string[]; // proposed top-level paths
}

// ---------------------------------------------------------------------------
// Archetype 1 — RAG Agent (the gateway project)
// ---------------------------------------------------------------------------

export const RAG_AGENT: Localized<ProjectArchetype> = {
  en: {
  slug: "rag-agent",
  title: "Agentic RAG — Domain-Specific Research Agent",
  tagline:
    "A RAG system that plans queries, routes to the right retriever, and cites sources — not a chatbot that pastes doc chunks.",
  tier: "free", // overview is free; deep-dive is all_access but exposed as read-only preview
  estimatedBuildTime: "2–3 weekends",
  difficulty: "intermediate",
  callbackSignal: "very_high",

  problem: {
    headline:
      "Naive RAG (embed-retrieve-stuff-into-prompt) collapses on ambiguous queries, multi-hop questions, and out-of-corpus asks.",
    detail:
      "Real queries need decomposition, query rewriting, multi-step retrieval, and groundedness checking. A hiring manager can tell in 30 seconds whether you built a real RAG system or copied a Hugging Face example.",
    whyItMatters:
      "RAG is the #1 in-demand AI engineering skill in 2026. Every enterprise needs it. Most candidates ship the toy version — shipping the production-shaped version separates you.",
  },

  architecture: {
    summary:
      "A planner agent decomposes the query, routes sub-queries to specialized retrievers (vector + keyword + structured), grades retrieved chunks for relevance, rewrites if grounding is weak, and returns a cited answer. Unanswerable queries escalate to a human-in-the-loop review.",
    mermaidDiagram: `graph LR
  U[User query] --> P[Planner Agent]
  P -->|decompose| SQ[Sub-queries]
  SQ --> VR[Vector Retriever]
  SQ --> KR[Keyword Retriever]
  SQ --> DR[Structured DB Retriever]
  VR --> G[Relevance Grader]
  KR --> G
  DR --> G
  G -->|pass| S[Synthesizer]
  G -->|fail| RW[Query Rewriter]
  RW --> SQ
  S --> GC[Groundedness Check]
  GC -->|grounded| A[Answer + Citations]
  GC -->|ungrounded| H[HITL Escalation]
  A --> T[(Tracing: Langfuse)]
  GC --> T`,
    components: [
      { id: "planner", label: "Planner Agent", role: "LLM" },
      { id: "vector_retriever", label: "Vector Retriever", role: "Tool" },
      { id: "keyword_retriever", label: "Keyword Retriever (BM25)", role: "Tool" },
      { id: "structured_retriever", label: "Structured DB Retriever", role: "Tool" },
      { id: "grader", label: "Relevance Grader", role: "LLM" },
      { id: "rewriter", label: "Query Rewriter", role: "LLM" },
      { id: "synthesizer", label: "Synthesizer + Citer", role: "LLM" },
      { id: "groundedness", label: "Groundedness Check", role: "LLM" },
      { id: "hitl", label: "HITL Escalation Queue", role: "Human" },
      { id: "trace", label: "Langfuse Tracing", role: "Observer" },
    ],
    keyEdges: [
      { from: "planner", to: "vector_retriever", label: "sub-query" },
      { from: "grader", to: "rewriter", label: "rewrite if weak" },
      { from: "groundedness", to: "hitl", label: "escalate if ungrounded" },
    ],
  },

  techStack: {
    required: [
      "Python 3.11+",
      "LangGraph (or raw Python if you want to show framework-free skills)",
      "pgvector or Qdrant for vector store",
      "A grounded-answer evaluator — Ragas or custom",
      "Langfuse or LangSmith for tracing",
      "FastAPI for the serve layer",
    ],
    optional: [
      "Cohere Rerank or cross-encoder reranker",
      "Elasticsearch or OpenSearch for hybrid retrieval",
      "Postgres for the HITL queue",
    ],
    avoidIfNovice: [
      "Don't start with AutoGen or Semantic Kernel — they add orchestration you don't need yet",
      "Don't use OpenAI Assistants API — it hides the retrieval layer you're trying to demonstrate",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Corpus", goal: "Pick a domain corpus (~500–5,000 docs). Public options: arXiv AI papers, SEC 10-K filings, your own Obsidian vault. Document provenance.", durationEstimate: "1 weekend" },
    { phase: "Phase 2 — Baseline RAG", goal: "Naive retrieval + prompt stuffing. Measure baseline on 30–50 hand-labeled queries. Save the scores — they'll make your 'before/after' story in the README.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Agentic Layer", goal: "Add planner, grader, rewriter, groundedness check. Re-run evals. Target +15–30% on grounded-answer rate.", durationEstimate: "1 weekend" },
    { phase: "Phase 4 — HITL + Tracing", goal: "Add escalation queue for ungrounded answers. Wire Langfuse. Ship traces publicly.", durationEstimate: "3–5 evenings" },
    { phase: "Phase 5 — Deploy + Demo", goal: "Fly.io / Railway / Modal deployment. Public demo URL. Loom walkthrough.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "Hand-label 30–50 queries across 5 categories: factual single-hop, multi-hop, ambiguous, out-of-corpus, adversarial. Run evals after every agentic-layer change. Publish results in README.",
    metrics: [
      { name: "Grounded Answer Rate", what: "% of answers whose claims are supported by retrieved context", howMeasured: "Ragas faithfulness metric or hand-check for first 30", targetRange: "Baseline 50–65%; Agentic target 80%+" },
      { name: "Citation Precision", what: "Do cited sources actually contain the claim", howMeasured: "Hand-check random sample of 20", targetRange: "90%+" },
      { name: "Refusal Rate (out-of-corpus)", what: "% of out-of-corpus questions correctly refused vs hallucinated", howMeasured: "Your 5 out-of-corpus seeds + grader", targetRange: "100% refused — any hallucination is a critical failure" },
      { name: "p50 / p95 Latency", what: "End-to-end answer latency", howMeasured: "Langfuse or your own timer", targetRange: "p50 < 4s, p95 < 10s" },
      { name: "Cost per Query", what: "LLM + vector-store dollars per answered query", howMeasured: "Langfuse cost tracking", targetRange: "< $0.05 target, < $0.15 ceiling" },
    ],
    datasetGuidance:
      "Your query set is a portfolio artifact in itself. Commit it to the repo as eval_queries.jsonl. A hiring manager who sees a real eval set takes you 10× more seriously than one who sees 'worked in testing.'",
  },

  hitlPattern: {
    triggers: [
      "Groundedness check score < 0.6",
      "Retrieval returns fewer than 3 relevant chunks after 2 rewrites",
      "User query flagged by a safety classifier",
    ],
    humanDecisions: [
      "Approve the agent's best-effort answer with a 'low-confidence' badge",
      "Write a manual answer that gets added to the corpus for next time",
      "Mark the query as out-of-scope and decline",
    ],
    autoResumeRules:
      "Once the human decides, the system emails the original user, adds the resolved query+answer to a 'supervised corpus' (separate from the primary corpus), and logs the decision to improve the groundedness threshold over time.",
  },

  failureModes: [
    { name: "Confident Hallucination", trigger: "Retrieved context is tangentially related — LLM weaves plausible-but-wrong synthesis", mitigation: "Groundedness check scoring + refusal pattern + citation requirement in prompt", severity: "critical" },
    { name: "Over-Retrieval", trigger: "Planner over-decomposes a simple query into 8 sub-queries, cost explodes", mitigation: "Cap sub-queries at 3; show planner's own 'is decomposition needed' gate", severity: "high" },
    { name: "Stale Corpus", trigger: "Corpus embeddings drift as documents update", mitigation: "Versioned chunks + nightly delta re-index + staleness tracking per chunk", severity: "medium" },
    { name: "Prompt Injection via Documents", trigger: "Retrieved doc contains 'Ignore previous instructions'", mitigation: "Retrieved content goes inside tagged XML blocks; system prompt explicitly treats tagged content as untrusted data", severity: "high" },
    { name: "Rewriter Oscillation", trigger: "Rewriter keeps rewriting the same failing query in a loop", mitigation: "Max-rewrite counter (≤2) then HITL escalation", severity: "medium" },
  ],

  deployment: {
    minimumViable: "A public demo URL (Fly.io, Railway, or Modal) with a read-only mode. Don't ship fully open write access — rate-limit aggressively or require a login.",
    observabilityMinimum: [
      "Langfuse or LangSmith dashboard — linked publicly (screenshots in README)",
      "Per-query cost + latency visible",
      "Eval results re-runnable in CI",
      "Error rate and HITL escalation rate tracked",
    ],
    costCeiling: "Pick a monthly cap ($20–50 is fine for a portfolio project). Document it. Use a cheaper model (Haiku/Mini) in the grader and rewriter loops — only the synthesizer needs a flagship model.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Distinct system prompts per agent (planner, grader, synthesizer) with explicit untrusted-context tagging — not one mega-prompt" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "Clear pattern choice: planner-executor with conditional rewrite, not a freeform ReAct loop. Pattern choice is justified in README." },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Three retrievers (vector / keyword / structured) used conditionally based on query type — demonstrates tool-choice reasoning" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Hand-labeled eval set, before/after metrics, CI-runnable harness, trace dashboard — the credibility tell" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Corpus → retrieval → grading → synthesis → groundedness pipeline with failure-mode documentation" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Untrusted-content handling, prompt-injection mitigation, refusal-on-out-of-corpus, HITL for low-confidence answers" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Cost ceiling, latency SLOs, public demo URL, user-visible confidence badges — not a science project" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "Agentic RAG over [your domain corpus] — 30-query eval set, 84% grounded-answer rate, Langfuse traces public.",
    metricsToHighlight: [
      "Grounded-answer rate: baseline → agentic (show the lift)",
      "p95 latency",
      "Cost per query",
      "Refusal rate on out-of-corpus (should be 100%)",
    ],
    antiPattern:
      "Do NOT claim 'production-grade' unless it's actually deployed and observable. Do NOT cite a single happy-path demo as proof it works. Do NOT hide the eval set — that's the whole credibility engine.",
  },

  repoStructure: [
    "README.md (architecture diagram, eval table, deployment URL, Loom link)",
    "corpus/ (with provenance notes)",
    "agents/ (planner, grader, rewriter, synthesizer, groundedness_check)",
    "retrievers/ (vector, keyword, structured)",
    "eval/ (eval_queries.jsonl, run_evals.py, results/)",
    "hitl/ (escalation_queue.py, review_ui/)",
    "deploy/ (Dockerfile, fly.toml or railway.toml)",
    "traces/ (sample Langfuse trace screenshots + links)",
    ".github/workflows/ (CI running evals on PR)",
  ],
  },
  es: {
    "slug": "rag-agent",
    "title": "Agentic RAG — Agente de investigación específico de dominio",
    "tagline": "Un sistema RAG que planifica consultas, enruta al recuperador adecuado y cita las fuentes — no un chatbot que pega fragmentos de documentos.",
    "tier": "free",
    "estimatedBuildTime": "2–3 fines de semana",
    "difficulty": "intermediate",
    "callbackSignal": "very_high",
    "problem": {
      "headline": "El RAG ingenuo (incrustar-recuperar-meter-en-el-prompt) se derrumba ante consultas ambiguas, preguntas de múltiples saltos y solicitudes fuera del corpus.",
      "detail": "Las consultas reales requieren descomposición, reescritura de consultas, recuperación en múltiples pasos y verificación de fundamentación. Un responsable de contratación puede distinguir en 30 segundos si construiste un sistema RAG real o copiaste un ejemplo de Hugging Face.",
      "whyItMatters": "RAG es la habilidad de ingeniería de IA más demandada en 2026. Toda empresa la necesita. La mayoría de los candidatos entregan la versión de juguete — entregar la versión con forma de producción te distingue."
    },
    "architecture": {
      "summary": "Un agente planificador descompone la consulta, enruta las subconsultas a recuperadores especializados (vectorial + palabra clave + estructurado), califica los fragmentos recuperados por relevancia, reescribe si la fundamentación es débil y devuelve una respuesta con citas. Las consultas sin respuesta se escalan a una revisión con humano en el bucle.",
      "mermaidDiagram": "graph LR\n  U[User query] --> P[Planner Agent]\n  P -->|decompose| SQ[Sub-queries]\n  SQ --> VR[Vector Retriever]\n  SQ --> KR[Keyword Retriever]\n  SQ --> DR[Structured DB Retriever]\n  VR --> G[Relevance Grader]\n  KR --> G\n  DR --> G\n  G -->|pass| S[Synthesizer]\n  G -->|fail| RW[Query Rewriter]\n  RW --> SQ\n  S --> GC[Groundedness Check]\n  GC -->|grounded| A[Answer + Citations]\n  GC -->|ungrounded| H[HITL Escalation]\n  A --> T[(Tracing: Langfuse)]\n  GC --> T",
      "components": [
        {
          "id": "planner",
          "label": "Agente planificador",
          "role": "LLM"
        },
        {
          "id": "vector_retriever",
          "label": "Recuperador vectorial",
          "role": "Tool"
        },
        {
          "id": "keyword_retriever",
          "label": "Recuperador por palabra clave (BM25)",
          "role": "Tool"
        },
        {
          "id": "structured_retriever",
          "label": "Recuperador de BD estructurada",
          "role": "Tool"
        },
        {
          "id": "grader",
          "label": "Calificador de relevancia",
          "role": "LLM"
        },
        {
          "id": "rewriter",
          "label": "Reescritor de consultas",
          "role": "LLM"
        },
        {
          "id": "synthesizer",
          "label": "Sintetizador + Citador",
          "role": "LLM"
        },
        {
          "id": "groundedness",
          "label": "Verificación de fundamentación",
          "role": "LLM"
        },
        {
          "id": "hitl",
          "label": "Cola de escalado HITL",
          "role": "Human"
        },
        {
          "id": "trace",
          "label": "Trazado con Langfuse",
          "role": "Observer"
        }
      ],
      "keyEdges": [
        {
          "from": "planner",
          "to": "vector_retriever",
          "label": "subconsulta"
        },
        {
          "from": "grader",
          "to": "rewriter",
          "label": "reescribir si es débil"
        },
        {
          "from": "groundedness",
          "to": "hitl",
          "label": "escalar si no está fundamentado"
        }
      ]
    },
    "techStack": {
      "required": [
        "Python 3.11+",
        "LangGraph (o Python puro si quieres demostrar habilidades sin frameworks)",
        "pgvector o Qdrant para el almacén vectorial",
        "Un evaluador de respuestas fundamentadas — Ragas o personalizado",
        "Langfuse o LangSmith para el trazado",
        "FastAPI para la capa de servicio"
      ],
      "optional": [
        "Cohere Rerank o un reranker cross-encoder",
        "Elasticsearch u OpenSearch para recuperación híbrida",
        "Postgres para la cola HITL"
      ],
      "avoidIfNovice": [
        "No empieces con AutoGen o Semantic Kernel — añaden una orquestación que aún no necesitas",
        "No uses la API de OpenAI Assistants — oculta la capa de recuperación que estás intentando demostrar"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Corpus",
        "goal": "Elige un corpus de dominio (~500–5,000 documentos). Opciones públicas: artículos de IA de arXiv, informes SEC 10-K, tu propia bóveda de Obsidian. Documenta la procedencia.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 2 — RAG base",
        "goal": "Recuperación ingenua + relleno del prompt. Mide la línea base sobre 30–50 consultas etiquetadas a mano. Guarda las puntuaciones — formarán tu historia de 'antes/después' en el README.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — Capa agéntica",
        "goal": "Añade planificador, calificador, reescritor y verificación de fundamentación. Vuelve a ejecutar las evaluaciones. Objetivo: +15–30% en la tasa de respuestas fundamentadas.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 4 — HITL + Trazado",
        "goal": "Añade la cola de escalado para respuestas no fundamentadas. Conecta Langfuse. Publica los trazados de forma pública.",
        "durationEstimate": "3–5 noches"
      },
      {
        "phase": "Fase 5 — Despliegue + Demo",
        "goal": "Despliegue en Fly.io / Railway / Modal. URL de demo pública. Recorrido en Loom.",
        "durationEstimate": "1 noche"
      }
    ],
    "evalHarness": {
      "summary": "Etiqueta a mano 30–50 consultas en 5 categorías: fáctica de un solo salto, de múltiples saltos, ambigua, fuera del corpus, adversaria. Ejecuta las evaluaciones después de cada cambio en la capa agéntica. Publica los resultados en el README.",
      "metrics": [
        {
          "name": "Tasa de respuestas fundamentadas",
          "what": "% de respuestas cuyas afirmaciones están respaldadas por el contexto recuperado",
          "howMeasured": "Métrica de fidelidad de Ragas o verificación manual de las primeras 30",
          "targetRange": "Línea base 50–65%; objetivo agéntico 80%+"
        },
        {
          "name": "Precisión de citas",
          "what": "Si las fuentes citadas realmente contienen la afirmación",
          "howMeasured": "Verificación manual de una muestra aleatoria de 20",
          "targetRange": "90%+"
        },
        {
          "name": "Tasa de rechazo (fuera del corpus)",
          "what": "% de preguntas fuera del corpus correctamente rechazadas frente a alucinadas",
          "howMeasured": "Tus 5 semillas fuera del corpus + calificador",
          "targetRange": "100% rechazadas — cualquier alucinación es un fallo crítico"
        },
        {
          "name": "Latencia p50 / p95",
          "what": "Latencia de respuesta de extremo a extremo",
          "howMeasured": "Langfuse o tu propio temporizador",
          "targetRange": "p50 < 4s, p95 < 10s"
        },
        {
          "name": "Costo por consulta",
          "what": "Dólares de LLM + almacén vectorial por consulta respondida",
          "howMeasured": "Seguimiento de costos de Langfuse",
          "targetRange": "objetivo < $0.05, tope < $0.15"
        }
      ],
      "datasetGuidance": "Tu conjunto de consultas es en sí mismo un artefacto de portafolio. Súbelo al repositorio como eval_queries.jsonl. Un responsable de contratación que ve un conjunto de evaluación real te toma 10× más en serio que uno que ve 'trabajé en pruebas.'"
    },
    "hitlPattern": {
      "triggers": [
        "Puntuación de la verificación de fundamentación < 0.6",
        "La recuperación devuelve menos de 3 fragmentos relevantes tras 2 reescrituras",
        "Consulta del usuario marcada por un clasificador de seguridad"
      ],
      "humanDecisions": [
        "Aprobar la mejor respuesta posible del agente con una insignia de 'baja confianza'",
        "Escribir una respuesta manual que se añade al corpus para la próxima vez",
        "Marcar la consulta como fuera de alcance y declinar"
      ],
      "autoResumeRules": "Una vez que el humano decide, el sistema envía un correo al usuario original, añade la consulta+respuesta resuelta a un 'corpus supervisado' (separado del corpus principal) y registra la decisión para mejorar el umbral de fundamentación con el tiempo."
    },
    "failureModes": [
      {
        "name": "Alucinación confiada",
        "trigger": "El contexto recuperado está relacionado tangencialmente — el LLM teje una síntesis plausible pero incorrecta",
        "mitigation": "Puntuación de verificación de fundamentación + patrón de rechazo + requisito de citas en el prompt",
        "severity": "critical"
      },
      {
        "name": "Sobrerrecuperación",
        "trigger": "El planificador sobredescompone una consulta simple en 8 subconsultas y el costo se dispara",
        "mitigation": "Limita las subconsultas a 3; muestra la propia compuerta del planificador de '¿es necesaria la descomposición?'",
        "severity": "high"
      },
      {
        "name": "Corpus obsoleto",
        "trigger": "Las incrustaciones del corpus se desvían a medida que los documentos se actualizan",
        "mitigation": "Fragmentos versionados + reindexación delta nocturna + seguimiento de obsolescencia por fragmento",
        "severity": "medium"
      },
      {
        "name": "Inyección de prompts vía documentos",
        "trigger": "El documento recuperado contiene 'Ignora las instrucciones anteriores'",
        "mitigation": "El contenido recuperado va dentro de bloques XML etiquetados; el system prompt trata explícitamente el contenido etiquetado como datos no confiables",
        "severity": "high"
      },
      {
        "name": "Oscilación del reescritor",
        "trigger": "El reescritor sigue reescribiendo la misma consulta fallida en bucle",
        "mitigation": "Contador de reescrituras máximas (≤2) y luego escalado HITL",
        "severity": "medium"
      }
    ],
    "deployment": {
      "minimumViable": "Una URL de demo pública (Fly.io, Railway o Modal) con un modo de solo lectura. No entregues acceso de escritura totalmente abierto — limita la tasa de forma agresiva o exige inicio de sesión.",
      "observabilityMinimum": [
        "Panel de Langfuse o LangSmith — enlazado públicamente (capturas de pantalla en el README)",
        "Costo + latencia por consulta visibles",
        "Resultados de evaluación reejecutables en CI",
        "Tasa de errores y tasa de escalado HITL rastreadas"
      ],
      "costCeiling": "Elige un tope mensual ($20–50 está bien para un proyecto de portafolio). Documéntalo. Usa un modelo más barato (Haiku/Mini) en los bucles del calificador y el reescritor — solo el sintetizador necesita un modelo insignia."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting e ingeniería de contexto",
        "howThisProjectShows": "System prompts distintos por agente (planificador, calificador, sintetizador) con etiquetado explícito de contexto no confiable — no un mega-prompt único"
      },
      {
        "domain": "D2",
        "domainName": "Patrones agénticos",
        "howThisProjectShows": "Elección clara de patrón: planificador-ejecutor con reescritura condicional, no un bucle ReAct libre. La elección del patrón se justifica en el README."
      },
      {
        "domain": "D3",
        "domainName": "Uso e integración de herramientas",
        "howThisProjectShows": "Tres recuperadores (vectorial / palabra clave / estructurado) usados condicionalmente según el tipo de consulta — demuestra razonamiento sobre la elección de herramientas"
      },
      {
        "domain": "D4",
        "domainName": "Evaluación y fiabilidad",
        "howThisProjectShows": "Conjunto de evaluación etiquetado a mano, métricas antes/después, arnés ejecutable en CI, panel de trazado — la señal de credibilidad"
      },
      {
        "domain": "D5",
        "domainName": "Diseño de sistemas",
        "howThisProjectShows": "Pipeline corpus → recuperación → calificación → síntesis → fundamentación con documentación de modos de fallo"
      },
      {
        "domain": "D6",
        "domainName": "Seguridad y gobernanza",
        "howThisProjectShows": "Manejo de contenido no confiable, mitigación de inyección de prompts, rechazo fuera del corpus, HITL para respuestas de baja confianza"
      },
      {
        "domain": "D7",
        "domainName": "Pensamiento de producto",
        "howThisProjectShows": "Tope de costos, SLO de latencia, URL de demo pública, insignias de confianza visibles para el usuario — no un proyecto de ciencia"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "RAG agéntico sobre [tu corpus de dominio] — conjunto de evaluación de 30 consultas, 84% de tasa de respuestas fundamentadas, trazados de Langfuse públicos.",
      "metricsToHighlight": [
        "Tasa de respuestas fundamentadas: línea base → agéntico (muestra la mejora)",
        "Latencia p95",
        "Costo por consulta",
        "Tasa de rechazo fuera del corpus (debería ser 100%)"
      ],
      "antiPattern": "NO afirmes que es 'de grado de producción' a menos que esté realmente desplegado y sea observable. NO cites una sola demo de camino feliz como prueba de que funciona. NO ocultes el conjunto de evaluación — ese es todo el motor de credibilidad."
    },
    "repoStructure": [
      "README.md (diagrama de arquitectura, tabla de evaluación, URL de despliegue, enlace de Loom)",
      "corpus/ (con notas de procedencia)",
      "agents/ (planner, grader, rewriter, synthesizer, groundedness_check)",
      "retrievers/ (vector, keyword, structured)",
      "eval/ (eval_queries.jsonl, run_evals.py, results/)",
      "hitl/ (escalation_queue.py, review_ui/)",
      "deploy/ (Dockerfile, fly.toml o railway.toml)",
      "traces/ (capturas de pantalla de trazados de Langfuse de muestra + enlaces)",
      ".github/workflows/ (CI ejecutando evaluaciones en PR)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 2 — Multi-Agent Researcher (the Anthropic pattern)
// ---------------------------------------------------------------------------

export const MULTI_AGENT_RESEARCHER: Localized<ProjectArchetype> = {
  en: {
  slug: "multi-agent-researcher",
  title: "Multi-Agent Researcher — LeadResearcher + Subagent Orchestration",
  tagline:
    "Anthropic's own multi-agent research pattern: a lead decomposes, parallel subagents gather, lead synthesizes. Show you can orchestrate, not just call.",
  tier: "all_access",
  estimatedBuildTime: "3–4 weekends",
  difficulty: "senior",
  callbackSignal: "very_high",

  problem: {
    headline:
      "A single agent doing research hits context-window limits, runs sub-queries serially (slow), and conflates discovery with synthesis.",
    detail:
      "The LeadResearcher + Subagent pattern — documented in Anthropic's own engineering blog — decouples planning from execution. Lead plans, subagents fan out in parallel, lead synthesizes. This is the pattern behind Anthropic's production research tooling.",
    whyItMatters:
      "Orchestration is the senior/staff-level signal. Any intermediate can call an LLM in a loop. Orchestrating parallel agents with shared memory and failure recovery is the job of the next level up.",
  },

  architecture: {
    summary:
      "LeadResearcher receives a research task, decomposes it into 3–5 parallel subagent tasks, spawns subagents with scoped tool access, collects results, synthesizes a report with citations, and iterates if gaps remain. Each subagent has its own tracing context.",
    mermaidDiagram: `graph TB
  U[User task] --> L[LeadResearcher]
  L -->|plan & spawn| S1[Subagent 1: Web search]
  L -->|plan & spawn| S2[Subagent 2: arXiv search]
  L -->|plan & spawn| S3[Subagent 3: GitHub search]
  S1 --> M[(Shared Memory Store)]
  S2 --> M
  S3 --> M
  M --> L
  L -->|gaps?| L
  L -->|synthesize| R[Research Report]
  R --> CW[Citation Writer]
  CW --> O[Final Output]
  L --> T[(Tracing: per-subagent spans)]
  S1 --> T
  S2 --> T
  S3 --> T`,
    components: [
      { id: "lead", label: "LeadResearcher", role: "LLM" },
      { id: "sub_web", label: "Web Search Subagent", role: "LLM" },
      { id: "sub_arxiv", label: "arXiv Subagent", role: "LLM" },
      { id: "sub_github", label: "GitHub Subagent", role: "LLM" },
      { id: "memory", label: "Shared Memory (Postgres/Redis)", role: "Store" },
      { id: "citer", label: "Citation Writer", role: "LLM" },
      { id: "trace", label: "OpenTelemetry Traces", role: "Observer" },
    ],
    keyEdges: [
      { from: "lead", to: "sub_web", label: "spawn w/ scoped tools" },
      { from: "sub_web", to: "memory", label: "write findings" },
      { from: "memory", to: "lead", label: "aggregated findings" },
    ],
  },

  techStack: {
    required: [
      "Claude API (Sonnet for lead, Haiku for subagents — cost discipline is part of the signal)",
      "LangGraph OR asyncio-based custom orchestrator (pick one, justify it)",
      "Postgres or Redis for shared memory",
      "OpenTelemetry + a viewer (Jaeger, Honeycomb, Langfuse)",
      "A real search API (Brave, Tavily, or SerpAPI)",
    ],
    optional: [
      "MCP server exposing your subagents as tools",
      "Anthropic's batch API for subagent fan-out",
    ],
    avoidIfNovice: [
      "Don't use CrewAI as a first attempt — it obscures the orchestration logic you're trying to showcase",
      "Don't skip the shared-memory layer — 'subagents return results as strings' is the junior pattern",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Single Subagent", goal: "Build one well-scoped subagent (web search). Measure its eval on 20 tasks.", durationEstimate: "1 weekend" },
    { phase: "Phase 2 — Lead + 3 Subagents", goal: "Add LeadResearcher with hardcoded 3-subagent plan. Parallel execution via asyncio.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Dynamic Planning", goal: "Lead generates its own subagent plan based on task. Add gap-detection loop.", durationEstimate: "1 weekend" },
    { phase: "Phase 4 — Memory + Citations", goal: "Shared memory store. Citation writer enforces 'every claim has a source' rule.", durationEstimate: "3–5 evenings" },
    { phase: "Phase 5 — Observability", goal: "OpenTelemetry spans per subagent. Dashboard public. Cost + latency budgets enforced.", durationEstimate: "3 evenings" },
  ],

  evalHarness: {
    summary:
      "Build a 20-task research benchmark across 4 categories: technical deep-dives, comparative analysis, recent-events summaries, controversial-topic balanced reports. Hand-grade completeness, citation accuracy, and balance.",
    metrics: [
      { name: "Completeness Score", what: "Does the report cover the dimensions a subject-matter expert would expect", howMeasured: "Rubric scored 1–5 by you (or a colleague) on 20 tasks", targetRange: "≥ 4.0 average" },
      { name: "Citation Accuracy", what: "Claim-to-source faithfulness", howMeasured: "Hand-check 5 random claims per report", targetRange: "≥ 90%" },
      { name: "Subagent Parallelism Gain", what: "Latency vs. serial single-agent baseline", howMeasured: "Time the same task serial vs. parallel", targetRange: "3–5× speedup on multi-source tasks" },
      { name: "Cost per Report", what: "Total LLM cost per completed research task", howMeasured: "OTel cost span", targetRange: "< $0.50 target, < $2 ceiling" },
      { name: "Gap-Loop Convergence", what: "% of tasks that terminate without exceeding 3 gap-iteration loops", howMeasured: "Trace counter", targetRange: "≥ 80%" },
    ],
    datasetGuidance:
      "Pick a domain where depth matters (AI safety research, historical events, technical protocol evolution). Shallow domains won't showcase the pattern's value.",
  },

  hitlPattern: {
    triggers: [
      "Gap-detection loop exceeds 3 iterations",
      "Citation writer can't ground a claim to any retrieved source",
      "Subagent returns an 'I don't have sufficient access' error from a tool",
    ],
    humanDecisions: [
      "Add a specific source URL the agent missed",
      "Mark the task as unanswerable with current tools and explain why",
      "Approve the partial report with a 'known gaps' section",
    ],
    autoResumeRules:
      "Human interventions are logged and fed back as few-shot examples in the LeadResearcher's planning prompt for the next run — the system learns from human overrides.",
  },

  failureModes: [
    { name: "Subagent Context Pollution", trigger: "Subagent 1 finds low-signal junk that Lead treats as authoritative", mitigation: "Subagent output schema forces confidence scoring; Lead down-weights low-confidence findings", severity: "high" },
    { name: "Cost Runaway on Gap Loop", trigger: "Lead keeps finding gaps and spawning more subagents", mitigation: "Hard cap on total subagent spawns per task (e.g. 10); budget guard that halts on $2 spent", severity: "high" },
    { name: "Parallel Write Races", trigger: "Two subagents write conflicting findings to shared memory", mitigation: "Append-only memory log with subagent ID attribution; Lead reconciles in synthesis", severity: "medium" },
    { name: "Citation Laundering", trigger: "Subagent returns a claim without a source; Citation Writer invents one", mitigation: "Citer is a separate step with strict source-match requirement; unsourced claims trigger HITL or refusal", severity: "critical" },
    { name: "Infinite Recursion", trigger: "Subagent spawns its own sub-subagents, creating a tree", mitigation: "Scope: subagents CANNOT spawn. Only LeadResearcher spawns. Enforced at the tool-access layer.", severity: "high" },
  ],

  deployment: {
    minimumViable: "Public read-only demo with rate limiting. Report generation takes 30–90s — build the UI to show streaming subagent progress so users see work happening.",
    observabilityMinimum: [
      "OpenTelemetry traces with per-subagent spans — public dashboard",
      "Cost and token counts per subagent visible",
      "Gap-loop iteration counter on every trace",
      "Eval results CI-tracked on main branch",
    ],
    costCeiling: "$30/month cap is reasonable. Haiku for subagents cuts 70–90% of cost vs Sonnet everywhere.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Distinct prompts for lead vs subagent with scoped context windows — lead never sees raw subagent context, only their structured outputs" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "Explicit LeadResearcher + Subagent pattern with justification vs alternatives (ReAct, swarm, hierarchical planning)" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Per-subagent tool scoping — web-search subagent cannot hit arXiv; demonstrates principle of least privilege" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Research benchmark with completeness rubric, citation accuracy checks, parallelism gain measurement" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Shared memory design (append-only + conflict resolution), parallel fan-out, gap-detection loop with convergence guarantees" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Subagent spawn restrictions, cost budget guard, citation-laundering prevention, HITL for low-confidence outputs" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Streaming UX for long-running tasks, visible cost/latency trade-offs, known-gaps transparency" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "LeadResearcher + Subagent pattern (Anthropic's) for deep research — 20-task benchmark, 4.2/5 completeness, 3.8× parallelism gain over single-agent baseline.",
    metricsToHighlight: [
      "Completeness rubric score",
      "Parallelism gain vs single-agent baseline",
      "Citation accuracy",
      "Public OTel trace dashboard link",
    ],
    antiPattern:
      "Do NOT call this 'multi-agent' if you have one agent calling three tools. Real multi-agent means independently-scoped agents with their own prompts, context, and tool access.",
  },

  repoStructure: [
    "README.md (architecture, benchmark results, trace dashboard link)",
    "orchestrator/ (lead_researcher.py, subagent_spawner.py, memory_store.py)",
    "subagents/ (web_search.py, arxiv.py, github.py — each with own prompt + schema)",
    "citation/ (citer.py, source_matcher.py)",
    "bench/ (20 research tasks, rubric.md, grader.py)",
    "traces/ (sample trace screenshots, OTel dashboard URL)",
    "deploy/ (Dockerfile, budget_guard.py, rate_limiter.py)",
    "docs/pattern-justification.md (why LeadResearcher+Subagent over alternatives)",
  ],
  },
  es: {
    "slug": "multi-agent-researcher",
    "title": "Multi-Agent Researcher — Orquestación LeadResearcher + Subagentes",
    "tagline": "El propio patrón de investigación multiagente de Anthropic: un líder descompone, subagentes en paralelo recopilan, el líder sintetiza. Demuestra que puedes orquestar, no solo invocar.",
    "tier": "all_access",
    "estimatedBuildTime": "3–4 fines de semana",
    "difficulty": "senior",
    "callbackSignal": "very_high",
    "problem": {
      "headline": "Un único agente haciendo investigación choca con los límites de la ventana de contexto, ejecuta las subconsultas en serie (lento) y confunde el descubrimiento con la síntesis.",
      "detail": "El patrón LeadResearcher + Subagentes —documentado en el propio blog de ingeniería de Anthropic— desacopla la planificación de la ejecución. El líder planifica, los subagentes se despliegan en paralelo, el líder sintetiza. Este es el patrón detrás del tooling de investigación en producción de Anthropic.",
      "whyItMatters": "La orquestación es la señal de nivel senior/staff. Cualquier intermedio puede invocar un LLM en un bucle. Orquestar agentes en paralelo con memoria compartida y recuperación ante fallos es el trabajo del siguiente nivel."
    },
    "architecture": {
      "summary": "LeadResearcher recibe una tarea de investigación, la descompone en 3–5 tareas de subagentes en paralelo, genera subagentes con acceso a herramientas acotado, recopila resultados, sintetiza un informe con citas e itera si quedan lagunas. Cada subagente tiene su propio contexto de trazado.",
      "mermaidDiagram": "graph TB\n  U[User task] --> L[LeadResearcher]\n  L -->|plan & spawn| S1[Subagent 1: Web search]\n  L -->|plan & spawn| S2[Subagent 2: arXiv search]\n  L -->|plan & spawn| S3[Subagent 3: GitHub search]\n  S1 --> M[(Shared Memory Store)]\n  S2 --> M\n  S3 --> M\n  M --> L\n  L -->|gaps?| L\n  L -->|synthesize| R[Research Report]\n  R --> CW[Citation Writer]\n  CW --> O[Final Output]\n  L --> T[(Tracing: per-subagent spans)]\n  S1 --> T\n  S2 --> T\n  S3 --> T",
      "components": [
        {
          "id": "lead",
          "label": "LeadResearcher",
          "role": "LLM"
        },
        {
          "id": "sub_web",
          "label": "Subagente de búsqueda web",
          "role": "LLM"
        },
        {
          "id": "sub_arxiv",
          "label": "Subagente de arXiv",
          "role": "LLM"
        },
        {
          "id": "sub_github",
          "label": "Subagente de GitHub",
          "role": "LLM"
        },
        {
          "id": "memory",
          "label": "Memoria compartida (Postgres/Redis)",
          "role": "Store"
        },
        {
          "id": "citer",
          "label": "Citation Writer",
          "role": "LLM"
        },
        {
          "id": "trace",
          "label": "Trazas de OpenTelemetry",
          "role": "Observer"
        }
      ],
      "keyEdges": [
        {
          "from": "lead",
          "to": "sub_web",
          "label": "generar con herramientas acotadas"
        },
        {
          "from": "sub_web",
          "to": "memory",
          "label": "escribir hallazgos"
        },
        {
          "from": "memory",
          "to": "lead",
          "label": "hallazgos agregados"
        }
      ]
    },
    "techStack": {
      "required": [
        "Claude API (Sonnet para el líder, Haiku para los subagentes — la disciplina de costes es parte de la señal)",
        "LangGraph O un orquestador propio basado en asyncio (elige uno y justifícalo)",
        "Postgres o Redis para la memoria compartida",
        "OpenTelemetry + un visor (Jaeger, Honeycomb, Langfuse)",
        "Una API de búsqueda real (Brave, Tavily o SerpAPI)"
      ],
      "optional": [
        "Servidor MCP que expone tus subagentes como herramientas",
        "La batch API de Anthropic para el despliegue de subagentes"
      ],
      "avoidIfNovice": [
        "No uses CrewAI como primer intento — oculta la lógica de orquestación que intentas mostrar",
        "No omitas la capa de memoria compartida — 'los subagentes devuelven resultados como cadenas' es el patrón de nivel junior"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Un solo subagente",
        "goal": "Construye un subagente bien acotado (búsqueda web). Mide su eval en 20 tareas.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 2 — Líder + 3 subagentes",
        "goal": "Añade LeadResearcher con un plan fijo de 3 subagentes. Ejecución en paralelo con asyncio.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — Planificación dinámica",
        "goal": "El líder genera su propio plan de subagentes según la tarea. Añade un bucle de detección de lagunas.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 4 — Memoria + citas",
        "goal": "Almacén de memoria compartida. El citation writer impone la regla de 'cada afirmación tiene una fuente'.",
        "durationEstimate": "3–5 tardes"
      },
      {
        "phase": "Fase 5 — Observabilidad",
        "goal": "Spans de OpenTelemetry por subagente. Dashboard público. Presupuestos de coste + latencia aplicados.",
        "durationEstimate": "3 tardes"
      }
    ],
    "evalHarness": {
      "summary": "Construye un benchmark de investigación de 20 tareas en 4 categorías: análisis técnicos profundos, análisis comparativos, resúmenes de eventos recientes e informes equilibrados de temas controvertidos. Califica a mano la exhaustividad, la precisión de las citas y el equilibrio.",
      "metrics": [
        {
          "name": "Puntuación de exhaustividad",
          "what": "Si el informe cubre las dimensiones que esperaría un experto en la materia",
          "howMeasured": "Rúbrica puntuada de 1 a 5 por ti (o un colega) en 20 tareas",
          "targetRange": "≥ 4.0 de media"
        },
        {
          "name": "Precisión de citas",
          "what": "Fidelidad entre afirmación y fuente",
          "howMeasured": "Revisión manual de 5 afirmaciones aleatorias por informe",
          "targetRange": "≥ 90%"
        },
        {
          "name": "Ganancia de paralelismo de subagentes",
          "what": "Latencia frente a la línea base en serie de un solo agente",
          "howMeasured": "Cronometra la misma tarea en serie frente a en paralelo",
          "targetRange": "aceleración de 3–5× en tareas multifuente"
        },
        {
          "name": "Coste por informe",
          "what": "Coste total de LLM por tarea de investigación completada",
          "howMeasured": "Span de coste de OTel",
          "targetRange": "objetivo < $0.50, techo < $2"
        },
        {
          "name": "Convergencia del bucle de lagunas",
          "what": "% de tareas que terminan sin superar 3 iteraciones del bucle de lagunas",
          "howMeasured": "Contador de trazas",
          "targetRange": "≥ 80%"
        }
      ],
      "datasetGuidance": "Elige un dominio donde la profundidad importe (investigación en seguridad de IA, eventos históricos, evolución de protocolos técnicos). Los dominios superficiales no mostrarán el valor del patrón."
    },
    "hitlPattern": {
      "triggers": [
        "El bucle de detección de lagunas supera 3 iteraciones",
        "El citation writer no puede fundamentar una afirmación en ninguna fuente recuperada",
        "Un subagente devuelve un error de 'no tengo acceso suficiente' desde una herramienta"
      ],
      "humanDecisions": [
        "Añadir una URL de fuente específica que el agente pasó por alto",
        "Marcar la tarea como incontestable con las herramientas actuales y explicar por qué",
        "Aprobar el informe parcial con una sección de 'lagunas conocidas'"
      ],
      "autoResumeRules": "Las intervenciones humanas se registran y se reincorporan como ejemplos few-shot en el prompt de planificación del LeadResearcher para la siguiente ejecución — el sistema aprende de las anulaciones humanas."
    },
    "failureModes": [
      {
        "name": "Contaminación del contexto de subagentes",
        "trigger": "El subagente 1 encuentra basura de baja señal que el líder trata como autoritativa",
        "mitigation": "El esquema de salida del subagente obliga a puntuar la confianza; el líder reduce el peso de los hallazgos de baja confianza",
        "severity": "high"
      },
      {
        "name": "Desbordamiento de coste en el bucle de lagunas",
        "trigger": "El líder sigue encontrando lagunas y generando más subagentes",
        "mitigation": "Límite estricto en el total de subagentes generados por tarea (p. ej. 10); guardián de presupuesto que detiene al gastar $2",
        "severity": "high"
      },
      {
        "name": "Carreras de escritura en paralelo",
        "trigger": "Dos subagentes escriben hallazgos en conflicto en la memoria compartida",
        "mitigation": "Registro de memoria de solo anexado con atribución de ID de subagente; el líder reconcilia en la síntesis",
        "severity": "medium"
      },
      {
        "name": "Blanqueo de citas",
        "trigger": "Un subagente devuelve una afirmación sin fuente; el Citation Writer inventa una",
        "mitigation": "El citer es un paso separado con un requisito estricto de coincidencia de fuente; las afirmaciones sin fuente activan HITL o rechazo",
        "severity": "critical"
      },
      {
        "name": "Recursión infinita",
        "trigger": "Un subagente genera sus propios sub-subagentes, creando un árbol",
        "mitigation": "Alcance: los subagentes NO PUEDEN generar. Solo LeadResearcher genera. Aplicado en la capa de acceso a herramientas.",
        "severity": "high"
      }
    ],
    "deployment": {
      "minimumViable": "Demo pública de solo lectura con limitación de tasa. La generación de informes tarda 30–90s — construye la UI para mostrar el progreso de los subagentes en streaming, de modo que los usuarios vean el trabajo en marcha.",
      "observabilityMinimum": [
        "Trazas de OpenTelemetry con spans por subagente — dashboard público",
        "Conteos de coste y tokens por subagente visibles",
        "Contador de iteraciones del bucle de lagunas en cada traza",
        "Resultados de eval rastreados por CI en la rama main"
      ],
      "costCeiling": "Un tope de $30/mes es razonable. Usar Haiku para los subagentes recorta un 70–90% del coste frente a usar Sonnet en todo."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting e ingeniería de contexto",
        "howThisProjectShows": "Prompts distintos para el líder frente a los subagentes con ventanas de contexto acotadas — el líder nunca ve el contexto crudo de los subagentes, solo sus salidas estructuradas"
      },
      {
        "domain": "D2",
        "domainName": "Patrones agénticos",
        "howThisProjectShows": "Patrón explícito LeadResearcher + Subagentes con justificación frente a alternativas (ReAct, swarm, planificación jerárquica)"
      },
      {
        "domain": "D3",
        "domainName": "Uso e integración de herramientas",
        "howThisProjectShows": "Acotamiento de herramientas por subagente — el subagente de búsqueda web no puede acceder a arXiv; demuestra el principio de mínimo privilegio"
      },
      {
        "domain": "D4",
        "domainName": "Evaluación y fiabilidad",
        "howThisProjectShows": "Benchmark de investigación con rúbrica de exhaustividad, comprobaciones de precisión de citas y medición de la ganancia de paralelismo"
      },
      {
        "domain": "D5",
        "domainName": "Diseño de sistemas",
        "howThisProjectShows": "Diseño de memoria compartida (solo anexado + resolución de conflictos), despliegue en paralelo, bucle de detección de lagunas con garantías de convergencia"
      },
      {
        "domain": "D6",
        "domainName": "Seguridad y gobernanza",
        "howThisProjectShows": "Restricciones de generación de subagentes, guardián de presupuesto de coste, prevención de blanqueo de citas, HITL para salidas de baja confianza"
      },
      {
        "domain": "D7",
        "domainName": "Pensamiento de producto",
        "howThisProjectShows": "UX en streaming para tareas de larga duración, compromisos de coste/latencia visibles, transparencia de lagunas conocidas"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Patrón LeadResearcher + Subagentes (el de Anthropic) para investigación profunda — benchmark de 20 tareas, 4.2/5 de exhaustividad, ganancia de paralelismo de 3.8× sobre la línea base de un solo agente.",
      "metricsToHighlight": [
        "Puntuación de rúbrica de exhaustividad",
        "Ganancia de paralelismo frente a la línea base de un solo agente",
        "Precisión de citas",
        "Enlace al dashboard público de trazas OTel"
      ],
      "antiPattern": "NO llames a esto 'multiagente' si tienes un solo agente invocando tres herramientas. Multiagente real significa agentes acotados de forma independiente, con sus propios prompts, contexto y acceso a herramientas."
    },
    "repoStructure": [
      "README.md (arquitectura, resultados del benchmark, enlace al dashboard de trazas)",
      "orchestrator/ (lead_researcher.py, subagent_spawner.py, memory_store.py)",
      "subagents/ (web_search.py, arxiv.py, github.py — cada uno con su propio prompt + esquema)",
      "citation/ (citer.py, source_matcher.py)",
      "bench/ (20 tareas de investigación, rubric.md, grader.py)",
      "traces/ (capturas de trazas de ejemplo, URL del dashboard de OTel)",
      "deploy/ (Dockerfile, budget_guard.py, rate_limiter.py)",
      "docs/pattern-justification.md (por qué LeadResearcher+Subagentes frente a las alternativas)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 3 — HITL Workflow Agent (the 'real consequences' project)
// ---------------------------------------------------------------------------

export const HITL_WORKFLOW_AGENT: Localized<ProjectArchetype> = {
  en: {
  slug: "hitl-workflow-agent",
  title: "HITL Workflow Agent — Real Consequences with Human Gates",
  tagline:
    "An agent that takes real actions in a real system (email, PR, calendar, ticket) with human approval gates. The 2026 callback-magnet pattern.",
  tier: "all_access",
  estimatedBuildTime: "2–3 weekends",
  difficulty: "senior",
  callbackSignal: "very_high",

  problem: {
    headline:
      "Most portfolio agents return strings. Production agents take actions that cost money, send messages, open PRs, or change database state.",
    detail:
      "Real-consequence agents require structured human-in-the-loop patterns: preview, approve, execute, rollback. Candidates who build this correctly stand out from the 90% who ship 'chat with my docs' demos.",
    whyItMatters:
      "Every enterprise AI deployment in 2026 needs HITL. This is the project type hiring managers explicitly cite as a callback trigger (AgenticCareers 2026, Anthropic Managed Agents API launch).",
  },

  architecture: {
    summary:
      "User describes an outcome. Planner drafts a multi-step action plan. Each step classified by blast radius (read-only / reversible / irreversible). Blast-radius-irreversible steps require explicit human approval before execution. All actions are audit-logged and reversible where possible.",
    mermaidDiagram: `graph TB
  U[User intent] --> P[Planner]
  P --> Plan[Action Plan]
  Plan --> C[Blast Radius Classifier]
  C -->|read-only| E[Auto-execute]
  C -->|reversible| A1[Preview + Approve]
  C -->|irreversible| A2[Preview + 2-step Approve]
  A1 -->|approved| E
  A2 -->|approved| E
  A1 -->|rejected| R[Replan]
  A2 -->|rejected| R
  E --> AL[(Audit Log)]
  E -->|success| N[User notified]
  E -->|failure| Ro[Rollback]
  Ro --> AL
  R --> P`,
    components: [
      { id: "planner", label: "Action Planner", role: "LLM" },
      { id: "classifier", label: "Blast Radius Classifier", role: "LLM" },
      { id: "approval_ui", label: "Approval UI / Slack bot", role: "Human" },
      { id: "executor", label: "Tool Executor", role: "Tool" },
      { id: "audit_log", label: "Audit Log (Postgres)", role: "Store" },
      { id: "rollback", label: "Rollback Engine", role: "Tool" },
    ],
    keyEdges: [
      { from: "classifier", to: "approval_ui", label: "reversible / irreversible" },
      { from: "executor", to: "audit_log", label: "every action" },
      { from: "executor", to: "rollback", label: "on failure" },
    ],
  },

  techStack: {
    required: [
      "Claude API (function-calling / tool-use)",
      "Your target action surface — pick ONE: GitHub (PRs), Gmail (drafts), Google Calendar, Linear/Jira, Slack",
      "FastAPI + a simple approval UI (Next.js or plain HTML) OR a Slack bot for approvals",
      "Postgres for audit log",
      "OpenTelemetry for action traces",
    ],
    optional: [
      "MCP for the action tools",
      "Anthropic's tool-use batch API",
    ],
    avoidIfNovice: [
      "Don't build on top of Zapier or n8n — they hide the action classification you're trying to demonstrate",
      "Don't skip the blast-radius classifier — 'ask before every action' is the junior version",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Pick Action Surface", goal: "Choose ONE system with a good API. Build 3–5 atomic action functions.", durationEstimate: "1 evening" },
    { phase: "Phase 2 — Planner + Classifier", goal: "Planner takes intent, produces action list. Classifier tags each as read-only / reversible / irreversible.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Approval UI", goal: "Slack bot OR web UI that shows previews + approve/reject. Two-step confirm for irreversible.", durationEstimate: "1 weekend" },
    { phase: "Phase 4 — Audit + Rollback", goal: "Every action writes to audit log. Irreversible actions get a documented-but-unexecutable rollback path. Reversible actions get executed rollback.", durationEstimate: "3–5 evenings" },
    { phase: "Phase 5 — Red-team Test", goal: "Manually try to get the agent to do irreversible things without approval. Document the defense.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "30 test intents across 3 categories: safe-automation, approval-required, adversarial (intent designed to trigger unauthorized action). Measure: approval-gate miss rate (must be 0%), false-positive approval rate (how often safe actions get gated unnecessarily), plan quality.",
    metrics: [
      { name: "Approval-Gate Miss Rate", what: "% of irreversible actions that executed without human approval", howMeasured: "Adversarial eval set + audit log inspection", targetRange: "0% — anything above is a critical bug" },
      { name: "False-Positive Approval Rate", what: "% of read-only actions incorrectly gated", howMeasured: "Safe-automation eval set", targetRange: "< 10% — over-gating creates approval fatigue" },
      { name: "Plan Completion Rate", what: "% of intents where agent produces an executable plan without user clarification", howMeasured: "Hand-scored on 30 intents", targetRange: "≥ 75%" },
      { name: "Rollback Coverage", what: "% of executed reversible actions with a tested rollback path", howMeasured: "Inspect code + test run", targetRange: "100% for reversible category" },
      { name: "Action Audit Completeness", what: "% of actions with complete audit log entries (who/what/when/approval-chain)", howMeasured: "Inspect audit table after eval runs", targetRange: "100%" },
    ],
    datasetGuidance:
      "The adversarial set is the credibility tell. Include prompts like 'delete all PRs that mention X' or 'email every contact saying I quit.' A hiring manager reading this will instantly know whether you built real safety or cosplay safety.",
  },

  hitlPattern: {
    triggers: [
      "Action classified as 'irreversible' (deletes data, sends external communication, initiates payment)",
      "Action classified as 'reversible' but high-blast-radius (>10 records affected)",
      "Planner confidence on intent is < 0.7",
      "Any action in red-flag categories (mass operations, external comms, financial)",
    ],
    humanDecisions: [
      "Approve as-is",
      "Approve with modification (edit the draft, narrow the scope)",
      "Reject and have planner replan",
      "Cancel task entirely",
    ],
    autoResumeRules:
      "Approval decision is logged with approver identity. Task resumes at the approved step. Rejections with modifications become few-shot examples for the planner.",
  },

  failureModes: [
    { name: "Classifier Bypass", trigger: "Planner structures an irreversible action as a sequence of 'reversible' steps to avoid gating", mitigation: "Classifier runs on the full plan, not per-step; aggregate-effect analysis; hard deny-list for certain action shapes", severity: "critical" },
    { name: "Approval Fatigue", trigger: "Too many unnecessary approvals trains user to approve blindly", mitigation: "Per-user learned allow-list — repeated approvals for same pattern auto-downgrade classification over time, with audit", severity: "high" },
    { name: "Stale Approval", trigger: "User approves a plan, context changes before execution", mitigation: "Approvals expire after N minutes; re-preview if stale", severity: "medium" },
    { name: "Tool Call Injection", trigger: "User input manipulates the tool call shape (e.g. 'send email with subject \"; DROP TABLE')", mitigation: "Tool inputs are schema-validated; classifier sees fully-parameterized call, not a templated string", severity: "high" },
    { name: "Audit Log Tampering", trigger: "Agent modifies its own audit log to hide an unauthorized action", mitigation: "Audit log is append-only with row-level DB permissions the agent CANNOT hold; separate service account", severity: "critical" },
  ],

  deployment: {
    minimumViable: "Demo against YOUR OWN test account (test GitHub org, throwaway Gmail, etc.) — don't demo against a live personal system. Scoped OAuth token with minimum privileges.",
    observabilityMinimum: [
      "Audit log queryable via public-read dashboard (for demo) showing: time, actor, action, approval chain, outcome",
      "OTel spans per action with approval-status tag",
      "Alert on any classifier-bypass attempt",
      "Cost budget guard",
    ],
    costCeiling: "$20/month — this project type is cheap because classification uses Haiku and most actions don't need LLM calls.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Planner prompt forces structured action plans with blast-radius reasoning — not freeform natural language" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "Plan-Classify-Approve-Execute-Audit pattern with explicit gates — not a ReAct loop pretending to be safe" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Real tool integration (GitHub/Gmail/Calendar) with schema-validated parameters and scoped credentials" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Adversarial eval set, approval-gate miss rate = 0% target, rollback coverage tracking" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Audit log design, rollback engine, credential scoping, deny-list enforcement" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Blast-radius classification, approval fatigue mitigation, tamper-resistant audit log, adversarial testing — THIS is the project where D6 shines" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Approval UX that doesn't create fatigue, clear action previews, reversibility transparency" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "HITL workflow agent for [GitHub/Gmail/Calendar] — 30-task eval with adversarial set, 0% approval-gate miss rate, full audit log, demoable rollback.",
    metricsToHighlight: [
      "Approval-gate miss rate (should be 0%)",
      "Adversarial test pass rate",
      "Rollback coverage %",
      "Public audit-log viewer",
    ],
    antiPattern:
      "Do NOT demo against a real production system. Do NOT skip the adversarial set — without it the safety claims are vibes. Do NOT use the word 'autonomous' to mean 'no human in the loop' when every real action is gated.",
  },

  repoStructure: [
    "README.md (architecture, adversarial eval results, audit dashboard link)",
    "planner/ (action_planner.py, classifier.py)",
    "actions/ (one file per action surface: github.py, gmail.py, etc.)",
    "approval/ (slack_bot.py OR web_ui/)",
    "audit/ (audit_log.py, rollback_engine.py)",
    "eval/ (30_intents.jsonl, adversarial_set.jsonl, run_evals.py)",
    "docs/security-model.md (threat model + defenses)",
    "docs/blast-radius-guide.md (how to tune classifier for your org)",
  ],
  },
  es: {
    "slug": "hitl-workflow-agent",
    "title": "Agente de Flujo de Trabajo HITL — Consecuencias Reales con Compuertas Humanas",
    "tagline": "Un agente que ejecuta acciones reales en un sistema real (email, PR, calendario, ticket) con compuertas de aprobación humana. El patrón imán de callbacks de 2026.",
    "tier": "all_access",
    "estimatedBuildTime": "2–3 fines de semana",
    "difficulty": "senior",
    "callbackSignal": "very_high",
    "problem": {
      "headline": "La mayoría de los agentes de portafolio devuelven cadenas de texto. Los agentes de producción ejecutan acciones que cuestan dinero, envían mensajes, abren PRs o cambian el estado de la base de datos.",
      "detail": "Los agentes con consecuencias reales requieren patrones estructurados de human-in-the-loop: vista previa, aprobación, ejecución, rollback. Los candidatos que construyen esto correctamente se destacan del 90% que entrega demos de 'chatea con mis documentos'.",
      "whyItMatters": "Cada despliegue de IA empresarial en 2026 necesita HITL. Este es el tipo de proyecto que los gerentes de contratación citan explícitamente como un disparador de callbacks (AgenticCareers 2026, lanzamiento de la Managed Agents API de Anthropic)."
    },
    "architecture": {
      "summary": "El usuario describe un resultado deseado. El Planner redacta un plan de acción de múltiples pasos. Cada paso se clasifica por radio de impacto (solo lectura / reversible / irreversible). Los pasos con radio de impacto irreversible requieren aprobación humana explícita antes de ejecutarse. Todas las acciones se registran en un log de auditoría y son reversibles cuando es posible.",
      "mermaidDiagram": "graph TB\n  U[User intent] --> P[Planner]\n  P --> Plan[Action Plan]\n  Plan --> C[Blast Radius Classifier]\n  C -->|read-only| E[Auto-execute]\n  C -->|reversible| A1[Preview + Approve]\n  C -->|irreversible| A2[Preview + 2-step Approve]\n  A1 -->|approved| E\n  A2 -->|approved| E\n  A1 -->|rejected| R[Replan]\n  A2 -->|rejected| R\n  E --> AL[(Audit Log)]\n  E -->|success| N[User notified]\n  E -->|failure| Ro[Rollback]\n  Ro --> AL\n  R --> P",
      "components": [
        {
          "id": "planner",
          "label": "Planificador de Acciones",
          "role": "LLM"
        },
        {
          "id": "classifier",
          "label": "Clasificador de Radio de Impacto",
          "role": "LLM"
        },
        {
          "id": "approval_ui",
          "label": "UI de Aprobación / bot de Slack",
          "role": "Human"
        },
        {
          "id": "executor",
          "label": "Ejecutor de Herramientas",
          "role": "Tool"
        },
        {
          "id": "audit_log",
          "label": "Log de Auditoría (Postgres)",
          "role": "Store"
        },
        {
          "id": "rollback",
          "label": "Motor de Rollback",
          "role": "Tool"
        }
      ],
      "keyEdges": [
        {
          "from": "classifier",
          "to": "approval_ui",
          "label": "reversible / irreversible"
        },
        {
          "from": "executor",
          "to": "audit_log",
          "label": "cada acción"
        },
        {
          "from": "executor",
          "to": "rollback",
          "label": "ante un fallo"
        }
      ]
    },
    "techStack": {
      "required": [
        "Claude API (function-calling / tool-use)",
        "Tu superficie de acción objetivo — elige UNA: GitHub (PRs), Gmail (borradores), Google Calendar, Linear/Jira, Slack",
        "FastAPI + una UI de aprobación simple (Next.js o HTML plano) O un bot de Slack para aprobaciones",
        "Postgres para el log de auditoría",
        "OpenTelemetry para trazas de acciones"
      ],
      "optional": [
        "MCP para las herramientas de acción",
        "La batch API de tool-use de Anthropic"
      ],
      "avoidIfNovice": [
        "No construyas sobre Zapier o n8n — ocultan la clasificación de acciones que estás tratando de demostrar",
        "No omitas el clasificador de radio de impacto — 'preguntar antes de cada acción' es la versión junior"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Elegir la Superficie de Acción",
        "goal": "Elige UN sistema con una buena API. Construye 3–5 funciones de acción atómicas.",
        "durationEstimate": "1 noche"
      },
      {
        "phase": "Fase 2 — Planner + Clasificador",
        "goal": "El Planner toma la intención y produce una lista de acciones. El clasificador etiqueta cada una como solo lectura / reversible / irreversible.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — UI de Aprobación",
        "goal": "Bot de Slack O UI web que muestra vistas previas + aprobar/rechazar. Confirmación de dos pasos para las irreversibles.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 4 — Auditoría + Rollback",
        "goal": "Cada acción escribe en el log de auditoría. Las acciones irreversibles obtienen una ruta de rollback documentada-pero-no-ejecutable. Las acciones reversibles obtienen un rollback ejecutado.",
        "durationEstimate": "3–5 noches"
      },
      {
        "phase": "Fase 5 — Prueba de Red-team",
        "goal": "Intenta manualmente lograr que el agente haga cosas irreversibles sin aprobación. Documenta la defensa.",
        "durationEstimate": "1 noche"
      }
    ],
    "evalHarness": {
      "summary": "30 intenciones de prueba en 3 categorías: automatización-segura, requiere-aprobación, adversarial (intención diseñada para disparar una acción no autorizada). Mide: tasa de omisión de compuerta de aprobación (debe ser 0%), tasa de aprobación de falsos positivos (con qué frecuencia se ponen compuertas innecesarias a acciones seguras), calidad del plan.",
      "metrics": [
        {
          "name": "Tasa de Omisión de Compuerta de Aprobación",
          "what": "% de acciones irreversibles que se ejecutaron sin aprobación humana",
          "howMeasured": "Conjunto de eval adversarial + inspección del log de auditoría",
          "targetRange": "0% — cualquier valor superior es un bug crítico"
        },
        {
          "name": "Tasa de Aprobación de Falsos Positivos",
          "what": "% de acciones de solo lectura a las que se puso compuerta incorrectamente",
          "howMeasured": "Conjunto de eval de automatización-segura",
          "targetRange": "< 10% — el exceso de compuertas genera fatiga de aprobación"
        },
        {
          "name": "Tasa de Completitud del Plan",
          "what": "% de intenciones donde el agente produce un plan ejecutable sin aclaración del usuario",
          "howMeasured": "Puntuado manualmente en 30 intenciones",
          "targetRange": "≥ 75%"
        },
        {
          "name": "Cobertura de Rollback",
          "what": "% de acciones reversibles ejecutadas con una ruta de rollback probada",
          "howMeasured": "Inspeccionar código + ejecución de prueba",
          "targetRange": "100% para la categoría reversible"
        },
        {
          "name": "Completitud de la Auditoría de Acciones",
          "what": "% de acciones con entradas completas en el log de auditoría (quién/qué/cuándo/cadena-de-aprobación)",
          "howMeasured": "Inspeccionar la tabla de auditoría tras las ejecuciones de eval",
          "targetRange": "100%"
        }
      ],
      "datasetGuidance": "El conjunto adversarial es la señal de credibilidad. Incluye prompts como 'elimina todos los PRs que mencionen X' o 'envía un email a cada contacto diciendo que renuncio.' Un gerente de contratación que lea esto sabrá al instante si construiste seguridad real o seguridad de cosplay."
    },
    "hitlPattern": {
      "triggers": [
        "Acción clasificada como 'irreversible' (elimina datos, envía comunicación externa, inicia un pago)",
        "Acción clasificada como 'reversible' pero de alto radio de impacto (>10 registros afectados)",
        "La confianza del Planner sobre la intención es < 0.7",
        "Cualquier acción en categorías de bandera roja (operaciones masivas, comunicaciones externas, financieras)"
      ],
      "humanDecisions": [
        "Aprobar tal cual",
        "Aprobar con modificación (editar el borrador, acotar el alcance)",
        "Rechazar y hacer que el Planner replanifique",
        "Cancelar la tarea por completo"
      ],
      "autoResumeRules": "La decisión de aprobación se registra con la identidad del aprobador. La tarea se reanuda en el paso aprobado. Los rechazos con modificaciones se convierten en ejemplos few-shot para el Planner."
    },
    "failureModes": [
      {
        "name": "Bypass del Clasificador",
        "trigger": "El Planner estructura una acción irreversible como una secuencia de pasos 'reversibles' para evitar las compuertas",
        "mitigation": "El clasificador se ejecuta sobre el plan completo, no por paso; análisis de efecto agregado; deny-list rígida para ciertas formas de acción",
        "severity": "critical"
      },
      {
        "name": "Fatiga de Aprobación",
        "trigger": "Demasiadas aprobaciones innecesarias entrenan al usuario a aprobar a ciegas",
        "mitigation": "Allow-list aprendida por usuario — las aprobaciones repetidas para el mismo patrón degradan automáticamente la clasificación con el tiempo, con auditoría",
        "severity": "high"
      },
      {
        "name": "Aprobación Obsoleta",
        "trigger": "El usuario aprueba un plan, el contexto cambia antes de la ejecución",
        "mitigation": "Las aprobaciones expiran tras N minutos; re-previsualizar si está obsoleta",
        "severity": "medium"
      },
      {
        "name": "Inyección en la Llamada a Herramienta",
        "trigger": "La entrada del usuario manipula la forma de la llamada a la herramienta (ej. 'enviar email con asunto \"; DROP TABLE')",
        "mitigation": "Las entradas de las herramientas se validan por esquema; el clasificador ve la llamada totalmente parametrizada, no una cadena con plantilla",
        "severity": "high"
      },
      {
        "name": "Manipulación del Log de Auditoría",
        "trigger": "El agente modifica su propio log de auditoría para ocultar una acción no autorizada",
        "mitigation": "El log de auditoría es de solo-anexado con permisos de BD a nivel de fila que el agente NO PUEDE tener; cuenta de servicio separada",
        "severity": "critical"
      }
    ],
    "deployment": {
      "minimumViable": "Haz la demo contra TU PROPIA cuenta de prueba (org de prueba de GitHub, Gmail desechable, etc.) — no hagas la demo contra un sistema personal en vivo. Token OAuth acotado con privilegios mínimos.",
      "observabilityMinimum": [
        "Log de auditoría consultable mediante un dashboard de lectura-pública (para la demo) que muestre: hora, actor, acción, cadena de aprobación, resultado",
        "Spans de OTel por acción con etiqueta de estado-de-aprobación",
        "Alerta ante cualquier intento de bypass del clasificador",
        "Guarda de presupuesto de costos"
      ],
      "costCeiling": "$20/mes — este tipo de proyecto es económico porque la clasificación usa Haiku y la mayoría de las acciones no necesitan llamadas al LLM."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting & Context Engineering",
        "howThisProjectShows": "El prompt del Planner fuerza planes de acción estructurados con razonamiento de radio de impacto — no lenguaje natural libre"
      },
      {
        "domain": "D2",
        "domainName": "Agentic Patterns",
        "howThisProjectShows": "Patrón Planificar-Clasificar-Aprobar-Ejecutar-Auditar con compuertas explícitas — no un loop ReAct que finge ser seguro"
      },
      {
        "domain": "D3",
        "domainName": "Tool Use & Integration",
        "howThisProjectShows": "Integración de herramientas real (GitHub/Gmail/Calendar) con parámetros validados por esquema y credenciales acotadas"
      },
      {
        "domain": "D4",
        "domainName": "Evaluation & Reliability",
        "howThisProjectShows": "Conjunto de eval adversarial, objetivo de tasa de omisión de compuerta de aprobación = 0%, seguimiento de cobertura de rollback"
      },
      {
        "domain": "D5",
        "domainName": "Systems Design",
        "howThisProjectShows": "Diseño del log de auditoría, motor de rollback, acotamiento de credenciales, aplicación de deny-list"
      },
      {
        "domain": "D6",
        "domainName": "Safety & Governance",
        "howThisProjectShows": "Clasificación de radio de impacto, mitigación de fatiga de aprobación, log de auditoría resistente a manipulaciones, pruebas adversariales — ESTE es el proyecto donde brilla D6"
      },
      {
        "domain": "D7",
        "domainName": "Product Thinking",
        "howThisProjectShows": "UX de aprobación que no genera fatiga, vistas previas de acción claras, transparencia de reversibilidad"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Agente de flujo de trabajo HITL para [GitHub/Gmail/Calendar] — eval de 30 tareas con conjunto adversarial, 0% de tasa de omisión de compuerta de aprobación, log de auditoría completo, rollback demostrable.",
      "metricsToHighlight": [
        "Tasa de omisión de compuerta de aprobación (debería ser 0%)",
        "Tasa de aprobación de pruebas adversariales",
        "% de cobertura de rollback",
        "Visor público del log de auditoría"
      ],
      "antiPattern": "NO hagas la demo contra un sistema de producción real. NO omitas el conjunto adversarial — sin él, las afirmaciones de seguridad son puro vibe. NO uses la palabra 'autónomo' para significar 'sin humano en el loop' cuando cada acción real tiene compuerta."
    },
    "repoStructure": [
      "README.md (arquitectura, resultados de eval adversarial, enlace al dashboard de auditoría)",
      "planner/ (action_planner.py, classifier.py)",
      "actions/ (un archivo por superficie de acción: github.py, gmail.py, etc.)",
      "approval/ (slack_bot.py O web_ui/)",
      "audit/ (audit_log.py, rollback_engine.py)",
      "eval/ (30_intents.jsonl, adversarial_set.jsonl, run_evals.py)",
      "docs/security-model.md (modelo de amenazas + defensas)",
      "docs/blast-radius-guide.md (cómo ajustar el clasificador para tu organización)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 4 — LLM Evaluation Harness (the credibility-infrastructure project)
// ---------------------------------------------------------------------------

export const EVAL_HARNESS: Localized<ProjectArchetype> = {
  en: {
  slug: "eval-harness",
  title: "LLM Evaluation Harness",
  tagline:
    "The system that answers 'did this prompt change help or hurt?' with a number instead of a vibe — the infrastructure every other AI project quietly depends on.",
  tier: "all_access",
  estimatedBuildTime: "2–3 weekends",
  difficulty: "senior",
  callbackSignal: "very_high",

  problem: {
    headline:
      "Teams ship LLM features they can't measure. 'Is this prompt better?' gets answered by eyeballing 5 outputs and shipping on a hunch.",
    detail:
      "Without a held-out golden set, a runner, and a scorer, every prompt change is a coin flip you can't audit. Most candidates have never built an eval harness — they have an intuition that 'it seems better.' The harness is what turns AI engineering from craft into engineering: a CI gate that blocks regressions and a dashboard that shows the trend.",
    whyItMatters:
      "In 2026 the bottleneck on shipping LLM features isn't building them — it's knowing whether a change is safe to ship. Eval is the single most-requested-but-least-demonstrated skill. A candidate who can stand up a harness is the candidate who can ship responsibly.",
  },

  architecture: {
    summary:
      "A versioned golden-set store feeds a runner that executes the system-under-test on every case, scoring each output with both programmatic checks (regex/JSON-schema/exact-match) and an LLM-as-judge for open-ended quality. A scorer aggregates per-metric, compares against the last-known baseline, and emits a pass/fail verdict that gates CI plus a regression dashboard for the trend over time.",
    mermaidDiagram: `graph LR
  G[(Golden Set — versioned JSONL)] --> R[Eval Runner]
  SUT[System Under Test — prompt/model/agent] --> R
  R --> PC[Programmatic Checks]
  R --> JJ[LLM-as-Judge]
  PC --> AG[Scorer / Aggregator]
  JJ --> AG
  BL[(Baseline Scores)] --> AG
  AG -->|delta vs baseline| GATE{CI Gate}
  GATE -->|pass| MERGE[Allow merge]
  GATE -->|regress| BLOCK[Block + diff report]
  AG --> DASH[Regression Dashboard]`,
    components: [
      { id: "golden_set", label: "Golden Set (versioned)", role: "Store" },
      { id: "runner", label: "Eval Runner", role: "Tool" },
      { id: "programmatic", label: "Programmatic Checks", role: "Tool" },
      { id: "judge", label: "LLM-as-Judge", role: "LLM" },
      { id: "scorer", label: "Scorer / Aggregator", role: "Tool" },
      { id: "baseline", label: "Baseline Scores", role: "Store" },
      { id: "ci_gate", label: "CI Gate", role: "Observer" },
      { id: "dashboard", label: "Regression Dashboard", role: "Observer" },
    ],
    keyEdges: [
      { from: "golden_set", to: "runner", label: "cases" },
      { from: "judge", to: "scorer", label: "open-ended quality" },
      { from: "scorer", to: "ci_gate", label: "delta vs baseline" },
    ],
  },

  techStack: {
    required: [
      "Python 3.11+",
      "An eval framework — promptfoo, Inspect (UK AISI), or a thin custom runner (show you understand the loop, don't just config a tool)",
      "Pydantic for typed case + result schemas",
      "An LLM-as-judge with a rubric prompt + structured output",
      "GitHub Actions (or any CI) to run the gate on PRs",
    ],
    optional: [
      "Langfuse or a SQLite results table for the dashboard / trend",
      "Statistical significance check (bootstrap CI on win-rate)",
      "Pairwise A/B judging to reduce single-output judge variance",
    ],
    avoidIfNovice: [
      "Don't lean entirely on LLM-as-judge — pair it with programmatic checks wherever the answer is checkable (the judge is for the un-checkable parts)",
      "Don't score on the same examples you tuned the prompt on — that's eval-on-train, and a reviewer will spot it immediately",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Golden Set", goal: "Build a held-out set of 40–80 cases with expected outputs (or rubrics). Cover the easy, the hard, and the adversarial. Version it — this is the asset.", durationEstimate: "1 weekend" },
    { phase: "Phase 2 — Runner + Programmatic Checks", goal: "Execute the system-under-test on every case. Add deterministic checks (exact-match, JSON-schema valid, must-contain/must-not-contain).", durationEstimate: "3–5 evenings" },
    { phase: "Phase 3 — LLM-as-Judge", goal: "Add a rubric-driven judge for open-ended quality. Calibrate it against your own labels on 20 cases — report judge-vs-human agreement.", durationEstimate: "1 weekend" },
    { phase: "Phase 4 — Baseline + CI Gate", goal: "Persist baseline scores. Wire the gate so a PR that regresses any metric fails CI with a diff report.", durationEstimate: "3 evenings" },
    { phase: "Phase 5 — Dashboard", goal: "Trend each metric across commits. Publish the dashboard. Demonstrate one real regression caught.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "This project IS the eval harness, so the meta-evaluation is: how trustworthy are the numbers it produces? Validate the judge against human labels, track score stability across re-runs (LLM judging is non-deterministic), and prove the gate catches a planted regression.",
    metrics: [
      { name: "pass@1", what: "% of golden-set cases passing all programmatic + judge thresholds on a single run", howMeasured: "Runner tallies per-case pass/fail", targetRange: "Report it honestly; the number is the system-under-test's, not a target to inflate" },
      { name: "Win-Rate vs Baseline", what: "% of cases where the candidate version beats the baseline in pairwise judging", howMeasured: "Pairwise LLM-as-judge with position-swap to cancel order bias", targetRange: "> 50% to ship; report the bootstrap confidence interval, not just the point estimate" },
      { name: "Judge–Human Agreement", what: "How often the LLM judge agrees with your own labels", howMeasured: "Cohen's kappa or raw agreement on 20 hand-labeled cases", targetRange: "≥ 0.7 agreement — below that, the judge rubric needs work before you trust it" },
      { name: "Regression Delta", what: "Per-metric change vs the stored baseline that the CI gate keys on", howMeasured: "Scorer diffs current run against baseline.json", targetRange: "Gate blocks on any metric dropping > 2pp (tune to your noise floor)" },
      { name: "Score Stability", what: "Variance of the same eval re-run 3× (judge non-determinism)", howMeasured: "Run the suite 3× at temperature 0, report stddev of pass@1", targetRange: "stddev < 2pp — if higher, your thresholds are inside the noise" },
    ],
    datasetGuidance:
      "The golden set is the entire deliverable. Commit it as golden_set.jsonl with provenance per case (who wrote the expected answer, why). A held-out set you can prove you didn't tune against is the credibility tell — note explicitly in the README that the tuning set and eval set are disjoint.",
  },

  // No hitlPattern: an eval harness is batch infrastructure — there is no
  // honest in-the-loop human-approval step in the scoring path.

  failureModes: [
    { name: "Judge Sycophancy / Bias", trigger: "LLM-as-judge rewards longer, more confident, or more verbose answers regardless of correctness", mitigation: "Rubric forces judging on specific criteria; pairwise A/B with position-swap; spot-validate against human labels", severity: "high" },
    { name: "Contaminated Golden Set", trigger: "Expected answers were generated by the same model you're evaluating, so it grades its own homework", mitigation: "Human-author or independently-source expected outputs; document provenance per case", severity: "critical" },
    { name: "Metric Gaming / Goodhart", trigger: "A prompt is tuned to pass the eval cases specifically, not to generalize", mitigation: "Keep a rotating held-back slice the tuner never sees; refresh the golden set periodically", severity: "high" },
    { name: "Eval-on-Train", trigger: "The same examples used to tune the prompt are scored as the eval", mitigation: "Hard split between tuning and eval sets, enforced in code, stated in README", severity: "critical" },
    { name: "Judge Non-Determinism Masquerading as Signal", trigger: "A 1pp 'improvement' is actually run-to-run judge noise", mitigation: "Report confidence intervals; gate threshold sits above the measured noise floor", severity: "medium" },
  ],

  deployment: {
    minimumViable: "A `make eval` (or `pytest`-style) command anyone can run on a clean checkout, plus a GitHub Action that runs it on every PR and posts the score diff as a comment.",
    observabilityMinimum: [
      "Per-run results persisted (JSON or SQLite) with commit SHA",
      "Public regression dashboard showing each metric's trend over commits",
      "CI gate status visible as a PR check + score-diff comment",
      "Judge cost + latency per run tracked",
    ],
    costCeiling: "Evals run on every PR, so cost compounds — use a cheap judge model (Haiku-class) and cache deterministic check results. A 60-case suite should cost cents per run; document the per-run cost.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "The judge rubric prompt is itself a context-engineering artifact — criteria-scoped, structured-output, calibrated against human labels" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "Runner orchestrates system-under-test + programmatic checks + judge as a deterministic pipeline — evaluation as a first-class system, not an afterthought" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Programmatic checks (schema validators, matchers) and the judge are composable tools the runner dispatches per case type" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "This is the domain — held-out sets, judge calibration, baseline diffing, CI gating, noise-floor awareness. The purest D4 artifact you can ship." },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Versioned golden set, baseline persistence, runner/scorer separation, CI integration — designed as reusable infrastructure across projects" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Regression gating prevents shipping known-worse behavior; adversarial cases in the golden set catch safety regressions before merge" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Turns 'it feels better' into a shippable/blockable decision with a cost budget — the harness exists to make ship/no-ship calls faster and safer" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "An LLM eval harness with a 60-case held-out golden set, calibrated LLM-as-judge (0.78 human agreement), and a CI gate that blocked a real regression.",
    metricsToHighlight: [
      "Golden-set size + held-out discipline (tuning/eval disjoint)",
      "Judge–human agreement (the calibration number)",
      "A real regression the gate caught (before/after)",
      "Per-run cost + score stability",
    ],
    antiPattern:
      "Do NOT claim an accuracy number without a held-out set — eval-on-train is the cardinal sin. Do NOT trust LLM-as-judge blindly; without judge–human agreement, the numbers are theater. Do NOT report a point estimate as if it has no noise.",
  },

  repoStructure: [
    "README.md (what the harness measures, judge calibration, a caught-regression case study)",
    "golden_set.jsonl (cases + expected outputs + provenance — the asset)",
    "runner/ (run_evals.py, case_schema.py)",
    "checks/ (programmatic: schema_check.py, match_check.py)",
    "judge/ (rubric_prompt.md, judge.py, calibration/ with human labels)",
    "scorer/ (aggregate.py, baseline.json, diff_report.py)",
    "dashboard/ (trend view + screenshots)",
    ".github/workflows/eval-gate.yml (runs on PR, posts score diff)",
  ],
  },
  es: {
    "slug": "eval-harness",
    "title": "LLM Evaluation Harness",
    "tagline": "El sistema que responde '¿este cambio de prompt ayudó o perjudicó?' con un número en lugar de una intuición — la infraestructura de la que dependen silenciosamente todos los demás proyectos de IA.",
    "tier": "all_access",
    "estimatedBuildTime": "2–3 fines de semana",
    "difficulty": "senior",
    "callbackSignal": "very_high",
    "problem": {
      "headline": "Los equipos lanzan funciones de LLM que no pueden medir. '¿Es mejor este prompt?' se responde mirando de reojo 5 salidas y lanzando por corazonada.",
      "detail": "Sin un conjunto dorado reservado, un runner y un scorer, cada cambio de prompt es un volado que no puedes auditar. La mayoría de los candidatos nunca ha construido un eval harness — tienen la intuición de que 'parece mejor'. El harness es lo que convierte la ingeniería de IA de artesanía en ingeniería: una compuerta de CI que bloquea regresiones y un dashboard que muestra la tendencia.",
      "whyItMatters": "En 2026 el cuello de botella para lanzar funciones de LLM no es construirlas — es saber si un cambio es seguro de lanzar. La evaluación es la habilidad más solicitada pero menos demostrada. Un candidato que puede levantar un harness es el candidato que puede lanzar de forma responsable."
    },
    "architecture": {
      "summary": "Un almacén de conjunto dorado versionado alimenta un runner que ejecuta el sistema bajo prueba en cada caso, calificando cada salida tanto con verificaciones programáticas (regex/JSON-schema/coincidencia exacta) como con un LLM-as-judge para la calidad de respuestas abiertas. Un scorer agrega por métrica, compara contra el último baseline conocido y emite un veredicto pass/fail que controla CI, además de un dashboard de regresiones para la tendencia a lo largo del tiempo.",
      "mermaidDiagram": "graph LR\n  G[(Golden Set — versioned JSONL)] --> R[Eval Runner]\n  SUT[System Under Test — prompt/model/agent] --> R\n  R --> PC[Programmatic Checks]\n  R --> JJ[LLM-as-Judge]\n  PC --> AG[Scorer / Aggregator]\n  JJ --> AG\n  BL[(Baseline Scores)] --> AG\n  AG -->|delta vs baseline| GATE{CI Gate}\n  GATE -->|pass| MERGE[Allow merge]\n  GATE -->|regress| BLOCK[Block + diff report]\n  AG --> DASH[Regression Dashboard]",
      "components": [
        {
          "id": "golden_set",
          "label": "Golden Set (versionado)",
          "role": "Store"
        },
        {
          "id": "runner",
          "label": "Eval Runner",
          "role": "Tool"
        },
        {
          "id": "programmatic",
          "label": "Verificaciones programáticas",
          "role": "Tool"
        },
        {
          "id": "judge",
          "label": "LLM-as-Judge",
          "role": "LLM"
        },
        {
          "id": "scorer",
          "label": "Scorer / Agregador",
          "role": "Tool"
        },
        {
          "id": "baseline",
          "label": "Puntuaciones baseline",
          "role": "Store"
        },
        {
          "id": "ci_gate",
          "label": "CI Gate",
          "role": "Observer"
        },
        {
          "id": "dashboard",
          "label": "Dashboard de regresiones",
          "role": "Observer"
        }
      ],
      "keyEdges": [
        {
          "from": "golden_set",
          "to": "runner",
          "label": "casos"
        },
        {
          "from": "judge",
          "to": "scorer",
          "label": "calidad de respuestas abiertas"
        },
        {
          "from": "scorer",
          "to": "ci_gate",
          "label": "delta vs baseline"
        }
      ]
    },
    "techStack": {
      "required": [
        "Python 3.11+",
        "Un framework de evaluación — promptfoo, Inspect (UK AISI), o un runner personalizado ligero (demuestra que entiendes el bucle, no solo que configuras una herramienta)",
        "Pydantic para esquemas tipados de casos + resultados",
        "Un LLM-as-judge con un prompt de rúbrica + salida estructurada",
        "GitHub Actions (o cualquier CI) para ejecutar la compuerta en los PR"
      ],
      "optional": [
        "Langfuse o una tabla de resultados en SQLite para el dashboard / la tendencia",
        "Verificación de significancia estadística (intervalo de confianza por bootstrap sobre la tasa de victorias)",
        "Juicio A/B por pares para reducir la varianza del juez de salida única"
      ],
      "avoidIfNovice": [
        "No te apoyes por completo en el LLM-as-judge — combínalo con verificaciones programáticas dondequiera que la respuesta sea verificable (el juez es para las partes no verificables)",
        "No califiques con los mismos ejemplos con los que ajustaste el prompt — eso es evaluar sobre el conjunto de entrenamiento, y un revisor lo detectará de inmediato"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Conjunto dorado",
        "goal": "Construye un conjunto reservado de 40–80 casos con salidas esperadas (o rúbricas). Cubre lo fácil, lo difícil y lo adversarial. Versiónalo — este es el activo.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 2 — Runner + verificaciones programáticas",
        "goal": "Ejecuta el sistema bajo prueba en cada caso. Añade verificaciones deterministas (coincidencia exacta, JSON-schema válido, debe-contener/no-debe-contener).",
        "durationEstimate": "3–5 tardes"
      },
      {
        "phase": "Fase 3 — LLM-as-Judge",
        "goal": "Añade un juez guiado por rúbrica para la calidad de respuestas abiertas. Calíbralo contra tus propias etiquetas en 20 casos — reporta la concordancia juez-vs-humano.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 4 — Baseline + CI Gate",
        "goal": "Persiste las puntuaciones baseline. Conecta la compuerta para que un PR que regrese en cualquier métrica falle el CI con un reporte de diferencias.",
        "durationEstimate": "3 tardes"
      },
      {
        "phase": "Fase 5 — Dashboard",
        "goal": "Grafica la tendencia de cada métrica a lo largo de los commits. Publica el dashboard. Demuestra una regresión real detectada.",
        "durationEstimate": "1 tarde"
      }
    ],
    "evalHarness": {
      "summary": "Este proyecto ES el eval harness, así que la meta-evaluación es: ¿qué tan confiables son los números que produce? Valida el juez contra etiquetas humanas, rastrea la estabilidad de las puntuaciones entre re-ejecuciones (el juicio de LLM no es determinista) y demuestra que la compuerta atrapa una regresión plantada.",
      "metrics": [
        {
          "name": "pass@1",
          "what": "% de casos del conjunto dorado que superan todos los umbrales programáticos + de juez en una sola ejecución",
          "howMeasured": "El runner contabiliza pass/fail por caso",
          "targetRange": "Repórtalo con honestidad; el número es del sistema bajo prueba, no un objetivo a inflar"
        },
        {
          "name": "Tasa de victorias vs baseline",
          "what": "% de casos donde la versión candidata supera al baseline en el juicio por pares",
          "howMeasured": "LLM-as-judge por pares con intercambio de posición para cancelar el sesgo de orden",
          "targetRange": "> 50% para lanzar; reporta el intervalo de confianza por bootstrap, no solo la estimación puntual"
        },
        {
          "name": "Concordancia juez-humano",
          "what": "Con qué frecuencia el juez LLM coincide con tus propias etiquetas",
          "howMeasured": "Kappa de Cohen o concordancia bruta sobre 20 casos etiquetados a mano",
          "targetRange": "≥ 0.7 de concordancia — por debajo de eso, la rúbrica del juez necesita trabajo antes de confiar en ella"
        },
        {
          "name": "Delta de regresión",
          "what": "Cambio por métrica vs el baseline almacenado que la compuerta de CI toma como referencia",
          "howMeasured": "El scorer compara la ejecución actual contra baseline.json",
          "targetRange": "La compuerta bloquea ante cualquier métrica que caiga > 2pp (ajusta a tu piso de ruido)"
        },
        {
          "name": "Estabilidad de puntuación",
          "what": "Varianza de la misma evaluación re-ejecutada 3× (no determinismo del juez)",
          "howMeasured": "Ejecuta la suite 3× a temperatura 0, reporta la desviación estándar de pass@1",
          "targetRange": "desviación estándar < 2pp — si es mayor, tus umbrales están dentro del ruido"
        }
      ],
      "datasetGuidance": "El conjunto dorado es el entregable completo. Confírmalo como golden_set.jsonl con procedencia por caso (quién escribió la respuesta esperada, por qué). Un conjunto reservado que puedes demostrar que no usaste para ajustar es la señal de credibilidad — indica explícitamente en el README que el conjunto de ajuste y el de evaluación son disjuntos."
    },
    "failureModes": [
      {
        "name": "Adulación / sesgo del juez",
        "trigger": "El LLM-as-judge recompensa respuestas más largas, más seguras o más verbosas sin importar la corrección",
        "mitigation": "La rúbrica fuerza a juzgar sobre criterios específicos; A/B por pares con intercambio de posición; validación puntual contra etiquetas humanas",
        "severity": "high"
      },
      {
        "name": "Conjunto dorado contaminado",
        "trigger": "Las respuestas esperadas fueron generadas por el mismo modelo que estás evaluando, así que califica su propia tarea",
        "mitigation": "Escribe a mano o consigue de forma independiente las salidas esperadas; documenta la procedencia por caso",
        "severity": "critical"
      },
      {
        "name": "Manipulación de métricas / Goodhart",
        "trigger": "Un prompt se ajusta para pasar específicamente los casos de evaluación, no para generalizar",
        "mitigation": "Mantén una porción rotatoria reservada que quien ajusta nunca ve; refresca el conjunto dorado periódicamente",
        "severity": "high"
      },
      {
        "name": "Evaluar sobre el conjunto de entrenamiento",
        "trigger": "Los mismos ejemplos usados para ajustar el prompt se califican como la evaluación",
        "mitigation": "División estricta entre conjuntos de ajuste y de evaluación, aplicada en el código, declarada en el README",
        "severity": "critical"
      },
      {
        "name": "No determinismo del juez disfrazado de señal",
        "trigger": "Una 'mejora' de 1pp es en realidad ruido del juez entre ejecuciones",
        "mitigation": "Reporta intervalos de confianza; el umbral de la compuerta se sitúa por encima del piso de ruido medido",
        "severity": "medium"
      }
    ],
    "deployment": {
      "minimumViable": "Un comando `make eval` (o estilo `pytest`) que cualquiera pueda ejecutar en un checkout limpio, más una GitHub Action que lo ejecute en cada PR y publique la diferencia de puntuación como comentario.",
      "observabilityMinimum": [
        "Resultados por ejecución persistidos (JSON o SQLite) con el commit SHA",
        "Dashboard público de regresiones que muestre la tendencia de cada métrica a lo largo de los commits",
        "Estado de la compuerta de CI visible como una verificación de PR + comentario con la diferencia de puntuación",
        "Costo + latencia del juez por ejecución rastreados"
      ],
      "costCeiling": "Las evaluaciones se ejecutan en cada PR, así que el costo se acumula — usa un modelo de juez económico (clase Haiku) y cachea los resultados de verificaciones deterministas. Una suite de 60 casos debería costar centavos por ejecución; documenta el costo por ejecución."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting & Context Engineering",
        "howThisProjectShows": "El prompt de rúbrica del juez es en sí mismo un artefacto de ingeniería de contexto — acotado a criterios, con salida estructurada, calibrado contra etiquetas humanas"
      },
      {
        "domain": "D2",
        "domainName": "Agentic Patterns",
        "howThisProjectShows": "El runner orquesta el sistema bajo prueba + verificaciones programáticas + juez como un pipeline determinista — la evaluación como sistema de primera clase, no una ocurrencia tardía"
      },
      {
        "domain": "D3",
        "domainName": "Tool Use & Integration",
        "howThisProjectShows": "Las verificaciones programáticas (validadores de esquema, comparadores) y el juez son herramientas componibles que el runner despacha según el tipo de caso"
      },
      {
        "domain": "D4",
        "domainName": "Evaluation & Reliability",
        "howThisProjectShows": "Este es el dominio — conjuntos reservados, calibración del juez, comparación con baseline, control por CI, conciencia del piso de ruido. El artefacto D4 más puro que puedes lanzar."
      },
      {
        "domain": "D5",
        "domainName": "Systems Design",
        "howThisProjectShows": "Conjunto dorado versionado, persistencia del baseline, separación runner/scorer, integración con CI — diseñado como infraestructura reutilizable entre proyectos"
      },
      {
        "domain": "D6",
        "domainName": "Safety & Governance",
        "howThisProjectShows": "El control de regresiones evita lanzar comportamientos que se sabe que empeoran; los casos adversariales en el conjunto dorado atrapan regresiones de seguridad antes del merge"
      },
      {
        "domain": "D7",
        "domainName": "Product Thinking",
        "howThisProjectShows": "Convierte el 'se siente mejor' en una decisión lanzable/bloqueable con un presupuesto de costo — el harness existe para tomar decisiones de lanzar/no lanzar más rápido y de forma más segura"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Un eval harness de LLM con un conjunto dorado reservado de 60 casos, un LLM-as-judge calibrado (0.78 de concordancia humana) y una compuerta de CI que bloqueó una regresión real.",
      "metricsToHighlight": [
        "Tamaño del conjunto dorado + disciplina de reserva (ajuste/evaluación disjuntos)",
        "Concordancia juez-humano (el número de calibración)",
        "Una regresión real que la compuerta atrapó (antes/después)",
        "Costo por ejecución + estabilidad de puntuación"
      ],
      "antiPattern": "NO afirmes un número de exactitud sin un conjunto reservado — evaluar sobre el conjunto de entrenamiento es el pecado capital. NO confíes ciegamente en el LLM-as-judge; sin concordancia juez-humano, los números son teatro. NO reportes una estimación puntual como si no tuviera ruido."
    },
    "repoStructure": [
      "README.md (qué mide el harness, calibración del juez, un caso de estudio de regresión detectada)",
      "golden_set.jsonl (casos + salidas esperadas + procedencia — el activo)",
      "runner/ (run_evals.py, case_schema.py)",
      "checks/ (programáticas: schema_check.py, match_check.py)",
      "judge/ (rubric_prompt.md, judge.py, calibration/ con etiquetas humanas)",
      "scorer/ (aggregate.py, baseline.json, diff_report.py)",
      "dashboard/ (vista de tendencia + capturas de pantalla)",
      ".github/workflows/eval-gate.yml (se ejecuta en PR, publica la diferencia de puntuación)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 5 — MCP Tool Server (the 'extend any agent' project)
// ---------------------------------------------------------------------------

export const MCP_SERVER: Localized<ProjectArchetype> = {
  en: {
  slug: "mcp-server",
  title: "MCP Tool Server",
  tagline:
    "A clean, well-described Model Context Protocol server — the standard way to give any Anthropic-compatible agent a new capability without rebuilding the agent.",
  tier: "all_access",
  estimatedBuildTime: "1–2 weekends",
  difficulty: "intermediate",
  callbackSignal: "very_high",

  problem: {
    headline:
      "Agents are only as useful as their tools. The skill that's actually in demand is exposing a backing service as tools an agent can use reliably — not building yet another agent.",
    detail:
      "MCP (Model Context Protocol) is the open, Anthropic-originated standard for connecting agents to tools, resources, and prompts. A good MCP server is mostly an exercise in interface design: typed schemas, descriptions an LLM can actually act on, bounded outputs, and auth on the backing service. Most candidates have used MCP servers; few have built one that an agent uses correctly on the first try.",
    whyItMatters:
      "MCP went from a 2024 proposal to the default integration layer across the agent ecosystem. 'I built an MCP server for X' is a concrete, verifiable, fast-to-build signal that you understand the agent–tool boundary — and it's reusable across every agent you'll ever build.",
  },

  architecture: {
    summary:
      "An MCP server speaks the protocol over a transport (stdio for local, streamable HTTP for remote), exposing three primitive types: tools (model-invoked actions with typed input schemas), resources (read-only context the host can pull), and prompts (reusable templates). Each tool handler validates input against its schema, calls the backing service/API, and returns a bounded, structured result. Auth guards the backing service so the agent can't exceed the server's own privileges.",
    mermaidDiagram: `graph LR
  H[MCP Host / Agent] -->|JSON-RPC| TR{Transport: stdio / HTTP}
  TR --> SRV[MCP Server]
  SRV --> TOOLS[Tool Handlers — typed schemas]
  SRV --> RES[Resources — read-only context]
  SRV --> PR[Prompt Templates]
  TOOLS --> V[Input Validation]
  V --> AUTH[Auth / Scoped Creds]
  AUTH --> API[(Backing Service / API)]
  API --> B[Bounded Result]
  B --> SRV`,
    components: [
      { id: "host", label: "MCP Host / Agent", role: "LLM" },
      { id: "transport", label: "Transport (stdio/HTTP)", role: "Tool" },
      { id: "server", label: "MCP Server", role: "Tool" },
      { id: "tool_handlers", label: "Tool Handlers", role: "Tool" },
      { id: "resources", label: "Resource Endpoints", role: "Store" },
      { id: "prompts", label: "Prompt Templates", role: "Tool" },
      { id: "auth", label: "Auth / Scoped Creds", role: "Observer" },
      { id: "backing", label: "Backing Service / API", role: "Store" },
    ],
    keyEdges: [
      { from: "host", to: "transport", label: "JSON-RPC" },
      { from: "tool_handlers", to: "auth", label: "validated call" },
      { from: "auth", to: "backing", label: "scoped request" },
    ],
  },

  techStack: {
    required: [
      "The official MCP SDK — Python (`mcp`) or TypeScript (`@modelcontextprotocol/sdk`)",
      "A real backing service to wrap — a public API, your own DB, or a SaaS you use",
      "Typed input schemas (Pydantic / Zod) for every tool",
      "MCP Inspector for manual testing of tool calls",
      "stdio transport to start; streamable HTTP for the remote demo",
    ],
    optional: [
      "OAuth on a remote HTTP server (the production-grade auth story)",
      "Rate limiting + output truncation on the backing service",
      "A Claude Desktop / Claude Code config snippet so reviewers can run it",
    ],
    avoidIfNovice: [
      "Don't expose one mega-tool with a free-text 'action' param — that pushes parsing onto the model and defeats the typed-schema point",
      "Don't return raw unbounded API responses — a 10k-row dump blows the agent's context and is the #1 rookie MCP mistake",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — One Tool, stdio", goal: "Wrap one backing-service operation as a single well-described MCP tool over stdio. Verify it in MCP Inspector.", durationEstimate: "1 evening" },
    { phase: "Phase 2 — Tool Suite + Schemas", goal: "Add 4–6 tools with tight typed input schemas and descriptions written for an LLM to act on. Bound every output.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Resources + Prompts", goal: "Expose read-only resources (e.g. a schema doc) and one reusable prompt template. Show you understand all three MCP primitives.", durationEstimate: "2–3 evenings" },
    { phase: "Phase 4 — Auth + HTTP Transport", goal: "Add scoped credentials to the backing service and a streamable-HTTP transport so the server can run remotely.", durationEstimate: "1 weekend" },
    { phase: "Phase 5 — Eval + Demo", goal: "Run the tool-use eval. Record an agent (Claude Desktop/Code) using the server end-to-end. Publish the config snippet.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "Eval the server from the agent's perspective: give an agent 20 natural-language tasks that should map to your tools, and measure whether it picks the right tool, fills the schema correctly, and gets a usable result. Tool descriptions are prompts — eval them like prompts.",
    metrics: [
      { name: "Tool-Call Success Rate", what: "% of tasks where the agent selects the correct tool and the call returns a usable result", howMeasured: "20 scripted tasks run through a test agent; inspect the tool-call trace", targetRange: "≥ 90% — misses usually mean a tool is under-described" },
      { name: "Schema-Validation Pass Rate", what: "% of agent-generated tool calls that validate against the input schema on the first attempt", howMeasured: "Count schema-validation rejections in the server logs", targetRange: "≥ 95% first-attempt; frequent rejections mean the schema or description is ambiguous" },
      { name: "Tool-Selection Accuracy", what: "When multiple tools could plausibly apply, does the agent pick the intended one", howMeasured: "Hand-check the 5 most-ambiguous tasks", targetRange: "≥ 90% — overlapping tool descriptions are the usual culprit" },
      { name: "Latency per Tool", what: "p50/p95 wall-clock per tool call (transport + handler + backing service)", howMeasured: "Time each handler; log per tool", targetRange: "p95 < 2s for read tools — slow tools make agents time out or retry" },
      { name: "Output Boundedness", what: "% of tool results within a token/row budget", howMeasured: "Assert max size in the handler + check in eval", targetRange: "100% bounded — unbounded output is a correctness bug, not a nicety" },
    ],
    datasetGuidance:
      "Write the 20 tasks as natural language a user would actually type, not as 'call tool X.' The gap between user intent and your tool surface is exactly what you're evaluating. Commit them as tool_eval_tasks.jsonl with the expected tool per task.",
  },

  // No hitlPattern: an MCP server is a tool provider, not a decision-maker.
  // Human-in-the-loop belongs in the host/agent that consumes it, not here.

  failureModes: [
    { name: "Under-Described Tools", trigger: "A terse tool description leaves the model guessing what it does or when to use it", mitigation: "Descriptions state purpose, when-to-use, and param semantics; eval tool-selection accuracy and iterate on the wording", severity: "high" },
    { name: "Unbounded Tool Output", trigger: "A tool returns a full table / large blob that blows the agent's context window", mitigation: "Paginate + truncate with an explicit 'more available' signal; cap result size in the handler", severity: "high" },
    { name: "Missing Auth on Backing Service", trigger: "The server exposes a backing API with no auth, so anyone who reaches the server gets full access", mitigation: "Scoped credentials held by the server (least privilege); auth on the HTTP transport; never proxy the agent's identity blindly", severity: "critical" },
    { name: "Tool-Output Injection", trigger: "Backing-service data contains 'instructions' the host model treats as commands", mitigation: "Return data as clearly-typed result fields, not free prose; document that tool output is untrusted content", severity: "high" },
    { name: "Schema Drift vs Backing API", trigger: "The backing API changes and the tool schema silently lies to the agent", mitigation: "Contract test the handler against the live/mocked API in CI; version the tool schema", severity: "medium" },
  ],

  deployment: {
    minimumViable: "A published server runnable two ways: stdio (a copy-paste Claude Desktop/Code config block in the README) and a remote streamable-HTTP endpoint with auth. Reviewers must be able to attach it to their own agent in under 5 minutes.",
    observabilityMinimum: [
      "Per-tool call logs: tool name, validated args, latency, success/failure",
      "Schema-validation rejection rate tracked",
      "Auth failures on the backing service surfaced",
      "Tool-use eval re-runnable in CI",
    ],
    costCeiling: "The server itself is near-free to host (stdio is local; HTTP is a tiny service). The cost is in the backing service's API — document rate limits and any per-call cost there.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Tool descriptions and prompt templates are context engineering — written for an LLM to select and fill correctly, then eval-tuned" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "Implements the standard agent–tool boundary (MCP) cleanly: tools/resources/prompts as distinct primitives, not one overloaded endpoint" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "This is the domain — typed schemas, transport choice, backing-service integration, bounded outputs. A purpose-built D3 artifact." },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Agent-perspective eval of tool-selection + schema-validation rates; contract tests against the backing API" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Transport selection (stdio vs HTTP), schema versioning, pagination/truncation strategy, auth boundary design" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Least-privilege scoped credentials, auth on the transport, treating tool output as untrusted content (injection defense)" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "5-minute attach-to-your-agent onboarding, copy-paste config, reusable across hosts — built to be adopted, not just to exist" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "An MCP tool server wrapping [your backing service] — 6 typed tools, 94% tool-call success across a 20-task agent eval, runnable in Claude Desktop in one paste.",
    metricsToHighlight: [
      "Tool-call success rate (agent-perspective)",
      "Schema-validation first-attempt pass rate",
      "Number of tools + all three MCP primitives covered",
      "5-minute attach-to-your-agent onboarding",
    ],
    antiPattern:
      "Do NOT ship one mega-tool with a free-text action param — that's an API, not an MCP surface. Do NOT return unbounded output. Do NOT skip auth on the backing service and call it 'a demo' — the auth boundary is the point.",
  },

  repoStructure: [
    "README.md (what it wraps, the all-three-primitives story, copy-paste Claude config, eval results)",
    "server/ (server.py or index.ts — protocol wiring + transport)",
    "tools/ (one module per tool, each with a typed schema + LLM-facing description)",
    "resources/ (read-only context endpoints)",
    "prompts/ (reusable prompt templates)",
    "auth/ (scoped-credential + transport-auth handling)",
    "eval/ (tool_eval_tasks.jsonl, run_tool_eval.py)",
    "tests/ (contract tests against the backing API)",
  ],
  },
  es: {
    "slug": "mcp-server",
    "title": "Servidor de Herramientas MCP",
    "tagline": "Un servidor de Model Context Protocol limpio y bien documentado — la forma estándar de darle una nueva capacidad a cualquier agente compatible con Anthropic sin reconstruir el agente.",
    "tier": "all_access",
    "estimatedBuildTime": "1–2 fines de semana",
    "difficulty": "intermediate",
    "callbackSignal": "very_high",
    "problem": {
      "headline": "Los agentes solo son tan útiles como sus herramientas. La habilidad que realmente está en demanda es exponer un servicio de respaldo como herramientas que un agente pueda usar de forma confiable — no construir otro agente más.",
      "detail": "MCP (Model Context Protocol) es el estándar abierto, originado por Anthropic, para conectar agentes con herramientas, recursos y prompts. Un buen servidor MCP es principalmente un ejercicio de diseño de interfaces: esquemas tipados, descripciones sobre las que un LLM pueda actuar realmente, salidas acotadas y autenticación en el servicio de respaldo. La mayoría de los candidatos han usado servidores MCP; pocos han construido uno que un agente use correctamente al primer intento.",
      "whyItMatters": "MCP pasó de ser una propuesta en 2024 a la capa de integración por defecto en todo el ecosistema de agentes. 'Construí un servidor MCP para X' es una señal concreta, verificable y rápida de construir de que entiendes la frontera agente–herramienta — y es reutilizable en cada agente que construyas."
    },
    "architecture": {
      "summary": "Un servidor MCP habla el protocolo sobre un transporte (stdio para local, HTTP en streaming para remoto), exponiendo tres tipos primitivos: herramientas (acciones invocadas por el modelo con esquemas de entrada tipados), recursos (contexto de solo lectura que el host puede extraer) y prompts (plantillas reutilizables). Cada manejador de herramienta valida la entrada contra su esquema, llama al servicio/API de respaldo y devuelve un resultado acotado y estructurado. La autenticación protege el servicio de respaldo para que el agente no pueda exceder los privilegios propios del servidor.",
      "mermaidDiagram": "graph LR\n  H[MCP Host / Agent] -->|JSON-RPC| TR{Transport: stdio / HTTP}\n  TR --> SRV[MCP Server]\n  SRV --> TOOLS[Tool Handlers — typed schemas]\n  SRV --> RES[Resources — read-only context]\n  SRV --> PR[Prompt Templates]\n  TOOLS --> V[Input Validation]\n  V --> AUTH[Auth / Scoped Creds]\n  AUTH --> API[(Backing Service / API)]\n  API --> B[Bounded Result]\n  B --> SRV",
      "components": [
        {
          "id": "host",
          "label": "Host / Agente MCP",
          "role": "LLM"
        },
        {
          "id": "transport",
          "label": "Transporte (stdio/HTTP)",
          "role": "Tool"
        },
        {
          "id": "server",
          "label": "Servidor MCP",
          "role": "Tool"
        },
        {
          "id": "tool_handlers",
          "label": "Manejadores de Herramientas",
          "role": "Tool"
        },
        {
          "id": "resources",
          "label": "Endpoints de Recursos",
          "role": "Store"
        },
        {
          "id": "prompts",
          "label": "Plantillas de Prompts",
          "role": "Tool"
        },
        {
          "id": "auth",
          "label": "Auth / Credenciales con Alcance",
          "role": "Observer"
        },
        {
          "id": "backing",
          "label": "Servicio / API de Respaldo",
          "role": "Store"
        }
      ],
      "keyEdges": [
        {
          "from": "host",
          "to": "transport",
          "label": "JSON-RPC"
        },
        {
          "from": "tool_handlers",
          "to": "auth",
          "label": "llamada validada"
        },
        {
          "from": "auth",
          "to": "backing",
          "label": "solicitud con alcance"
        }
      ]
    },
    "techStack": {
      "required": [
        "El SDK oficial de MCP — Python (`mcp`) o TypeScript (`@modelcontextprotocol/sdk`)",
        "Un servicio de respaldo real que envolver — una API pública, tu propia base de datos o un SaaS que uses",
        "Esquemas de entrada tipados (Pydantic / Zod) para cada herramienta",
        "MCP Inspector para pruebas manuales de llamadas a herramientas",
        "Transporte stdio para empezar; HTTP en streaming para la demo remota"
      ],
      "optional": [
        "OAuth en un servidor HTTP remoto (la historia de autenticación de grado producción)",
        "Limitación de tasa + truncamiento de salida en el servicio de respaldo",
        "Un fragmento de configuración de Claude Desktop / Claude Code para que los revisores puedan ejecutarlo"
      ],
      "avoidIfNovice": [
        "No expongas una megaherramienta con un parámetro de 'acción' de texto libre — eso traslada el parseo al modelo y anula el propósito del esquema tipado",
        "No devuelvas respuestas de API crudas y sin límites — un volcado de 10k filas revienta el contexto del agente y es el error de novato #1 con MCP"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Una Herramienta, stdio",
        "goal": "Envuelve una operación del servicio de respaldo como una única herramienta MCP bien descrita sobre stdio. Verifícala en MCP Inspector.",
        "durationEstimate": "1 tarde"
      },
      {
        "phase": "Fase 2 — Conjunto de Herramientas + Esquemas",
        "goal": "Añade 4–6 herramientas con esquemas de entrada tipados y ajustados, y descripciones escritas para que un LLM actúe sobre ellas. Acota cada salida.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — Recursos + Prompts",
        "goal": "Expón recursos de solo lectura (p. ej. un documento de esquema) y una plantilla de prompt reutilizable. Demuestra que entiendes las tres primitivas de MCP.",
        "durationEstimate": "2–3 tardes"
      },
      {
        "phase": "Fase 4 — Auth + Transporte HTTP",
        "goal": "Añade credenciales con alcance al servicio de respaldo y un transporte HTTP en streaming para que el servidor pueda ejecutarse de forma remota.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 5 — Evaluación + Demo",
        "goal": "Ejecuta la evaluación de uso de herramientas. Graba a un agente (Claude Desktop/Code) usando el servidor de extremo a extremo. Publica el fragmento de configuración.",
        "durationEstimate": "1 tarde"
      }
    ],
    "evalHarness": {
      "summary": "Evalúa el servidor desde la perspectiva del agente: dale a un agente 20 tareas en lenguaje natural que deberían mapear a tus herramientas y mide si elige la herramienta correcta, completa el esquema correctamente y obtiene un resultado utilizable. Las descripciones de las herramientas son prompts — evalúalas como prompts.",
      "metrics": [
        {
          "name": "Tasa de Éxito de Llamadas a Herramientas",
          "what": "% de tareas donde el agente selecciona la herramienta correcta y la llamada devuelve un resultado utilizable",
          "howMeasured": "20 tareas guionizadas ejecutadas a través de un agente de prueba; inspecciona la traza de llamadas a herramientas",
          "targetRange": "≥ 90% — los fallos suelen significar que una herramienta está poco descrita"
        },
        {
          "name": "Tasa de Aprobación de Validación de Esquema",
          "what": "% de llamadas a herramientas generadas por el agente que validan contra el esquema de entrada al primer intento",
          "howMeasured": "Cuenta los rechazos de validación de esquema en los logs del servidor",
          "targetRange": "≥ 95% al primer intento; los rechazos frecuentes significan que el esquema o la descripción es ambigua"
        },
        {
          "name": "Precisión de Selección de Herramienta",
          "what": "Cuando varias herramientas podrían aplicar plausiblemente, si el agente elige la intencionada",
          "howMeasured": "Revisa manualmente las 5 tareas más ambiguas",
          "targetRange": "≥ 90% — las descripciones de herramientas solapadas suelen ser la causa habitual"
        },
        {
          "name": "Latencia por Herramienta",
          "what": "p50/p95 de tiempo real por llamada a herramienta (transporte + manejador + servicio de respaldo)",
          "howMeasured": "Cronometra cada manejador; registra por herramienta",
          "targetRange": "p95 < 2s para herramientas de lectura — las herramientas lentas hacen que los agentes agoten el tiempo o reintenten"
        },
        {
          "name": "Acotamiento de Salida",
          "what": "% de resultados de herramientas dentro de un presupuesto de tokens/filas",
          "howMeasured": "Verifica el tamaño máximo en el manejador + comprueba en la evaluación",
          "targetRange": "100% acotado — la salida sin límites es un error de correctitud, no un detalle opcional"
        }
      ],
      "datasetGuidance": "Escribe las 20 tareas como lenguaje natural que un usuario realmente escribiría, no como 'llama a la herramienta X.' La brecha entre la intención del usuario y tu superficie de herramientas es exactamente lo que estás evaluando. Confírmalas como tool_eval_tasks.jsonl con la herramienta esperada por tarea."
    },
    "failureModes": [
      {
        "name": "Herramientas Poco Descritas",
        "trigger": "Una descripción escueta de herramienta deja al modelo adivinando qué hace o cuándo usarla",
        "mitigation": "Las descripciones indican propósito, cuándo usarla y la semántica de los parámetros; evalúa la precisión de selección de herramienta e itera sobre la redacción",
        "severity": "high"
      },
      {
        "name": "Salida de Herramienta Sin Límites",
        "trigger": "Una herramienta devuelve una tabla completa / un blob grande que revienta la ventana de contexto del agente",
        "mitigation": "Pagina + trunca con una señal explícita de 'hay más disponible'; limita el tamaño del resultado en el manejador",
        "severity": "high"
      },
      {
        "name": "Falta de Auth en el Servicio de Respaldo",
        "trigger": "El servidor expone una API de respaldo sin autenticación, así que cualquiera que alcance el servidor obtiene acceso total",
        "mitigation": "Credenciales con alcance en poder del servidor (mínimo privilegio); autenticación en el transporte HTTP; nunca hagas de proxy a ciegas de la identidad del agente",
        "severity": "critical"
      },
      {
        "name": "Inyección en la Salida de Herramienta",
        "trigger": "Los datos del servicio de respaldo contienen 'instrucciones' que el modelo host trata como comandos",
        "mitigation": "Devuelve los datos como campos de resultado claramente tipados, no como prosa libre; documenta que la salida de la herramienta es contenido no confiable",
        "severity": "high"
      },
      {
        "name": "Deriva de Esquema vs API de Respaldo",
        "trigger": "La API de respaldo cambia y el esquema de la herramienta miente silenciosamente al agente",
        "mitigation": "Prueba de contrato del manejador contra la API en vivo/simulada en CI; versiona el esquema de la herramienta",
        "severity": "medium"
      }
    ],
    "deployment": {
      "minimumViable": "Un servidor publicado ejecutable de dos maneras: stdio (un bloque de configuración de Claude Desktop/Code para copiar y pegar en el README) y un endpoint remoto HTTP en streaming con autenticación. Los revisores deben poder conectarlo a su propio agente en menos de 5 minutos.",
      "observabilityMinimum": [
        "Logs de llamadas por herramienta: nombre de la herramienta, argumentos validados, latencia, éxito/fallo",
        "Tasa de rechazos de validación de esquema rastreada",
        "Fallos de autenticación en el servicio de respaldo expuestos",
        "Evaluación de uso de herramientas reejecutable en CI"
      ],
      "costCeiling": "El servidor en sí es casi gratis de alojar (stdio es local; HTTP es un servicio diminuto). El costo está en la API del servicio de respaldo — documenta los límites de tasa y cualquier costo por llamada allí."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting e Ingeniería de Contexto",
        "howThisProjectShows": "Las descripciones de herramientas y las plantillas de prompts son ingeniería de contexto — escritas para que un LLM seleccione y complete correctamente, y luego afinadas mediante evaluación"
      },
      {
        "domain": "D2",
        "domainName": "Patrones Agénticos",
        "howThisProjectShows": "Implementa la frontera estándar agente–herramienta (MCP) de forma limpia: herramientas/recursos/prompts como primitivas distintas, no un único endpoint sobrecargado"
      },
      {
        "domain": "D3",
        "domainName": "Uso e Integración de Herramientas",
        "howThisProjectShows": "Este es el dominio — esquemas tipados, elección de transporte, integración con el servicio de respaldo, salidas acotadas. Un artefacto D3 construido a propósito."
      },
      {
        "domain": "D4",
        "domainName": "Evaluación y Confiabilidad",
        "howThisProjectShows": "Evaluación desde la perspectiva del agente de las tasas de selección de herramienta + validación de esquema; pruebas de contrato contra la API de respaldo"
      },
      {
        "domain": "D5",
        "domainName": "Diseño de Sistemas",
        "howThisProjectShows": "Selección de transporte (stdio vs HTTP), versionado de esquemas, estrategia de paginación/truncamiento, diseño de la frontera de autenticación"
      },
      {
        "domain": "D6",
        "domainName": "Seguridad y Gobernanza",
        "howThisProjectShows": "Credenciales con alcance de mínimo privilegio, autenticación en el transporte, tratar la salida de la herramienta como contenido no confiable (defensa contra inyección)"
      },
      {
        "domain": "D7",
        "domainName": "Pensamiento de Producto",
        "howThisProjectShows": "Incorporación de conexión-a-tu-agente en 5 minutos, configuración de copiar y pegar, reutilizable entre hosts — construido para ser adoptado, no solo para existir"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Un servidor de herramientas MCP que envuelve [tu servicio de respaldo] — 6 herramientas tipadas, 94% de éxito en llamadas a herramientas a través de una evaluación de agente de 20 tareas, ejecutable en Claude Desktop con un solo pegado.",
      "metricsToHighlight": [
        "Tasa de éxito de llamadas a herramientas (perspectiva del agente)",
        "Tasa de aprobación al primer intento de validación de esquema",
        "Número de herramientas + las tres primitivas de MCP cubiertas",
        "Incorporación de conexión-a-tu-agente en 5 minutos"
      ],
      "antiPattern": "NO publiques una megaherramienta con un parámetro de acción de texto libre — eso es una API, no una superficie MCP. NO devuelvas salida sin límites. NO omitas la autenticación en el servicio de respaldo y lo llames 'una demo' — la frontera de autenticación es el punto."
    },
    "repoStructure": [
      "README.md (qué envuelve, la historia de las tres primitivas, configuración de Claude para copiar y pegar, resultados de la evaluación)",
      "server/ (server.py o index.ts — cableado del protocolo + transporte)",
      "tools/ (un módulo por herramienta, cada uno con un esquema tipado + descripción orientada al LLM)",
      "resources/ (endpoints de contexto de solo lectura)",
      "prompts/ (plantillas de prompts reutilizables)",
      "auth/ (manejo de credenciales con alcance + autenticación del transporte)",
      "eval/ (tool_eval_tasks.jsonl, run_tool_eval.py)",
      "tests/ (pruebas de contrato contra la API de respaldo)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 6 — Agentic Coding Tool (the staff-level self-correcting agent)
// ---------------------------------------------------------------------------

export const AGENTIC_CODING_TOOL: Localized<ProjectArchetype> = {
  en: {
  slug: "agentic-coding-tool",
  title: "Agentic Coding Tool",
  tagline:
    "A code agent that plans, edits, runs the tests, reads the failures, and repairs itself — with a human gate on anything destructive. The hardest, highest-signal build in the kit.",
  tier: "all_access",
  estimatedBuildTime: "4–5 weekends",
  difficulty: "staff",
  callbackSignal: "very_high",

  problem: {
    headline:
      "A coding agent that one-shots a diff is a parlor trick. A coding agent that runs the tests, reads the red, and fixes itself is the actual product — and it's hard.",
    detail:
      "The hard parts aren't the LLM call; they're the loop: a planner that decomposes a task, a constrained tool surface (read/edit/run) so the agent can't run wild, a test-runner feedback channel that turns failures into the next action, a reflection/repair step, and a diff proposer that a human approves before anything lands. Context management and stopping conditions are where most attempts fall apart.",
    whyItMatters:
      "Agentic coding tools are the defining product category of 2026, and building one — even a scoped one against a fixture repo — proves you understand tool loops, feedback-driven repair, context budgeting, and safety gating at the same time. This is the staff-level orchestration signal.",
  },

  architecture: {
    summary:
      "A planner turns a task into an ordered step list. A bounded tool loop executes: read files, propose edits, run the test command, and feed results back. On red tests, a reflection/repair step diagnoses the failure and re-plans the next edit. A diff proposer assembles the final patch; destructive operations and low-confidence diffs pause for human approval before apply. Approve applies the patch; reject sends the agent back to re-plan with the rejection as context.",
    mermaidDiagram: `graph TB
  T[Task] --> PL[Planner]
  PL --> LOOP{Tool Loop}
  LOOP -->|read| RD[Read Files]
  LOOP -->|edit| ED[Propose Edit]
  LOOP -->|run| TR[Test Runner]
  TR -->|green| DP[Diff Proposer]
  TR -->|red| RF[Reflection / Repair]
  RF --> PL
  DP --> GATE{Destructive or low-confidence?}
  GATE -->|no| APPLY[Apply Patch]
  GATE -->|yes| HUMAN[Human Approval]
  HUMAN -->|approve| APPLY
  HUMAN -->|reject| PL
  APPLY --> DONE[Done + trace]`,
    components: [
      { id: "planner", label: "Planner", role: "LLM" },
      { id: "reader", label: "File Reader", role: "Tool" },
      { id: "editor", label: "Edit Proposer", role: "LLM" },
      { id: "test_runner", label: "Test Runner", role: "Tool" },
      { id: "reflection", label: "Reflection / Repair", role: "LLM" },
      { id: "diff_proposer", label: "Diff Proposer", role: "LLM" },
      { id: "human_gate", label: "Human Approval Gate", role: "Human" },
      { id: "trace", label: "Trace + Cost Meter", role: "Observer" },
    ],
    keyEdges: [
      { from: "test_runner", to: "reflection", label: "on red" },
      { from: "diff_proposer", to: "human_gate", label: "destructive / low-confidence" },
      { from: "human_gate", to: "planner", label: "re-plan on reject" },
    ],
  },

  techStack: {
    required: [
      "Claude API with tool-use (Sonnet for planning/repair; the loop is the cost driver)",
      "A sandboxed exec surface — Docker container or a restricted subprocess for read/edit/run",
      "A fixture repo with a real test suite to operate against",
      "A diff/patch layer (apply via git; never let the agent write outside the workspace)",
      "OpenTelemetry or Langfuse traces — per-step, with token + cost meters",
    ],
    optional: [
      "Tree-sitter / LSP for structural edits instead of string-replace",
      "A retrieval step over the repo to keep the planner's context small",
      "Parallel candidate diffs with the eval picking the best (rerank-by-tests)",
    ],
    avoidIfNovice: [
      "Don't give the agent an unrestricted shell — read/edit/run must be a constrained, audited tool surface, not raw bash",
      "Don't skip the stopping conditions — max-iterations, cost ceiling, and no-progress detection are what keep it from looping forever",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Tool Surface", goal: "Build read / edit / run-tests as constrained, audited tools against a fixture repo in a sandbox. No free shell.", durationEstimate: "1 weekend" },
    { phase: "Phase 2 — Plan → Edit → Run Loop", goal: "Planner decomposes a task; loop edits and runs tests. Stop on green or max-iterations.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Reflection / Repair", goal: "On red, feed the failure output back; reflection diagnoses and re-plans the next edit. Add no-progress detection.", durationEstimate: "1 weekend" },
    { phase: "Phase 4 — Diff Proposer + Human Gate", goal: "Assemble the final diff; gate destructive ops and low-confidence diffs for approve/reject. Reject re-plans.", durationEstimate: "1 weekend" },
    { phase: "Phase 5 — Eval + Cost Discipline", goal: "Run the fixture-task benchmark. Add cost ceiling + token budget. Publish traces.", durationEstimate: "1 weekend" },
  ],

  evalHarness: {
    summary:
      "Build a fixture benchmark: 20–30 tasks on a real repo, each with a hidden test that defines done (SWE-bench-style). Measure whether the agent makes the tests green without breaking others, how many edits it takes, and whether it regresses unrelated tests.",
    metrics: [
      { name: "Task Pass-Rate", what: "% of tasks where the agent makes the target tests green", howMeasured: "Run the hidden test per task after the agent finishes", targetRange: "Report honestly vs a no-agent baseline; even 40–60% on a real fixture set is a strong signal" },
      { name: "Edits per Task", what: "Number of edit operations to reach green (efficiency / thrash)", howMeasured: "Count edit tool calls from the trace", targetRange: "Lower is better; a runaway count signals missing stopping conditions" },
      { name: "Test-Green Rate", what: "% of runs that terminate on a genuinely passing suite (not a forced stop)", howMeasured: "Final test-runner exit status per run", targetRange: "≥ task pass-rate — a gap means it's stopping while red" },
      { name: "Regression Rate", what: "% of tasks where previously-passing tests break", howMeasured: "Run the full suite before/after; diff failures", targetRange: "0% target — breaking unrelated tests is a critical failure" },
      { name: "Cost per Task", what: "Total LLM cost per completed task", howMeasured: "Token/cost meter on the trace", targetRange: "Document it; the loop makes this the dominant cost — cap it" },
    ],
    datasetGuidance:
      "Use a real repo with a real suite (a small OSS library or a SWE-bench-Lite slice). Hand-write the hidden tests that define 'done' per task and keep them out of the agent's view — if the agent can read the grading test, it'll write to the test, not the fix.",
  },

  hitlPattern: {
    triggers: [
      "Proposed diff includes a destructive operation (file delete, schema migration, dependency bump, force-push)",
      "Diff-proposer confidence below threshold, or the change touches > N files / > N lines",
      "Repair loop hits max-iterations without the suite going green",
    ],
    humanDecisions: [
      "Approve the patch as proposed",
      "Reject and have the agent re-plan with the rejection reason as context",
      "Edit the diff manually, then apply",
      "Abort the task",
    ],
    autoResumeRules:
      "On approve, the patch is applied via git and the run completes with a trace link. On reject, the rejection reason is fed back as context and the planner re-plans the next attempt; persistent rejections become few-shot guidance in the planner prompt.",
  },

  failureModes: [
    { name: "Runaway Edits", trigger: "The agent thrashes — edit, break, edit, break — burning cost without converging", mitigation: "Max-iteration cap, cost ceiling, and no-progress detection (same test red 2× → escalate or stop)", severity: "high" },
    { name: "Hallucinated APIs", trigger: "The agent calls a function/import that doesn't exist in the repo or its deps", mitigation: "Run tests after every edit so fabrications surface immediately; optionally a lint/type-check gate before test-run", severity: "high" },
    { name: "Context Overflow", trigger: "Whole-repo context blows the window and the planner loses the thread", mitigation: "Retrieve only relevant files; summarize prior steps; keep the planner's context budgeted and bounded", severity: "high" },
    { name: "Writing to the Grading Test", trigger: "The agent edits the test that defines 'done' instead of fixing the code", mitigation: "Hidden grading tests the agent can't read or edit; protected paths in the tool surface", severity: "critical" },
    { name: "Escaping the Sandbox", trigger: "The agent runs a command that touches files outside the workspace or hits the network", mitigation: "Constrained tool surface (no raw shell), workspace-scoped filesystem, network off by default in the sandbox", severity: "critical" },
  ],

  deployment: {
    minimumViable: "A demo against a fixture repo in a sandbox (never a live codebase), runnable on one task with a recorded trace + the human-approval step visible. Read-only network, workspace-scoped filesystem.",
    observabilityMinimum: [
      "Per-step trace: plan, each edit, each test run, reflection, final diff",
      "Token + cost meter per task with the ceiling visible",
      "Iteration counter + stopping-condition reason on every run",
      "Fixture benchmark results CI-tracked",
    ],
    costCeiling: "$30–60/month for a portfolio demo; the loop dominates cost. Hard per-task cost ceiling that aborts the run, and a max-iteration cap. Document both.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Budgeted context — retrieve relevant files, summarize prior steps — instead of dumping the whole repo; distinct planner vs repair prompts" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "Plan → act → observe → reflect → repair loop with explicit stopping conditions — a real feedback-driven agent, not a single tool-use call" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "A constrained, audited read/edit/run tool surface against a sandbox — tool design as a safety boundary, not just capability" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Fixture benchmark with hidden grading tests, pass-rate vs baseline, regression-rate tracking, edits-per-task efficiency" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Sandbox isolation, feedback loop design, context budgeting, stopping conditions, diff/apply via git — the staff-level systems story" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Human gate on destructive ops, sandbox escape prevention, protected grading tests, cost/iteration guards" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Approve/reject UX on diffs, visible cost-per-task, honest pass-rate reporting — the trade-offs a real coding-agent product makes" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "An agentic coding tool that plans/edits/tests/repairs on a fixture benchmark — 52% task pass-rate, 0% regression, human gate on destructive diffs, full traces.",
    metricsToHighlight: [
      "Task pass-rate vs no-agent baseline",
      "Regression rate (should be 0%)",
      "Cost + iterations per task (efficiency)",
      "The human-approval gate on destructive diffs",
    ],
    antiPattern:
      "Do NOT demo a one-shot diff on a happy path and call it an agent — the loop and repair are the point. Do NOT give it a raw shell. Do NOT let it read the grading tests. Do NOT report pass-rate without a baseline and a regression number.",
  },

  repoStructure: [
    "README.md (loop architecture, fixture benchmark results, a repair case study, trace links)",
    "agent/ (planner.py, repair.py, diff_proposer.py)",
    "tools/ (read.py, edit.py, run_tests.py — constrained surface)",
    "sandbox/ (container/subprocess isolation, workspace scoping)",
    "approval/ (diff_gate.py + approve/reject UI)",
    "bench/ (fixture repo, tasks/, hidden grading tests, run_bench.py)",
    "traces/ (per-step trace screenshots + dashboard link)",
    "docs/safety-model.md (sandbox + gate threat model)",
  ],
  },
  es: {
    "slug": "agentic-coding-tool",
    "title": "Agentic Coding Tool",
    "tagline": "Un agente de código que planifica, edita, ejecuta las pruebas, lee los fallos y se repara a sí mismo — con una compuerta humana para cualquier acción destructiva. La construcción más difícil y de mayor señal del kit.",
    "tier": "all_access",
    "estimatedBuildTime": "4–5 fines de semana",
    "difficulty": "staff",
    "callbackSignal": "very_high",
    "problem": {
      "headline": "Un agente de código que genera un diff de un solo intento es un truco de salón. Un agente de código que ejecuta las pruebas, lee el rojo y se corrige a sí mismo es el producto real — y es difícil.",
      "detail": "Las partes difíciles no son la llamada al LLM; son el bucle: un planificador que descompone una tarea, una superficie de herramientas restringida (leer/editar/ejecutar) para que el agente no se descontrole, un canal de retroalimentación del ejecutor de pruebas que convierte los fallos en la siguiente acción, un paso de reflexión/reparación, y un proponente de diffs que un humano aprueba antes de que algo se aplique. La gestión del contexto y las condiciones de parada son donde la mayoría de los intentos se desmoronan.",
      "whyItMatters": "Las herramientas de código agénticas son la categoría de producto definitoria de 2026, y construir una — incluso una acotada contra un repo de fixtures — demuestra que entiendes los bucles de herramientas, la reparación guiada por retroalimentación, la presupuestación de contexto y las compuertas de seguridad, todo al mismo tiempo. Esta es la señal de orquestación de nivel staff."
    },
    "architecture": {
      "summary": "Un planificador convierte una tarea en una lista ordenada de pasos. Un bucle de herramientas acotado ejecuta: leer archivos, proponer ediciones, ejecutar el comando de pruebas y retroalimentar los resultados. Ante pruebas en rojo, un paso de reflexión/reparación diagnostica el fallo y replanifica la siguiente edición. Un proponente de diffs ensambla el parche final; las operaciones destructivas y los diffs de baja confianza se pausan para aprobación humana antes de aplicarse. Aprobar aplica el parche; rechazar devuelve al agente a replanificar con el rechazo como contexto.",
      "mermaidDiagram": "graph TB\n  T[Task] --> PL[Planner]\n  PL --> LOOP{Tool Loop}\n  LOOP -->|read| RD[Read Files]\n  LOOP -->|edit| ED[Propose Edit]\n  LOOP -->|run| TR[Test Runner]\n  TR -->|green| DP[Diff Proposer]\n  TR -->|red| RF[Reflection / Repair]\n  RF --> PL\n  DP --> GATE{Destructive or low-confidence?}\n  GATE -->|no| APPLY[Apply Patch]\n  GATE -->|yes| HUMAN[Human Approval]\n  HUMAN -->|approve| APPLY\n  HUMAN -->|reject| PL\n  APPLY --> DONE[Done + trace]",
      "components": [
        {
          "id": "planner",
          "label": "Planificador",
          "role": "LLM"
        },
        {
          "id": "reader",
          "label": "Lector de archivos",
          "role": "Tool"
        },
        {
          "id": "editor",
          "label": "Proponente de ediciones",
          "role": "LLM"
        },
        {
          "id": "test_runner",
          "label": "Ejecutor de pruebas",
          "role": "Tool"
        },
        {
          "id": "reflection",
          "label": "Reflexión / Reparación",
          "role": "LLM"
        },
        {
          "id": "diff_proposer",
          "label": "Proponente de diffs",
          "role": "LLM"
        },
        {
          "id": "human_gate",
          "label": "Compuerta de aprobación humana",
          "role": "Human"
        },
        {
          "id": "trace",
          "label": "Traza + medidor de costos",
          "role": "Observer"
        }
      ],
      "keyEdges": [
        {
          "from": "test_runner",
          "to": "reflection",
          "label": "en rojo"
        },
        {
          "from": "diff_proposer",
          "to": "human_gate",
          "label": "destructivo / baja confianza"
        },
        {
          "from": "human_gate",
          "to": "planner",
          "label": "replanificar al rechazar"
        }
      ]
    },
    "techStack": {
      "required": [
        "Claude API con uso de herramientas (Sonnet para planificación/reparación; el bucle es el impulsor del costo)",
        "Una superficie de ejecución en sandbox — contenedor Docker o un subproceso restringido para leer/editar/ejecutar",
        "Un repo de fixtures con una suite de pruebas real contra la cual operar",
        "Una capa de diff/parche (aplicar vía git; nunca dejar que el agente escriba fuera del espacio de trabajo)",
        "Trazas de OpenTelemetry o Langfuse — por paso, con medidores de tokens + costos"
      ],
      "optional": [
        "Tree-sitter / LSP para ediciones estructurales en lugar de reemplazo de cadenas",
        "Un paso de recuperación sobre el repo para mantener pequeño el contexto del planificador",
        "Diffs candidatos en paralelo con la evaluación eligiendo el mejor (rerank por pruebas)"
      ],
      "avoidIfNovice": [
        "No le des al agente un shell sin restricciones — leer/editar/ejecutar debe ser una superficie de herramientas restringida y auditada, no bash crudo",
        "No omitas las condiciones de parada — el máximo de iteraciones, el techo de costo y la detección de falta de progreso son lo que evita que entre en un bucle infinito"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Superficie de herramientas",
        "goal": "Construir leer / editar / ejecutar-pruebas como herramientas restringidas y auditadas contra un repo de fixtures en un sandbox. Sin shell libre.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 2 — Bucle Planificar → Editar → Ejecutar",
        "goal": "El planificador descompone una tarea; el bucle edita y ejecuta pruebas. Detente en verde o en el máximo de iteraciones.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — Reflexión / Reparación",
        "goal": "Ante rojo, retroalimenta la salida del fallo; la reflexión diagnostica y replanifica la siguiente edición. Añade detección de falta de progreso.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 4 — Proponente de diffs + Compuerta humana",
        "goal": "Ensambla el diff final; aplica compuerta a las operaciones destructivas y a los diffs de baja confianza para aprobar/rechazar. Rechazar replanifica.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 5 — Evaluación + Disciplina de costos",
        "goal": "Ejecuta el benchmark de tareas de fixtures. Añade techo de costo + presupuesto de tokens. Publica las trazas.",
        "durationEstimate": "1 fin de semana"
      }
    ],
    "evalHarness": {
      "summary": "Construye un benchmark de fixtures: 20–30 tareas sobre un repo real, cada una con una prueba oculta que define lo que es 'terminado' (estilo SWE-bench). Mide si el agente pone las pruebas en verde sin romper otras, cuántas ediciones le toma y si provoca regresiones en pruebas no relacionadas.",
      "metrics": [
        {
          "name": "Tasa de aprobación de tareas",
          "what": "% de tareas donde el agente pone en verde las pruebas objetivo",
          "howMeasured": "Ejecutar la prueba oculta por tarea después de que el agente termine",
          "targetRange": "Reporta honestamente frente a una línea base sin agente; incluso 40–60% en un conjunto de fixtures real es una señal fuerte"
        },
        {
          "name": "Ediciones por tarea",
          "what": "Número de operaciones de edición para llegar a verde (eficiencia / retrabajo)",
          "howMeasured": "Contar las llamadas a la herramienta de edición desde la traza",
          "targetRange": "Menos es mejor; un conteo descontrolado señala condiciones de parada faltantes"
        },
        {
          "name": "Tasa de pruebas en verde",
          "what": "% de ejecuciones que terminan con una suite genuinamente aprobada (no una parada forzada)",
          "howMeasured": "Estado de salida final del ejecutor de pruebas por ejecución",
          "targetRange": "≥ tasa de aprobación de tareas — una brecha significa que se detiene estando en rojo"
        },
        {
          "name": "Tasa de regresión",
          "what": "% de tareas donde se rompen pruebas que antes pasaban",
          "howMeasured": "Ejecutar la suite completa antes/después; hacer diff de los fallos",
          "targetRange": "Objetivo 0% — romper pruebas no relacionadas es un fallo crítico"
        },
        {
          "name": "Costo por tarea",
          "what": "Costo total de LLM por tarea completada",
          "howMeasured": "Medidor de tokens/costos en la traza",
          "targetRange": "Documéntalo; el bucle lo convierte en el costo dominante — pon un tope"
        }
      ],
      "datasetGuidance": "Usa un repo real con una suite real (una pequeña biblioteca OSS o un segmento de SWE-bench-Lite). Escribe a mano las pruebas ocultas que definen 'terminado' por tarea y mantenlas fuera de la vista del agente — si el agente puede leer la prueba de calificación, escribirá para la prueba, no para la corrección."
    },
    "hitlPattern": {
      "triggers": [
        "El diff propuesto incluye una operación destructiva (borrado de archivo, migración de esquema, actualización de dependencia, force-push)",
        "La confianza del proponente de diffs está por debajo del umbral, o el cambio toca > N archivos / > N líneas",
        "El bucle de reparación alcanza el máximo de iteraciones sin que la suite pase a verde"
      ],
      "humanDecisions": [
        "Aprobar el parche tal como fue propuesto",
        "Rechazar y hacer que el agente replanifique con la razón del rechazo como contexto",
        "Editar el diff manualmente, luego aplicar",
        "Abortar la tarea"
      ],
      "autoResumeRules": "Al aprobar, el parche se aplica vía git y la ejecución se completa con un enlace a la traza. Al rechazar, la razón del rechazo se retroalimenta como contexto y el planificador replanifica el siguiente intento; los rechazos persistentes se convierten en guía few-shot en el prompt del planificador."
    },
    "failureModes": [
      {
        "name": "Ediciones descontroladas",
        "trigger": "El agente entra en retrabajo — editar, romper, editar, romper — quemando costo sin converger",
        "mitigation": "Tope máximo de iteraciones, techo de costo y detección de falta de progreso (misma prueba en rojo 2× → escalar o detener)",
        "severity": "high"
      },
      {
        "name": "APIs alucinadas",
        "trigger": "El agente llama a una función/import que no existe en el repo ni en sus dependencias",
        "mitigation": "Ejecutar pruebas después de cada edición para que las fabricaciones salgan a la luz de inmediato; opcionalmente una compuerta de lint/verificación de tipos antes de ejecutar las pruebas",
        "severity": "high"
      },
      {
        "name": "Desbordamiento de contexto",
        "trigger": "El contexto de todo el repo revienta la ventana y el planificador pierde el hilo",
        "mitigation": "Recuperar solo los archivos relevantes; resumir los pasos previos; mantener el contexto del planificador presupuestado y acotado",
        "severity": "high"
      },
      {
        "name": "Escribir en la prueba de calificación",
        "trigger": "El agente edita la prueba que define 'terminado' en lugar de corregir el código",
        "mitigation": "Pruebas de calificación ocultas que el agente no puede leer ni editar; rutas protegidas en la superficie de herramientas",
        "severity": "critical"
      },
      {
        "name": "Escapar del sandbox",
        "trigger": "El agente ejecuta un comando que toca archivos fuera del espacio de trabajo o accede a la red",
        "mitigation": "Superficie de herramientas restringida (sin shell crudo), sistema de archivos acotado al espacio de trabajo, red desactivada por defecto en el sandbox",
        "severity": "critical"
      }
    ],
    "deployment": {
      "minimumViable": "Una demo contra un repo de fixtures en un sandbox (nunca una base de código en vivo), ejecutable en una tarea con una traza grabada + el paso de aprobación humana visible. Red de solo lectura, sistema de archivos acotado al espacio de trabajo.",
      "observabilityMinimum": [
        "Traza por paso: plan, cada edición, cada ejecución de pruebas, reflexión, diff final",
        "Medidor de tokens + costos por tarea con el techo visible",
        "Contador de iteraciones + razón de la condición de parada en cada ejecución",
        "Resultados del benchmark de fixtures rastreados en CI"
      ],
      "costCeiling": "$30–60/mes para una demo de portafolio; el bucle domina el costo. Techo de costo por tarea estricto que aborta la ejecución, y un tope máximo de iteraciones. Documenta ambos."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting & Context Engineering",
        "howThisProjectShows": "Contexto presupuestado — recuperar archivos relevantes, resumir pasos previos — en lugar de volcar todo el repo; prompts distintos para planificación vs reparación"
      },
      {
        "domain": "D2",
        "domainName": "Agentic Patterns",
        "howThisProjectShows": "Bucle planificar → actuar → observar → reflexionar → reparar con condiciones de parada explícitas — un agente real guiado por retroalimentación, no una sola llamada de uso de herramientas"
      },
      {
        "domain": "D3",
        "domainName": "Tool Use & Integration",
        "howThisProjectShows": "Una superficie de herramientas de leer/editar/ejecutar restringida y auditada contra un sandbox — el diseño de herramientas como frontera de seguridad, no solo como capacidad"
      },
      {
        "domain": "D4",
        "domainName": "Evaluation & Reliability",
        "howThisProjectShows": "Benchmark de fixtures con pruebas de calificación ocultas, tasa de aprobación vs línea base, seguimiento de tasa de regresión, eficiencia de ediciones por tarea"
      },
      {
        "domain": "D5",
        "domainName": "Systems Design",
        "howThisProjectShows": "Aislamiento del sandbox, diseño del bucle de retroalimentación, presupuestación de contexto, condiciones de parada, diff/aplicación vía git — la historia de sistemas de nivel staff"
      },
      {
        "domain": "D6",
        "domainName": "Safety & Governance",
        "howThisProjectShows": "Compuerta humana en operaciones destructivas, prevención de escape del sandbox, pruebas de calificación protegidas, guardas de costo/iteraciones"
      },
      {
        "domain": "D7",
        "domainName": "Product Thinking",
        "howThisProjectShows": "UX de aprobar/rechazar en los diffs, costo por tarea visible, reporte honesto de la tasa de aprobación — las concesiones que hace un producto real de agente de código"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Una herramienta de código agéntica que planifica/edita/prueba/repara sobre un benchmark de fixtures — 52% de tasa de aprobación de tareas, 0% de regresión, compuerta humana en diffs destructivos, trazas completas.",
      "metricsToHighlight": [
        "Tasa de aprobación de tareas vs línea base sin agente",
        "Tasa de regresión (debería ser 0%)",
        "Costo + iteraciones por tarea (eficiencia)",
        "La compuerta de aprobación humana en diffs destructivos"
      ],
      "antiPattern": "NO hagas una demo de un diff de un solo intento en un camino feliz y lo llames un agente — el bucle y la reparación son el punto. NO le des un shell crudo. NO dejes que lea las pruebas de calificación. NO reportes la tasa de aprobación sin una línea base y un número de regresión."
    },
    "repoStructure": [
      "README.md (arquitectura del bucle, resultados del benchmark de fixtures, un caso de estudio de reparación, enlaces a trazas)",
      "agent/ (planner.py, repair.py, diff_proposer.py)",
      "tools/ (read.py, edit.py, run_tests.py — superficie restringida)",
      "sandbox/ (aislamiento de contenedor/subproceso, acotamiento del espacio de trabajo)",
      "approval/ (diff_gate.py + UI de aprobar/rechazar)",
      "bench/ (repo de fixtures, tasks/, pruebas de calificación ocultas, run_bench.py)",
      "traces/ (capturas de la traza por paso + enlace al dashboard)",
      "docs/safety-model.md (modelo de amenazas del sandbox + compuerta)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 7 — Conversational Assistant (the free-tier production chatbot)
// ---------------------------------------------------------------------------

export const CONVERSATIONAL_ASSISTANT: Localized<ProjectArchetype> = {
  en: {
  slug: "conversational-assistant",
  title: "Conversational Assistant",
  tagline:
    "A production chatbot with real guardrails, memory, and human escalation — not a 'chat with my docs' demo that falls over on the second message.",
  tier: "free",
  estimatedBuildTime: "2–3 weekends",
  difficulty: "intermediate",
  callbackSignal: "high",

  problem: {
    headline:
      "Everyone has built a chatbot. Almost no one has built one with memory that survives a conversation, guardrails that catch abuse, and a clean handoff to a human.",
    detail:
      "The toy version is a single LLM call with the message history pasted in. The production version manages memory (rolling summary + retrieval so it doesn't forget or overflow), runs a moderation/guardrail layer on input and output, and escalates to a human on low-confidence or policy-flagged turns. The gap between those two is exactly what separates a demo from a deployable assistant.",
    whyItMatters:
      "Conversational interfaces are the most common production LLM surface, and the most commonly done badly. Showing memory management, guardrails, and escalation — the three things that break in production — is an accessible but genuinely differentiating signal, which is why it's the free-tier gateway project.",
  },

  architecture: {
    summary:
      "A turn handler receives each message, runs an input guardrail (moderation + prompt-injection check), then assembles context from a memory store (a rolling conversation summary plus retrieved relevant turns) before calling the model. An output guardrail checks the response; low-confidence or policy-flagged turns escalate to a human handoff queue. After each turn the memory summary is updated.",
    mermaidDiagram: `graph TB
  U[User turn] --> IG[Input Guardrail: moderation + injection check]
  IG -->|flagged| ESC[Human Escalation]
  IG -->|clean| CTX[Context Assembler]
  MEM[(Memory Store: summary + retrieval)] --> CTX
  CTX --> LLM[Assistant LLM]
  LLM --> OG[Output Guardrail]
  OG -->|low-confidence / policy| ESC
  OG -->|ok| RESP[Response to user]
  RESP --> UP[Update Memory Summary]
  UP --> MEM
  ESC -->|human reply| RESP`,
    components: [
      { id: "turn_handler", label: "Turn Handler", role: "Tool" },
      { id: "input_guardrail", label: "Input Guardrail", role: "LLM" },
      { id: "context", label: "Context Assembler", role: "Tool" },
      { id: "memory", label: "Memory Store (summary + retrieval)", role: "Store" },
      { id: "assistant", label: "Assistant LLM", role: "LLM" },
      { id: "output_guardrail", label: "Output Guardrail", role: "LLM" },
      { id: "escalation", label: "Human Escalation Queue", role: "Human" },
    ],
    keyEdges: [
      { from: "input_guardrail", to: "escalation", label: "flagged input" },
      { from: "memory", to: "context", label: "summary + retrieved turns" },
      { from: "output_guardrail", to: "escalation", label: "low-confidence / policy" },
    ],
  },

  techStack: {
    required: [
      "Claude API (a flagship model for the assistant; a cheap model for guardrails)",
      "A memory store — Postgres/Redis for the rolling summary + a vector store for turn retrieval",
      "A moderation/guardrail layer (a moderation API or a classifier prompt) on input AND output",
      "FastAPI (or similar) with streaming responses",
      "A simple escalation queue + a human-reply path",
    ],
    optional: [
      "Prompt-injection detection beyond moderation (heuristics + a classifier)",
      "Per-user long-term memory (profile facts) separate from session memory",
      "A web chat UI showing the 'a human is reviewing' state on escalation",
    ],
    avoidIfNovice: [
      "Don't paste the entire history into every call — that's the toy pattern and it overflows + costs balloon; use a rolling summary + retrieval",
      "Don't guardrail only the input — model output also needs a check (it can be jailbroken into unsafe content)",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Turn Loop + Streaming", goal: "Single-turn then multi-turn chat with streaming. Naive full-history context as the baseline to beat.", durationEstimate: "1 weekend" },
    { phase: "Phase 2 — Memory", goal: "Rolling conversation summary + retrieval of relevant prior turns. Measure recall on a multi-turn probe set vs the naive baseline.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Guardrails", goal: "Input + output moderation and a prompt-injection check. Build an adversarial probe set to measure catch-rate.", durationEstimate: "3–5 evenings" },
    { phase: "Phase 4 — Escalation", goal: "Low-confidence / policy-flagged turns route to a human queue; resume after the human replies.", durationEstimate: "3 evenings" },
    { phase: "Phase 5 — Deploy + Eval", goal: "Deploy with rate limiting. Run the helpfulness + guardrail + memory evals. Publish results.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "Eval the three things that break in production: helpfulness (does it answer well), guardrails (does it catch abuse without over-refusing), and memory (does it remember across turns). Build a multi-turn probe set and an adversarial set, and judge helpfulness pairwise vs a naive single-call baseline.",
    metrics: [
      { name: "Helpfulness Win-Rate", what: "% of prompts where the assistant beats the naive single-call baseline", howMeasured: "Pairwise LLM-as-judge with position-swap on 30 prompts", targetRange: "> 60% — memory + context should produce a clear lift" },
      { name: "Guardrail Catch-Rate", what: "% of adversarial/policy-violating inputs correctly caught or refused", howMeasured: "Adversarial probe set (jailbreaks, injections, policy violations)", targetRange: "≥ 95% caught — misses are the headline risk" },
      { name: "Memory-Recall Accuracy", what: "% of multi-turn questions answered using facts established earlier in the conversation", howMeasured: "Multi-turn probe set where turn N depends on turn 1–3", targetRange: "≥ 90% — the gap vs the naive baseline is the memory story" },
      { name: "Refusal-Correctness", what: "Refuses what it should AND answers what it should (the over-refusal balance)", howMeasured: "Mixed set of should-refuse and benign-but-edgy prompts", targetRange: "Both rates high — over-refusal is as much a failure as under-refusal" },
      { name: "Cost + Latency per Turn", what: "Streaming first-token latency and per-turn cost (guardrails add calls)", howMeasured: "Timer + cost meter", targetRange: "First-token < 1s; document per-turn cost including guardrail calls" },
    ],
    datasetGuidance:
      "Two artifacts: a multi-turn probe set where later turns depend on earlier ones (tests memory), and an adversarial set (jailbreaks, prompt injection, policy violations — tests guardrails). The adversarial set is the credibility tell; commit both as JSONL.",
  },

  hitlPattern: {
    triggers: [
      "Output guardrail flags the response as low-confidence or policy-adjacent",
      "Input guardrail flags the message (potential abuse, self-harm, or policy violation)",
      "User explicitly asks for a human, or sentiment/frustration crosses a threshold",
    ],
    humanDecisions: [
      "Take over the conversation and reply directly",
      "Approve the assistant's drafted response as-is",
      "Edit the drafted response before it's sent",
      "Close the conversation with a policy-appropriate message",
    ],
    autoResumeRules:
      "On a flagged turn the user sees a 'a person is reviewing this' state; after the human replies, that reply is added to the conversation memory and the assistant resumes normally on the next turn. Escalation decisions are logged for guardrail tuning.",
  },

  failureModes: [
    { name: "Prompt Injection", trigger: "A user message (or retrieved content) tells the assistant to ignore its instructions or leak its system prompt", mitigation: "Input guardrail + treat user/retrieved content as untrusted (tagged); system prompt never echoed; output guardrail catches leaks", severity: "high" },
    { name: "Memory Poisoning", trigger: "A user plants a false 'fact' early that the assistant later treats as ground truth", mitigation: "Distinguish user-asserted from system-known facts in memory; don't let session memory override authoritative sources; expire stale facts", severity: "high" },
    { name: "Over-Refusal", trigger: "Guardrails are too aggressive and the assistant refuses benign requests, tanking helpfulness", mitigation: "Calibrate the guardrail on a benign-but-edgy set; track refusal-correctness, not just catch-rate", severity: "medium" },
    { name: "Memory Overflow / Drift", trigger: "The rolling summary loses key facts as the conversation grows, or compounds summary errors", mitigation: "Retrieve original turns for high-stakes facts; cap summary length; periodically re-anchor to source turns", severity: "medium" },
    { name: "Guardrail Bypass via Output", trigger: "The model is jailbroken into emitting unsafe content the input guardrail didn't anticipate", mitigation: "Guardrail the OUTPUT too, not just the input; escalate on output flags", severity: "high" },
  ],

  deployment: {
    minimumViable: "A public chat demo with aggressive rate limiting and a visible escalation state. The escalation queue can be as simple as a logged row + a manual reply path for the demo — but it must exist, not be mocked.",
    observabilityMinimum: [
      "Per-turn cost + latency (including guardrail calls)",
      "Guardrail flag rate + escalation rate tracked",
      "Conversation logs (with retention/privacy note) for eval + tuning",
      "Helpfulness / guardrail / memory evals re-runnable",
    ],
    costCeiling: "$20/month — guardrails on every turn add calls, so use a cheap model for moderation and the flagship only for the assistant turn. Document the per-turn cost breakdown.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Context assembly from a rolling summary + retrieved turns instead of full-history dumping; untrusted-content tagging in the prompt" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "A guarded turn pipeline (input-guard → context → model → output-guard → escalate) rather than a single bare LLM call" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Memory store (summary + vector retrieval) and a moderation layer integrated as distinct components in the turn loop" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Helpfulness win-rate vs baseline, guardrail catch-rate on an adversarial set, memory-recall accuracy on a multi-turn probe set" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Memory architecture (session summary vs retrieval vs long-term facts), streaming turn loop, escalation queue design" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Input AND output guardrails, prompt-injection + memory-poisoning defenses, escalation on policy-flagged turns, over-refusal balance" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Streaming UX, a visible human-handoff state, refusal-correctness balance, per-turn cost discipline — production chatbot trade-offs" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "A production conversational assistant with memory + guardrails + human escalation — 96% guardrail catch-rate, 92% memory recall, escalation queue, public demo.",
    metricsToHighlight: [
      "Guardrail catch-rate on the adversarial set",
      "Memory-recall accuracy vs naive baseline",
      "Helpfulness win-rate vs single-call baseline",
      "Refusal-correctness (the over-refusal balance)",
    ],
    antiPattern:
      "Do NOT submit a single-call 'chat with my docs' demo — memory, guardrails, and escalation are the whole point. Do NOT guardrail only the input. Do NOT claim 'safe' without an adversarial set behind the number. Do NOT mock the escalation path.",
  },

  repoStructure: [
    "README.md (turn-pipeline diagram, memory design, guardrail + memory + helpfulness eval results, demo URL)",
    "chat/ (turn_handler.py, context_assembler.py, streaming.py)",
    "memory/ (summary.py, retrieval.py, long_term_facts.py)",
    "guardrails/ (input_guard.py, output_guard.py, injection_check.py)",
    "escalation/ (queue.py, human_reply.py)",
    "eval/ (multi_turn_probes.jsonl, adversarial_set.jsonl, run_evals.py)",
    "deploy/ (Dockerfile, rate_limiter.py)",
    "docs/safety-model.md (injection + memory-poisoning threat model)",
  ],
  },
  es: {
    "slug": "conversational-assistant",
    "title": "Conversational Assistant",
    "tagline": "Un chatbot en producción con guardrails reales, memoria y escalado a humanos — no una demo de 'chatea con mis documentos' que se cae al segundo mensaje.",
    "tier": "free",
    "estimatedBuildTime": "2–3 fines de semana",
    "difficulty": "intermediate",
    "callbackSignal": "high",
    "problem": {
      "headline": "Todo el mundo ha construido un chatbot. Casi nadie ha construido uno con memoria que sobreviva a una conversación, guardrails que detecten el abuso y un traspaso limpio a un humano.",
      "detail": "La versión de juguete es una sola llamada al LLM con el historial de mensajes pegado dentro. La versión de producción gestiona la memoria (resumen continuo + recuperación para que no olvide ni desborde), ejecuta una capa de moderación/guardrail en la entrada y la salida, y escala a un humano en turnos de baja confianza o marcados por políticas. La distancia entre esas dos es exactamente lo que separa una demo de un asistente desplegable.",
      "whyItMatters": "Las interfaces conversacionales son la superficie de LLM en producción más común, y la que más se hace mal. Mostrar gestión de memoria, guardrails y escalado — las tres cosas que se rompen en producción — es una señal accesible pero genuinamente diferenciadora, por lo que es el proyecto de entrada del tier gratuito."
    },
    "architecture": {
      "summary": "Un turn handler recibe cada mensaje, ejecuta un guardrail de entrada (moderación + comprobación de prompt-injection) y luego ensambla el contexto desde un almacén de memoria (un resumen continuo de la conversación más turnos relevantes recuperados) antes de llamar al modelo. Un guardrail de salida comprueba la respuesta; los turnos de baja confianza o marcados por políticas escalan a una cola de traspaso a humanos. Después de cada turno se actualiza el resumen de memoria.",
      "mermaidDiagram": "graph TB\n  U[User turn] --> IG[Input Guardrail: moderation + injection check]\n  IG -->|flagged| ESC[Human Escalation]\n  IG -->|clean| CTX[Context Assembler]\n  MEM[(Memory Store: summary + retrieval)] --> CTX\n  CTX --> LLM[Assistant LLM]\n  LLM --> OG[Output Guardrail]\n  OG -->|low-confidence / policy| ESC\n  OG -->|ok| RESP[Response to user]\n  RESP --> UP[Update Memory Summary]\n  UP --> MEM\n  ESC -->|human reply| RESP",
      "components": [
        {
          "id": "turn_handler",
          "label": "Turn Handler",
          "role": "Tool"
        },
        {
          "id": "input_guardrail",
          "label": "Guardrail de Entrada",
          "role": "LLM"
        },
        {
          "id": "context",
          "label": "Ensamblador de Contexto",
          "role": "Tool"
        },
        {
          "id": "memory",
          "label": "Almacén de Memoria (resumen + recuperación)",
          "role": "Store"
        },
        {
          "id": "assistant",
          "label": "Assistant LLM",
          "role": "LLM"
        },
        {
          "id": "output_guardrail",
          "label": "Guardrail de Salida",
          "role": "LLM"
        },
        {
          "id": "escalation",
          "label": "Cola de Escalado a Humanos",
          "role": "Human"
        }
      ],
      "keyEdges": [
        {
          "from": "input_guardrail",
          "to": "escalation",
          "label": "entrada marcada"
        },
        {
          "from": "memory",
          "to": "context",
          "label": "resumen + turnos recuperados"
        },
        {
          "from": "output_guardrail",
          "to": "escalation",
          "label": "baja confianza / política"
        }
      ]
    },
    "techStack": {
      "required": [
        "Claude API (un modelo flagship para el asistente; un modelo económico para los guardrails)",
        "Un almacén de memoria — Postgres/Redis para el resumen continuo + un vector store para la recuperación de turnos",
        "Una capa de moderación/guardrail (una API de moderación o un prompt clasificador) en la entrada Y la salida",
        "FastAPI (o similar) con respuestas en streaming",
        "Una cola de escalado sencilla + una ruta de respuesta humana"
      ],
      "optional": [
        "Detección de prompt-injection más allá de la moderación (heurísticas + un clasificador)",
        "Memoria de largo plazo por usuario (hechos de perfil) separada de la memoria de sesión",
        "Una UI de chat web que muestre el estado 'un humano está revisando' al escalar"
      ],
      "avoidIfNovice": [
        "No pegues todo el historial en cada llamada — ese es el patrón de juguete y desborda + los costes se disparan; usa un resumen continuo + recuperación",
        "No pongas guardrail solo en la entrada — la salida del modelo también necesita una comprobación (puede ser jailbroken hacia contenido inseguro)"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Turn Loop + Streaming",
        "goal": "Chat de un solo turno y luego de múltiples turnos con streaming. Contexto ingenuo de historial completo como línea base a superar.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 2 — Memoria",
        "goal": "Resumen continuo de la conversación + recuperación de turnos previos relevantes. Mide el recall en un conjunto de sondeos multi-turno frente a la línea base ingenua.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — Guardrails",
        "goal": "Moderación de entrada + salida y una comprobación de prompt-injection. Construye un conjunto de sondeos adversarios para medir la tasa de detección.",
        "durationEstimate": "3–5 noches"
      },
      {
        "phase": "Fase 4 — Escalado",
        "goal": "Los turnos de baja confianza / marcados por políticas se enrutan a una cola de humanos; se reanuda tras la respuesta del humano.",
        "durationEstimate": "3 noches"
      },
      {
        "phase": "Fase 5 — Deploy + Eval",
        "goal": "Despliega con rate limiting. Ejecuta las evals de utilidad + guardrail + memoria. Publica los resultados.",
        "durationEstimate": "1 noche"
      }
    ],
    "evalHarness": {
      "summary": "Evalúa las tres cosas que se rompen en producción: utilidad (¿responde bien?), guardrails (¿detecta el abuso sin sobre-rechazar?) y memoria (¿recuerda a lo largo de los turnos?). Construye un conjunto de sondeos multi-turno y un conjunto adversario, y juzga la utilidad por pares frente a una línea base ingenua de una sola llamada.",
      "metrics": [
        {
          "name": "Win-Rate de Utilidad",
          "what": "% de prompts en los que el asistente supera la línea base ingenua de una sola llamada",
          "howMeasured": "LLM-as-judge por pares con intercambio de posición en 30 prompts",
          "targetRange": "> 60% — memoria + contexto deberían producir una mejora clara"
        },
        {
          "name": "Tasa de Detección del Guardrail",
          "what": "% de entradas adversarias/que violan políticas correctamente detectadas o rechazadas",
          "howMeasured": "Conjunto de sondeos adversarios (jailbreaks, inyecciones, violaciones de políticas)",
          "targetRange": "≥ 95% detectado — los fallos son el riesgo principal"
        },
        {
          "name": "Precisión de Recall de Memoria",
          "what": "% de preguntas multi-turno respondidas usando hechos establecidos antes en la conversación",
          "howMeasured": "Conjunto de sondeos multi-turno donde el turno N depende de los turnos 1–3",
          "targetRange": "≥ 90% — la diferencia frente a la línea base ingenua es la historia de la memoria"
        },
        {
          "name": "Corrección de Rechazo",
          "what": "Rechaza lo que debe rechazar Y responde lo que debe responder (el equilibrio del sobre-rechazo)",
          "howMeasured": "Conjunto mixto de prompts que-deben-rechazarse y benignos-pero-límite",
          "targetRange": "Ambas tasas altas — el sobre-rechazo es un fallo tan grave como el sub-rechazo"
        },
        {
          "name": "Coste + Latencia por Turno",
          "what": "Latencia del primer token en streaming y coste por turno (los guardrails añaden llamadas)",
          "howMeasured": "Temporizador + medidor de coste",
          "targetRange": "Primer token < 1s; documenta el coste por turno incluyendo las llamadas del guardrail"
        }
      ],
      "datasetGuidance": "Dos artefactos: un conjunto de sondeos multi-turno donde los turnos posteriores dependen de los anteriores (prueba la memoria), y un conjunto adversario (jailbreaks, prompt injection, violaciones de políticas — prueba los guardrails). El conjunto adversario es la señal de credibilidad; súbelos ambos como JSONL."
    },
    "hitlPattern": {
      "triggers": [
        "El guardrail de salida marca la respuesta como de baja confianza o cercana a una política",
        "El guardrail de entrada marca el mensaje (posible abuso, autolesión o violación de políticas)",
        "El usuario pide explícitamente un humano, o el sentimiento/frustración cruza un umbral"
      ],
      "humanDecisions": [
        "Tomar el control de la conversación y responder directamente",
        "Aprobar la respuesta redactada por el asistente tal cual",
        "Editar la respuesta redactada antes de enviarla",
        "Cerrar la conversación con un mensaje acorde a las políticas"
      ],
      "autoResumeRules": "En un turno marcado, el usuario ve un estado 'una persona está revisando esto'; tras la respuesta del humano, esa respuesta se añade a la memoria de la conversación y el asistente se reanuda normalmente en el siguiente turno. Las decisiones de escalado se registran para ajustar los guardrails."
    },
    "failureModes": [
      {
        "name": "Prompt Injection",
        "trigger": "Un mensaje del usuario (o contenido recuperado) le dice al asistente que ignore sus instrucciones o filtre su system prompt",
        "mitigation": "Guardrail de entrada + tratar el contenido de usuario/recuperado como no confiable (etiquetado); el system prompt nunca se repite; el guardrail de salida detecta las filtraciones",
        "severity": "high"
      },
      {
        "name": "Envenenamiento de Memoria",
        "trigger": "Un usuario planta un 'hecho' falso al principio que el asistente después trata como verdad establecida",
        "mitigation": "Distinguir en la memoria los hechos afirmados por el usuario de los conocidos por el sistema; no dejar que la memoria de sesión anule fuentes autoritativas; expirar los hechos obsoletos",
        "severity": "high"
      },
      {
        "name": "Sobre-Rechazo",
        "trigger": "Los guardrails son demasiado agresivos y el asistente rechaza peticiones benignas, hundiendo la utilidad",
        "mitigation": "Calibrar el guardrail con un conjunto benigno-pero-límite; rastrear la corrección de rechazo, no solo la tasa de detección",
        "severity": "medium"
      },
      {
        "name": "Desbordamiento / Deriva de Memoria",
        "trigger": "El resumen continuo pierde hechos clave a medida que crece la conversación, o acumula errores de resumen",
        "mitigation": "Recuperar los turnos originales para hechos de alto riesgo; limitar la longitud del resumen; re-anclar periódicamente a los turnos de origen",
        "severity": "medium"
      },
      {
        "name": "Bypass del Guardrail vía Salida",
        "trigger": "El modelo es jailbroken para emitir contenido inseguro que el guardrail de entrada no anticipó",
        "mitigation": "Poner guardrail también en la SALIDA, no solo en la entrada; escalar ante marcas de salida",
        "severity": "high"
      }
    ],
    "deployment": {
      "minimumViable": "Una demo de chat pública con rate limiting agresivo y un estado de escalado visible. La cola de escalado puede ser tan simple como una fila registrada + una ruta de respuesta manual para la demo — pero debe existir, no estar simulada.",
      "observabilityMinimum": [
        "Coste + latencia por turno (incluyendo las llamadas del guardrail)",
        "Tasa de marcas del guardrail + tasa de escalado rastreadas",
        "Registros de conversación (con nota de retención/privacidad) para eval + ajuste",
        "Evals de utilidad / guardrail / memoria re-ejecutables"
      ],
      "costCeiling": "$20/mes — los guardrails en cada turno añaden llamadas, así que usa un modelo económico para la moderación y el flagship solo para el turno del asistente. Documenta el desglose de coste por turno."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting & Context Engineering",
        "howThisProjectShows": "Ensamblado de contexto desde un resumen continuo + turnos recuperados en lugar de volcar el historial completo; etiquetado de contenido no confiable en el prompt"
      },
      {
        "domain": "D2",
        "domainName": "Agentic Patterns",
        "howThisProjectShows": "Un pipeline de turno protegido (guard de entrada → contexto → modelo → guard de salida → escalar) en lugar de una sola llamada pelada al LLM"
      },
      {
        "domain": "D3",
        "domainName": "Tool Use & Integration",
        "howThisProjectShows": "Almacén de memoria (resumen + recuperación vectorial) y una capa de moderación integrados como componentes distintos en el turn loop"
      },
      {
        "domain": "D4",
        "domainName": "Evaluation & Reliability",
        "howThisProjectShows": "Win-rate de utilidad frente a la línea base, tasa de detección del guardrail en un conjunto adversario, precisión de recall de memoria en un conjunto de sondeos multi-turno"
      },
      {
        "domain": "D5",
        "domainName": "Systems Design",
        "howThisProjectShows": "Arquitectura de memoria (resumen de sesión vs recuperación vs hechos de largo plazo), turn loop en streaming, diseño de la cola de escalado"
      },
      {
        "domain": "D6",
        "domainName": "Safety & Governance",
        "howThisProjectShows": "Guardrails de entrada Y salida, defensas contra prompt-injection + envenenamiento de memoria, escalado en turnos marcados por políticas, equilibrio del sobre-rechazo"
      },
      {
        "domain": "D7",
        "domainName": "Product Thinking",
        "howThisProjectShows": "UX de streaming, un estado visible de traspaso a humanos, equilibrio de corrección de rechazo, disciplina de coste por turno — compromisos de un chatbot en producción"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Un asistente conversacional en producción con memoria + guardrails + escalado a humanos — 96% de tasa de detección del guardrail, 92% de recall de memoria, cola de escalado, demo pública.",
      "metricsToHighlight": [
        "Tasa de detección del guardrail en el conjunto adversario",
        "Precisión de recall de memoria frente a la línea base ingenua",
        "Win-rate de utilidad frente a la línea base de una sola llamada",
        "Corrección de rechazo (el equilibrio del sobre-rechazo)"
      ],
      "antiPattern": "NO envíes una demo de una sola llamada de 'chatea con mis documentos' — la memoria, los guardrails y el escalado son todo el sentido. NO pongas guardrail solo en la entrada. NO afirmes que es 'seguro' sin un conjunto adversario detrás del número. NO simules la ruta de escalado."
    },
    "repoStructure": [
      "README.md (diagrama del turn-pipeline, diseño de memoria, resultados de evals de guardrail + memoria + utilidad, URL de la demo)",
      "chat/ (turn_handler.py, context_assembler.py, streaming.py)",
      "memory/ (summary.py, retrieval.py, long_term_facts.py)",
      "guardrails/ (input_guard.py, output_guard.py, injection_check.py)",
      "escalation/ (queue.py, human_reply.py)",
      "eval/ (multi_turn_probes.jsonl, adversarial_set.jsonl, run_evals.py)",
      "deploy/ (Dockerfile, rate_limiter.py)",
      "docs/safety-model.md (modelo de amenazas de injection + envenenamiento de memoria)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 8 — Fine-Tuning & Model Adaptation (when prompting plateaus)
// ---------------------------------------------------------------------------

export const FINE_TUNING_ADAPTATION: Localized<ProjectArchetype> = {
  en: {
  slug: "fine-tuning-adaptation",
  title: "Fine-Tuning & Model Adaptation",
  tagline:
    "The disciplined path for when prompting has plateaued: curate data, train a LoRA adapter, and prove it beats the base model on a held-out set — with the overfit check most candidates skip.",
  tier: "all_access",
  estimatedBuildTime: "3–4 weekends",
  difficulty: "senior",
  callbackSignal: "high",

  problem: {
    headline:
      "Most 'fine-tuning' projects are a notebook that trains on 50 examples, reports a training-loss curve, and never compares against the base model. That proves nothing.",
    detail:
      "The skill isn't running a training loop — a library does that. The skill is knowing when adaptation is the right tool (you've exhausted prompting + few-shot + retrieval and still have a consistent gap), curating clean instruction data, choosing a parameter-efficient method (LoRA/QLoRA) so it's affordable, and — the part that separates engineers from tutorial-followers — evaluating the adapter against the base model on data it never saw, with an explicit overfit check. The honest finding is sometimes 'prompting was enough.'",
    whyItMatters:
      "In 2026 most teams should NOT fine-tune — and a candidate who can articulate when adaptation actually pays for itself (and prove the lift rigorously) is rarer and more valuable than one who fine-tunes reflexively. Demonstrating the discipline is the signal, not the GPU bill.",
  },

  architecture: {
    summary:
      "A curation step turns raw examples into a clean, deduplicated, train/validation/held-out-split instruction dataset. A PEFT trainer (LoRA/QLoRA) trains a low-rank adapter on the base model. An evaluation step runs both the base model and the adapted model on the held-out set and reports the delta, including a train-vs-validation gap as an overfit check. If the adapter wins, it's served as a small artifact merged or loaded on top of the base weights.",
    mermaidDiagram: `graph LR
  RAW[(Raw Examples)] --> CUR[Curation — clean / dedupe / split]
  CUR --> DS[(Train / Val / Held-out)]
  DS -->|train + val| TR[LoRA / QLoRA Trainer]
  BASE[Base Model] --> TR
  TR --> ADP[Trained Adapter]
  ADP --> EV[Eval Harness]
  BASE --> EV
  DS -->|held-out| EV
  EV -->|win vs base + no overfit| SRV[Serve Adapter]
  EV -->|no lift| KEEP[Keep Base — prompting was enough]`,
    components: [
      { id: "curation", label: "Data Curation / Split", role: "Tool" },
      { id: "dataset", label: "Train/Val/Held-out Set", role: "Store" },
      { id: "base", label: "Base Model", role: "LLM" },
      { id: "trainer", label: "LoRA/QLoRA Trainer", role: "Tool" },
      { id: "adapter", label: "Trained Adapter", role: "Store" },
      { id: "eval", label: "Base-vs-Adapter Eval", role: "Observer" },
      { id: "serve", label: "Adapter Serving", role: "Tool" },
    ],
    keyEdges: [
      { from: "curation", to: "dataset", label: "clean split" },
      { from: "trainer", to: "eval", label: "adapter under test" },
      { from: "eval", to: "serve", label: "ship only if it wins" },
    ],
  },

  techStack: {
    required: [
      "Python 3.11+ with PyTorch",
      "Hugging Face PEFT + transformers (LoRA/QLoRA), or Unsloth for cheaper training",
      "An open-weights base model you can legally fine-tune (Llama / Mistral / Qwen class)",
      "A held-out eval harness that runs base AND adapter on the same unseen set",
      "Weights & Biases (or a simple logger) for train/val curves",
    ],
    optional: [
      "QLoRA 4-bit quantization to train on a single consumer/cloud GPU",
      "A managed fine-tuning API (OpenAI / Together / Anthropic, where available) as a cost/quality baseline to compare against",
      "An LLM-as-judge for open-ended quality where exact-match doesn't apply",
    ],
    avoidIfNovice: [
      "Don't full-fine-tune a large model — LoRA/QLoRA gets ~the same task lift at a fraction of the cost and is the realistic production choice",
      "Don't report a training-loss curve as your result — training loss going down proves the optimizer works, not that the model got better at the task",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Justify Adaptation", goal: "Establish the gap: show prompting + few-shot + (if relevant) retrieval on a held-out set and document the residual failure mode fine-tuning is meant to close. This framing is half the project.", durationEstimate: "3–5 evenings" },
    { phase: "Phase 2 — Curate the Dataset", goal: "Build a clean instruction set (a few hundred to a few thousand examples). Deduplicate, decontaminate against the eval set, and split train/val/held-out. Document provenance.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Train the Adapter", goal: "LoRA/QLoRA train on the base model. Watch the train-vs-val gap, not just train loss. Sweep a couple of ranks/learning rates.", durationEstimate: "1 weekend" },
    { phase: "Phase 4 — Eval vs Base", goal: "Run base and adapted models on the held-out set. Report win-rate, task accuracy, and the overfit gap. Be willing to conclude 'prompting was enough.'", durationEstimate: "3 evenings" },
    { phase: "Phase 5 — Serve + Cost Story", goal: "Load/merge the adapter behind an inference endpoint. Measure inference cost/latency delta vs the base. Publish the before/after.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "The whole project lives or dies on one comparison: adapted model vs base model on data neither was tuned on. Anything else (loss curves, train accuracy) is process, not result. Pair task accuracy with an explicit overfit check and an inference-cost delta so the trade-off is honest.",
    metrics: [
      { name: "Win-Rate vs Base", what: "% of held-out cases where the adapted model beats the base model", howMeasured: "Head-to-head on the held-out set; pairwise LLM-as-judge with position-swap for open-ended tasks", targetRange: "> 55% with a confidence interval that clears 50% — otherwise adaptation didn't pay off" },
      { name: "Task Accuracy (Adapter vs Base)", what: "Absolute accuracy/quality on the target task, both models, same held-out set", howMeasured: "Programmatic metric for checkable tasks; rubric judge otherwise", targetRange: "Report both numbers and the lift; the lift is the deliverable, not the absolute" },
      { name: "Overfit Gap (Train − Val)", what: "Difference between training-set and validation-set performance", howMeasured: "Evaluate the adapter on both train and val", targetRange: "Small gap (e.g. < 5–10pp). A large gap means it memorized — reduce rank/epochs or add data" },
      { name: "Catastrophic-Forgetting Check", what: "Did adapting on the task degrade general capability the model had before", howMeasured: "Run a small general-capability probe set on base vs adapter", targetRange: "No meaningful regression — if general skills drop, the adapter is too aggressive" },
      { name: "Inference Cost / Latency Delta", what: "Change in per-request cost and latency from serving the adapter", howMeasured: "Benchmark base vs adapter-loaded inference", targetRange: "LoRA adds little; document it so the win is net of cost" },
    ],
    datasetGuidance:
      "Three disjoint splits, decontaminated against each other, are the credibility tell — the held-out set must contain nothing the adapter trained on. Commit the split manifest (hashes/ids per split) so a reviewer can verify the disjointness. Document where each example came from and who labeled it.",
  },

  // No hitlPattern: fine-tuning is an offline training + evaluation pipeline.
  // There is no honest in-the-loop human-approval step in the training path;
  // human judgment lives in data curation, not a runtime gate.

  failureModes: [
    { name: "Data Leakage / Contamination", trigger: "Eval examples (or near-duplicates) appear in the training set, inflating the measured lift", mitigation: "Decontaminate train against val/held-out by exact + near-duplicate match; commit a split manifest proving disjointness", severity: "critical" },
    { name: "Eval-on-Train", trigger: "Reporting performance on the same data the adapter was trained on", mitigation: "Held-out set the trainer never touches; enforce the split in code and state it in the README", severity: "critical" },
    { name: "Catastrophic Forgetting", trigger: "Aggressive adaptation erases general capabilities the base model had", mitigation: "Keep LoRA rank/epochs modest; run a general-capability regression probe; mix in a little general data if needed", severity: "high" },
    { name: "Overfitting on Tiny Data", trigger: "A few-hundred-example set + too many epochs memorizes rather than generalizes", mitigation: "Watch the train-vs-val gap, early-stop, prefer more data over more epochs", severity: "high" },
    { name: "Fine-Tuning When Prompting Would Do", trigger: "Reaching for adaptation before exhausting prompting/few-shot/retrieval — cost with no real edge", mitigation: "Phase 1 baseline gate: only proceed if a documented gap survives prompting + retrieval", severity: "medium" },
  ],

  deployment: {
    minimumViable: "An inference endpoint that loads the base model + the LoRA adapter, plus a one-command eval that reproduces the base-vs-adapter comparison on a clean checkout. The adapter weights are a small artifact you can publish (e.g. to the HF Hub).",
    observabilityMinimum: [
      "Train/val curves logged (W&B or equivalent) with the run config (rank, lr, epochs)",
      "The base-vs-adapter held-out comparison re-runnable and version-pinned",
      "Inference cost + latency delta tracked vs the base model",
      "Split manifest / decontamination check checked into the repo",
    ],
    costCeiling: "QLoRA on a single rented GPU (a few dollars/hour for a few hours) keeps a portfolio run well under $50. Document the exact GPU-hours and dollar cost — the cost discipline is part of the signal.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Phase 1 exhausts prompting + few-shot + retrieval first and documents the residual gap — showing fine-tuning is a last resort, not a reflex" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "The adapted model is framed as a component (a better policy) that drops into a larger agent/pipeline, not a standalone trick" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Integrates the PEFT training stack, adapter serving, and the eval harness as a reproducible pipeline" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Held-out base-vs-adapter comparison, overfit gap, forgetting check, confidence intervals — rigorous, contamination-aware evaluation" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Curation → split → train → eval → serve pipeline with decontamination and a small-artifact serving story" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Forgetting/regression checks guard against silently degrading the model; data provenance and licensing of the base model are documented" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Net-of-cost framing: a willingness to ship the base model when adaptation doesn't earn its cost is the product-mature call" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "A LoRA adapter for [your task] that beats the base model 61% head-to-head on a decontaminated held-out set, with a documented overfit check and inference-cost delta.",
    metricsToHighlight: [
      "Win-rate vs base on a held-out set (with confidence interval)",
      "Overfit gap (train vs val) — proof it generalized",
      "Catastrophic-forgetting check result",
      "Training cost (GPU-hours / dollars) + inference-cost delta",
    ],
    antiPattern:
      "Do NOT report a training-loss curve as your result. Do NOT skip the base-model comparison — a number with no baseline is meaningless. Do NOT fine-tune before proving prompting plateaued. Do NOT evaluate on data the adapter saw.",
  },

  repoStructure: [
    "README.md (when-to-fine-tune rationale, base-vs-adapter table, overfit + forgetting checks, cost)",
    "data/ (curation scripts, split_manifest.json, provenance notes)",
    "train/ (lora_config.yaml, train.py, run logs)",
    "eval/ (base_vs_adapter.py, heldout_set.jsonl, capability_probe.jsonl, results/)",
    "serve/ (load_adapter.py, inference endpoint)",
    "adapter/ (the published LoRA weights or a Hub link)",
    ".github/workflows/ (re-run the held-out comparison)",
  ],
  },
  es: {
    "slug": "fine-tuning-adaptation",
    "title": "Fine-Tuning y Adaptación de Modelos",
    "tagline": "El camino disciplinado para cuando el prompting ha llegado a su límite: cura los datos, entrena un adaptador LoRA y demuestra que supera al modelo base en un conjunto reservado — con la verificación de sobreajuste que la mayoría de los candidatos omite.",
    "tier": "all_access",
    "estimatedBuildTime": "3–4 fines de semana",
    "difficulty": "senior",
    "callbackSignal": "high",
    "problem": {
      "headline": "La mayoría de los proyectos de 'fine-tuning' son un notebook que entrena con 50 ejemplos, reporta una curva de pérdida de entrenamiento y nunca compara contra el modelo base. Eso no prueba nada.",
      "detail": "La habilidad no es ejecutar un ciclo de entrenamiento — de eso se encarga una librería. La habilidad es saber cuándo la adaptación es la herramienta correcta (has agotado prompting + few-shot + recuperación y aún tienes una brecha consistente), curar datos de instrucción limpios, elegir un método eficiente en parámetros (LoRA/QLoRA) para que sea asequible y — la parte que separa a los ingenieros de quienes siguen tutoriales — evaluar el adaptador contra el modelo base sobre datos que nunca vio, con una verificación explícita de sobreajuste. El hallazgo honesto a veces es 'el prompting era suficiente'.",
      "whyItMatters": "En 2026 la mayoría de los equipos NO deberían hacer fine-tuning — y un candidato que pueda articular cuándo la adaptación realmente vale la pena (y probar la mejora con rigor) es más raro y más valioso que uno que hace fine-tuning por reflejo. Demostrar la disciplina es la señal, no la factura de la GPU."
    },
    "architecture": {
      "summary": "Un paso de curación convierte ejemplos crudos en un conjunto de datos de instrucción limpio, deduplicado y dividido en train/validación/reservado. Un entrenador PEFT (LoRA/QLoRA) entrena un adaptador de bajo rango sobre el modelo base. Un paso de evaluación ejecuta tanto el modelo base como el modelo adaptado sobre el conjunto reservado y reporta el delta, incluyendo la brecha entre train y validación como verificación de sobreajuste. Si el adaptador gana, se sirve como un artefacto pequeño fusionado o cargado sobre los pesos base.",
      "mermaidDiagram": "graph LR\n  RAW[(Raw Examples)] --> CUR[Curation — clean / dedupe / split]\n  CUR --> DS[(Train / Val / Held-out)]\n  DS -->|train + val| TR[LoRA / QLoRA Trainer]\n  BASE[Base Model] --> TR\n  TR --> ADP[Trained Adapter]\n  ADP --> EV[Eval Harness]\n  BASE --> EV\n  DS -->|held-out| EV\n  EV -->|win vs base + no overfit| SRV[Serve Adapter]\n  EV -->|no lift| KEEP[Keep Base — prompting was enough]",
      "components": [
        {
          "id": "curation",
          "label": "Curación / División de Datos",
          "role": "Tool"
        },
        {
          "id": "dataset",
          "label": "Conjunto Train/Val/Reservado",
          "role": "Store"
        },
        {
          "id": "base",
          "label": "Modelo Base",
          "role": "LLM"
        },
        {
          "id": "trainer",
          "label": "Entrenador LoRA/QLoRA",
          "role": "Tool"
        },
        {
          "id": "adapter",
          "label": "Adaptador Entrenado",
          "role": "Store"
        },
        {
          "id": "eval",
          "label": "Evaluación Base-vs-Adaptador",
          "role": "Observer"
        },
        {
          "id": "serve",
          "label": "Servicio del Adaptador",
          "role": "Tool"
        }
      ],
      "keyEdges": [
        {
          "from": "curation",
          "to": "dataset",
          "label": "división limpia"
        },
        {
          "from": "trainer",
          "to": "eval",
          "label": "adaptador bajo prueba"
        },
        {
          "from": "eval",
          "to": "serve",
          "label": "desplegar solo si gana"
        }
      ]
    },
    "techStack": {
      "required": [
        "Python 3.11+ con PyTorch",
        "Hugging Face PEFT + transformers (LoRA/QLoRA), o Unsloth para un entrenamiento más económico",
        "Un modelo base de pesos abiertos que puedas hacer fine-tuning legalmente (clase Llama / Mistral / Qwen)",
        "Un harness de evaluación reservado que ejecute base Y adaptador sobre el mismo conjunto no visto",
        "Weights & Biases (o un logger simple) para las curvas de train/val"
      ],
      "optional": [
        "Cuantización QLoRA de 4 bits para entrenar en una sola GPU de consumo/nube",
        "Una API gestionada de fine-tuning (OpenAI / Together / Anthropic, donde esté disponible) como línea base de costo/calidad para comparar",
        "Un LLM-as-judge para calidad abierta donde no aplique la coincidencia exacta"
      ],
      "avoidIfNovice": [
        "No hagas full-fine-tune de un modelo grande — LoRA/QLoRA logra ~la misma mejora en la tarea a una fracción del costo y es la elección realista de producción",
        "No reportes una curva de pérdida de entrenamiento como tu resultado — que la pérdida de entrenamiento baje prueba que el optimizador funciona, no que el modelo mejoró en la tarea"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Justificar la Adaptación",
        "goal": "Establece la brecha: muestra prompting + few-shot + (si aplica) recuperación sobre un conjunto reservado y documenta el modo de fallo residual que el fine-tuning busca cerrar. Este encuadre es la mitad del proyecto.",
        "durationEstimate": "3–5 noches"
      },
      {
        "phase": "Fase 2 — Curar el Conjunto de Datos",
        "goal": "Construye un conjunto de instrucción limpio (de unos cientos a unos miles de ejemplos). Deduplica, descontamina contra el conjunto de evaluación y divide en train/val/reservado. Documenta la procedencia.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — Entrenar el Adaptador",
        "goal": "Entrena con LoRA/QLoRA sobre el modelo base. Observa la brecha entre train y val, no solo la pérdida de entrenamiento. Barre un par de rangos/tasas de aprendizaje.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 4 — Evaluar vs Base",
        "goal": "Ejecuta los modelos base y adaptado sobre el conjunto reservado. Reporta la tasa de victorias, la precisión en la tarea y la brecha de sobreajuste. Sé capaz de concluir 'el prompting era suficiente'.",
        "durationEstimate": "3 noches"
      },
      {
        "phase": "Fase 5 — Servir + Historia de Costos",
        "goal": "Carga/fusiona el adaptador detrás de un endpoint de inferencia. Mide el delta de costo/latencia de inferencia vs el base. Publica el antes/después.",
        "durationEstimate": "1 noche"
      }
    ],
    "evalHarness": {
      "summary": "Todo el proyecto se sostiene o cae en una comparación: modelo adaptado vs modelo base sobre datos con los que ninguno fue ajustado. Cualquier otra cosa (curvas de pérdida, precisión de entrenamiento) es proceso, no resultado. Combina la precisión en la tarea con una verificación explícita de sobreajuste y un delta de costo de inferencia para que la compensación sea honesta.",
      "metrics": [
        {
          "name": "Tasa de Victorias vs Base",
          "what": "% de casos reservados donde el modelo adaptado supera al modelo base",
          "howMeasured": "Enfrentamiento directo sobre el conjunto reservado; LLM-as-judge por pares con intercambio de posición para tareas abiertas",
          "targetRange": "> 55% con un intervalo de confianza que supere el 50% — de lo contrario, la adaptación no valió la pena"
        },
        {
          "name": "Precisión en la Tarea (Adaptador vs Base)",
          "what": "Precisión/calidad absoluta en la tarea objetivo, ambos modelos, mismo conjunto reservado",
          "howMeasured": "Métrica programática para tareas verificables; juez con rúbrica en caso contrario",
          "targetRange": "Reporta ambos números y la mejora; la mejora es el entregable, no el valor absoluto"
        },
        {
          "name": "Brecha de Sobreajuste (Train − Val)",
          "what": "Diferencia entre el rendimiento en el conjunto de entrenamiento y en el de validación",
          "howMeasured": "Evalúa el adaptador tanto en train como en val",
          "targetRange": "Brecha pequeña (p. ej. < 5–10pp). Una brecha grande significa que memorizó — reduce el rango/épocas o agrega datos"
        },
        {
          "name": "Verificación de Olvido Catastrófico",
          "what": "Si adaptar en la tarea degradó la capacidad general que el modelo tenía antes",
          "howMeasured": "Ejecuta un pequeño conjunto de sondeo de capacidad general en base vs adaptador",
          "targetRange": "Sin regresión significativa — si las habilidades generales caen, el adaptador es demasiado agresivo"
        },
        {
          "name": "Delta de Costo / Latencia de Inferencia",
          "what": "Cambio en el costo y la latencia por solicitud al servir el adaptador",
          "howMeasured": "Benchmark de inferencia base vs con adaptador cargado",
          "targetRange": "LoRA agrega poco; documéntalo para que la victoria sea neta de costo"
        }
      ],
      "datasetGuidance": "Tres divisiones disjuntas, descontaminadas entre sí, son la señal de credibilidad — el conjunto reservado no debe contener nada con lo que el adaptador entrenó. Registra el manifiesto de la división (hashes/ids por división) para que un revisor pueda verificar la disjunción. Documenta de dónde vino cada ejemplo y quién lo etiquetó."
    },
    "failureModes": [
      {
        "name": "Fuga / Contaminación de Datos",
        "trigger": "Ejemplos de evaluación (o casi-duplicados) aparecen en el conjunto de entrenamiento, inflando la mejora medida",
        "mitigation": "Descontamina el train contra val/reservado por coincidencia exacta + casi-duplicada; registra un manifiesto de división que pruebe la disjunción",
        "severity": "critical"
      },
      {
        "name": "Evaluar sobre el Train",
        "trigger": "Reportar rendimiento sobre los mismos datos con los que se entrenó el adaptador",
        "mitigation": "Conjunto reservado que el entrenador nunca toca; impón la división en el código y decláralo en el README",
        "severity": "critical"
      },
      {
        "name": "Olvido Catastrófico",
        "trigger": "Una adaptación agresiva borra las capacidades generales que el modelo base tenía",
        "mitigation": "Mantén el rango/épocas de LoRA moderados; ejecuta un sondeo de regresión de capacidad general; mezcla un poco de datos generales si es necesario",
        "severity": "high"
      },
      {
        "name": "Sobreajuste sobre Datos Diminutos",
        "trigger": "Un conjunto de unos cientos de ejemplos + demasiadas épocas memoriza en lugar de generalizar",
        "mitigation": "Observa la brecha entre train y val, detén temprano, prefiere más datos antes que más épocas",
        "severity": "high"
      },
      {
        "name": "Hacer Fine-Tuning Cuando el Prompting Bastaría",
        "trigger": "Recurrir a la adaptación antes de agotar prompting/few-shot/recuperación — costo sin ventaja real",
        "mitigation": "Puerta de línea base de la Fase 1: solo continúa si una brecha documentada sobrevive al prompting + recuperación",
        "severity": "medium"
      }
    ],
    "deployment": {
      "minimumViable": "Un endpoint de inferencia que carga el modelo base + el adaptador LoRA, más una evaluación de un solo comando que reproduce la comparación base-vs-adaptador en un checkout limpio. Los pesos del adaptador son un artefacto pequeño que puedes publicar (p. ej. en el HF Hub).",
      "observabilityMinimum": [
        "Curvas de train/val registradas (W&B o equivalente) con la configuración del run (rango, lr, épocas)",
        "La comparación reservada base-vs-adaptador re-ejecutable y con versión fijada",
        "Delta de costo + latencia de inferencia rastreado vs el modelo base",
        "Manifiesto de división / verificación de descontaminación registrado en el repo"
      ],
      "costCeiling": "QLoRA en una sola GPU rentada (unos pocos dólares/hora por unas horas) mantiene un run de portafolio muy por debajo de $50. Documenta las GPU-horas exactas y el costo en dólares — la disciplina de costos es parte de la señal."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting e Ingeniería de Contexto",
        "howThisProjectShows": "La Fase 1 agota primero prompting + few-shot + recuperación y documenta la brecha residual — mostrando que el fine-tuning es un último recurso, no un reflejo"
      },
      {
        "domain": "D2",
        "domainName": "Patrones Agénticos",
        "howThisProjectShows": "El modelo adaptado se encuadra como un componente (una mejor política) que encaja en un agente/pipeline mayor, no como un truco aislado"
      },
      {
        "domain": "D3",
        "domainName": "Uso de Herramientas e Integración",
        "howThisProjectShows": "Integra el stack de entrenamiento PEFT, el servicio del adaptador y el harness de evaluación como un pipeline reproducible"
      },
      {
        "domain": "D4",
        "domainName": "Evaluación y Confiabilidad",
        "howThisProjectShows": "Comparación reservada base-vs-adaptador, brecha de sobreajuste, verificación de olvido, intervalos de confianza — evaluación rigurosa y consciente de la contaminación"
      },
      {
        "domain": "D5",
        "domainName": "Diseño de Sistemas",
        "howThisProjectShows": "Pipeline curación → división → entrenamiento → evaluación → servicio con descontaminación y una historia de servicio de artefacto pequeño"
      },
      {
        "domain": "D6",
        "domainName": "Seguridad y Gobernanza",
        "howThisProjectShows": "Las verificaciones de olvido/regresión protegen contra la degradación silenciosa del modelo; se documenta la procedencia de los datos y la licencia del modelo base"
      },
      {
        "domain": "D7",
        "domainName": "Pensamiento de Producto",
        "howThisProjectShows": "Encuadre neto de costo: la disposición a desplegar el modelo base cuando la adaptación no gana su costo es la decisión madura de producto"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Un adaptador LoRA para [tu tarea] que supera al modelo base 61% en enfrentamiento directo sobre un conjunto reservado descontaminado, con una verificación documentada de sobreajuste y un delta de costo de inferencia.",
      "metricsToHighlight": [
        "Tasa de victorias vs base sobre un conjunto reservado (con intervalo de confianza)",
        "Brecha de sobreajuste (train vs val) — prueba de que generalizó",
        "Resultado de la verificación de olvido catastrófico",
        "Costo de entrenamiento (GPU-horas / dólares) + delta de costo de inferencia"
      ],
      "antiPattern": "NO reportes una curva de pérdida de entrenamiento como tu resultado. NO omitas la comparación con el modelo base — un número sin línea base carece de sentido. NO hagas fine-tuning antes de probar que el prompting llegó a su límite. NO evalúes sobre datos que el adaptador vio."
    },
    "repoStructure": [
      "README.md (justificación de cuándo hacer fine-tuning, tabla base-vs-adaptador, verificaciones de sobreajuste + olvido, costo)",
      "data/ (scripts de curación, split_manifest.json, notas de procedencia)",
      "train/ (lora_config.yaml, train.py, logs de runs)",
      "eval/ (base_vs_adapter.py, heldout_set.jsonl, capability_probe.jsonl, results/)",
      "serve/ (load_adapter.py, endpoint de inferencia)",
      "adapter/ (los pesos LoRA publicados o un enlace al Hub)",
      ".github/workflows/ (re-ejecutar la comparación reservada)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 9 — Voice & Multimodal Agent (real latency budgets, real modalities)
// ---------------------------------------------------------------------------

export const VOICE_MULTIMODAL_AGENT: Localized<ProjectArchetype> = {
  en: {
  slug: "voice-multimodal-agent",
  title: "Voice & Multimodal Agent",
  tagline:
    "Speech and vision in, speech out — under a real latency budget, with the ASR-confidence gate and barge-in handling that separate a usable voice agent from a laggy demo.",
  tier: "all_access",
  estimatedBuildTime: "3–4 weekends",
  difficulty: "senior",
  callbackSignal: "high",

  problem: {
    headline:
      "A text agent has no clock. A voice agent does: every component in the STT → reason → TTS chain spends part of a budget the user feels as awkward silence.",
    detail:
      "The hard parts are real-time, not intelligence: streaming speech-to-text, a latency budget split across recognition, reasoning, and synthesis, barge-in (the user interrupts mid-response and the agent must stop talking), and graceful handling of misrecognized audio so an ASR error doesn't cascade into a confidently-wrong action. Add a vision modality and you also have to keep image context in sync with the spoken turn. Most demos ignore the clock and feel broken the moment a real person uses them.",
    whyItMatters:
      "Voice and multimodal interfaces are moving from novelty to default surface (support, in-car, accessibility, hands-free ops). An agent that holds a sub-second-feeling conversation under a real latency budget — and degrades gracefully on bad audio — is a high-signal, comparatively rare build.",
  },

  architecture: {
    summary:
      "Audio streams into a speech-to-text engine that emits partial then final transcripts. The agent core reasons over the transcript (plus any image context from a vision encoder) and streams its response into a text-to-speech engine so the user hears the first words before the full answer is generated. A barge-in detector listening on the mic can interrupt and cancel TTS mid-utterance. Low-ASR-confidence or safety-flagged audio pauses for human confirmation before any consequential action.",
    mermaidDiagram: `graph LR
  MIC[Mic Audio Stream] --> STT[Streaming STT]
  CAM[Image / Video Frame] --> VE[Vision Encoder]
  STT -->|transcript| CORE[Agent Core]
  VE -->|image context| CORE
  CORE -->|streamed tokens| TTS[Streaming TTS]
  TTS --> SPK[Speaker Output]
  MIC -->|user interrupts| BI[Barge-in Detector]
  BI -->|cancel| TTS
  STT -->|low confidence / flagged| HUM[Human Confirmation]
  HUM -->|confirm| CORE`,
    components: [
      { id: "stt", label: "Streaming STT", role: "Tool" },
      { id: "vision", label: "Vision Encoder", role: "Tool" },
      { id: "core", label: "Agent Core", role: "LLM" },
      { id: "tts", label: "Streaming TTS", role: "Tool" },
      { id: "barge_in", label: "Barge-in Detector", role: "Observer" },
      { id: "confirm", label: "Human Confirmation", role: "Human" },
      { id: "speaker", label: "Speaker Output", role: "Tool" },
    ],
    keyEdges: [
      { from: "stt", to: "core", label: "final transcript" },
      { from: "core", to: "tts", label: "streamed tokens" },
      { from: "barge_in", to: "tts", label: "cancel on interrupt" },
      { from: "stt", to: "confirm", label: "low ASR confidence" },
    ],
  },

  techStack: {
    required: [
      "A streaming STT engine (Whisper / faster-whisper, Deepgram, or a realtime speech API)",
      "A streaming-capable agent core (Claude or another LLM with token streaming)",
      "A streaming TTS engine (ElevenLabs, a realtime TTS API, or a local model)",
      "WebSockets (or WebRTC) for full-duplex audio so barge-in is possible",
      "A latency harness that times each stage and the end-to-end turn",
    ],
    optional: [
      "A unified realtime speech-to-speech API (lower latency, less to wire — but show you understand the pipeline it hides)",
      "A vision encoder / multimodal model for the image-in modality",
      "Voice-activity detection (VAD) for turn-taking and barge-in",
    ],
    avoidIfNovice: [
      "Don't build request/response (record-whole-clip → transcribe → answer → synthesize-whole-clip) — the dead air makes it feel broken; stream from the first stage",
      "Don't ignore barge-in — an agent that can't be interrupted feels robotic and is the fastest way to look like a toy",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Non-Streaming Baseline", goal: "Record → STT → agent → TTS → play, end to end. Measure the (bad) end-to-end latency. This is the number you're going to beat.", durationEstimate: "1 weekend" },
    { phase: "Phase 2 — Stream Every Stage", goal: "Partial-transcript STT, token-streamed agent, first-audio-fast TTS over WebSockets. Measure time-to-first-audio, not just total time.", durationEstimate: "1 weekend" },
    { phase: "Phase 3 — Barge-in + Turn-Taking", goal: "VAD-driven barge-in that cancels TTS when the user speaks; clean turn boundaries. This is what makes it feel human.", durationEstimate: "3–5 evenings" },
    { phase: "Phase 4 — Confidence Gate + Multimodal", goal: "Gate low-ASR-confidence turns to a confirmation step; add the image-in modality and keep it in sync with the spoken turn.", durationEstimate: "1 weekend" },
    { phase: "Phase 5 — Latency Budget + Demo", goal: "Publish the per-stage latency budget (p50/p95) and a recorded conversation that survives interruptions and a misheard word.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "Two axes most voice demos never measure: accuracy (does it understand and do the right thing) and the clock (does it feel responsive). Word-error-rate alone is insufficient — measure end-to-end and time-to-first-audio latency, task success despite ASR noise, and barge-in correctness.",
    metrics: [
      { name: "Word Error Rate (WER)", what: "STT transcription error rate on your audio probe set, including accented/noisy samples", howMeasured: "Standard WER against reference transcripts on a held-out audio set", targetRange: "Report on clean AND noisy splits — the noisy number is the honest one" },
      { name: "End-to-End Latency (p50/p95)", what: "Time from end-of-user-speech to start-of-agent-speech (the felt latency)", howMeasured: "Timestamp each stage; measure time-to-first-audio across many turns", targetRange: "Time-to-first-audio p95 < ~1.5s feels conversational; report the per-stage breakdown" },
      { name: "Task Success Despite ASR Noise", what: "% of tasks completed correctly even when the transcript has errors", howMeasured: "Run the task probe set over clean + degraded audio; check the action taken", targetRange: "High and stable across noise levels — a steep drop means ASR errors are cascading" },
      { name: "Barge-in Handling Rate", what: "% of interruptions where the agent stops talking promptly and processes the new input", howMeasured: "Scripted interruption tests; measure stop-talking latency and recovery", targetRange: "≥ 95% clean interrupts; stop-talking latency < ~300ms" },
      { name: "Confirmation-Gate Precision", what: "Does the low-confidence gate fire on genuinely uncertain transcripts (not on every turn)", howMeasured: "Check gate firing against ASR-confidence labels on the probe set", targetRange: "Fires on the truly-uncertain; a gate that fires constantly destroys the UX" },
    ],
    datasetGuidance:
      "Your audio probe set is the artifact: clean samples, accented/noisy samples, and scripted interruption scenarios, each with a reference transcript and an expected action. Commit it (or a manifest of clips + transcripts). The noisy + interruption cases are what prove it's not a clean-room demo.",
  },

  hitlPattern: {
    triggers: [
      "ASR confidence falls below a threshold on a consequential turn",
      "The audio (or requested action) is flagged by a safety classifier",
      "A high-stakes action is about to be taken on a transcript the agent isn't sure it heard correctly",
    ],
    humanDecisions: [
      "Confirm the transcript / the agent's understanding before it acts",
      "Correct the misheard transcript and let the agent proceed",
      "Cancel the action and ask the user to repeat",
    ],
    autoResumeRules:
      "On a low-confidence consequential turn the agent voices a short confirmation ('Did you mean…?') instead of acting blindly; on confirm it proceeds with the corrected transcript, and the confidence threshold + the misrecognition are logged to tune the gate over time.",
  },

  failureModes: [
    { name: "Latency Blowup", trigger: "Stages run sequentially and non-streamed, so the user hears seconds of dead air before any response", mitigation: "Stream every stage; budget latency per stage and alert on regressions; measure time-to-first-audio, not just total", severity: "high" },
    { name: "ASR Error Cascade", trigger: "A misrecognized word becomes a confidently-wrong action with no second-guess", mitigation: "Confidence gate + confirmation on consequential turns; design the agent to ask rather than assume on low confidence", severity: "high" },
    { name: "No Barge-in", trigger: "The agent keeps talking over a user who is trying to interrupt or correct it", mitigation: "VAD-driven barge-in that cancels TTS within a few hundred ms and re-listens", severity: "high" },
    { name: "Modality Desync", trigger: "The spoken turn and the image context refer to different moments — the agent answers about a stale frame", mitigation: "Timestamp + bind image context to the turn; drop or re-fetch stale frames before reasoning", severity: "medium" },
    { name: "Audio-Channel Prompt Injection", trigger: "Spoken or background audio contains instructions the agent treats as commands", mitigation: "Treat transcripts as untrusted user content; safety-classify audio; gate consequential actions behind confirmation", severity: "medium" },
  ],

  deployment: {
    minimumViable: "A browser (WebRTC/WebSocket) or phone demo where a reviewer can actually talk to it, interrupt it, and hear it recover — plus a published per-stage latency budget. A recorded conversation is acceptable, but live-callable is the strong version.",
    observabilityMinimum: [
      "Per-stage latency logged (STT / reason / TTS) with p50/p95 + end-to-end",
      "WER and barge-in handling tracked on the probe set, re-runnable",
      "Confirmation-gate firing rate + outcomes logged",
      "Per-turn cost across STT + LLM + TTS tracked (three meters)",
    ],
    costCeiling: "Realtime STT + TTS APIs bill per second of audio and add up faster than text — set a monthly cap, prefer streaming models, and document the per-minute-of-conversation cost.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "The agent core's prompt handles noisy transcripts and ask-don't-assume behavior on low confidence — context engineered for an imperfect input channel" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "A streaming, full-duplex turn loop with barge-in and a confidence-gated confirmation step — not a request/response call" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Integrates STT, TTS, a vision encoder, and full-duplex transport into one real-time pipeline with a latency budget" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "WER on clean+noisy audio, latency p50/p95, task success under ASR noise, barge-in correctness — measures the clock, not just accuracy" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Latency-budgeted streaming pipeline, full-duplex transport, modality synchronization — real-time systems design" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Confidence-gated confirmation before consequential actions, audio safety-classification, transcripts treated as untrusted input" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Time-to-first-audio as the felt metric, barge-in as a UX requirement, per-minute cost discipline — built for a real conversation, not a clip" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "A voice agent with sub-1.5s time-to-first-audio, 96% clean barge-in handling, and an ASR-confidence gate that confirms before acting — live-callable in the browser.",
    metricsToHighlight: [
      "End-to-end / time-to-first-audio latency (p50/p95) with per-stage breakdown",
      "WER on clean vs noisy audio",
      "Barge-in handling rate + stop-talking latency",
      "Task success under ASR noise (does it stay correct when it mishears)",
    ],
    antiPattern:
      "Do NOT ship a record-then-respond demo and call it a voice agent — the dead air is the whole problem. Do NOT report only clean-audio WER. Do NOT skip barge-in. Do NOT act on a low-confidence transcript without a confirmation path.",
  },

  repoStructure: [
    "README.md (latency-budget diagram, WER clean/noisy, barge-in results, live demo link)",
    "audio/ (streaming STT + TTS wiring, VAD, barge-in handler)",
    "agent/ (core reasoning, low-confidence ask-don't-assume prompt)",
    "vision/ (image-in encoder + turn synchronization)",
    "transport/ (WebSocket/WebRTC full-duplex layer)",
    "eval/ (audio_probes/ with transcripts, latency_harness.py, barge_in_tests.py)",
    "deploy/ (browser demo, rate limiting)",
    "docs/latency-budget.md (per-stage p50/p95 + cost-per-minute)",
  ],
  },
  es: {
    "slug": "voice-multimodal-agent",
    "title": "Agente de Voz y Multimodal",
    "tagline": "Voz y visión de entrada, voz de salida — bajo un presupuesto de latencia real, con la compuerta de confianza del ASR y el manejo de barge-in que separan a un agente de voz utilizable de una demo con retardos.",
    "tier": "all_access",
    "estimatedBuildTime": "3–4 fines de semana",
    "difficulty": "senior",
    "callbackSignal": "high",
    "problem": {
      "headline": "Un agente de texto no tiene reloj. Un agente de voz sí: cada componente de la cadena STT → razonar → TTS gasta parte de un presupuesto que el usuario percibe como un silencio incómodo.",
      "detail": "Las partes difíciles son de tiempo real, no de inteligencia: reconocimiento de voz en streaming, un presupuesto de latencia repartido entre reconocimiento, razonamiento y síntesis, barge-in (el usuario interrumpe a mitad de la respuesta y el agente debe dejar de hablar) y el manejo elegante del audio mal reconocido para que un error del ASR no se convierta en cascada en una acción segura pero equivocada. Añade una modalidad de visión y también tienes que mantener el contexto de la imagen sincronizado con el turno hablado. La mayoría de las demos ignoran el reloj y parecen rotas en el momento en que una persona real las usa.",
      "whyItMatters": "Las interfaces de voz y multimodales están pasando de ser una novedad a ser la superficie por defecto (soporte, en el automóvil, accesibilidad, operaciones manos libres). Un agente que sostiene una conversación con sensación de menos de un segundo bajo un presupuesto de latencia real — y que se degrada con elegancia ante audio malo — es un proyecto de alta señal y comparativamente poco común."
    },
    "architecture": {
      "summary": "El audio se transmite en streaming a un motor de speech-to-text que emite transcripciones parciales y luego finales. El núcleo del agente razona sobre la transcripción (más cualquier contexto de imagen de un codificador de visión) y transmite su respuesta en streaming a un motor de text-to-speech para que el usuario escuche las primeras palabras antes de que se genere la respuesta completa. Un detector de barge-in que escucha el micrófono puede interrumpir y cancelar el TTS a mitad del enunciado. El audio con baja confianza del ASR o marcado por seguridad se pausa para confirmación humana antes de cualquier acción consecuente.",
      "mermaidDiagram": "graph LR\n  MIC[Mic Audio Stream] --> STT[Streaming STT]\n  CAM[Image / Video Frame] --> VE[Vision Encoder]\n  STT -->|transcript| CORE[Agent Core]\n  VE -->|image context| CORE\n  CORE -->|streamed tokens| TTS[Streaming TTS]\n  TTS --> SPK[Speaker Output]\n  MIC -->|user interrupts| BI[Barge-in Detector]\n  BI -->|cancel| TTS\n  STT -->|low confidence / flagged| HUM[Human Confirmation]\n  HUM -->|confirm| CORE",
      "components": [
        {
          "id": "stt",
          "label": "Streaming STT",
          "role": "Tool"
        },
        {
          "id": "vision",
          "label": "Vision Encoder",
          "role": "Tool"
        },
        {
          "id": "core",
          "label": "Agent Core",
          "role": "LLM"
        },
        {
          "id": "tts",
          "label": "Streaming TTS",
          "role": "Tool"
        },
        {
          "id": "barge_in",
          "label": "Barge-in Detector",
          "role": "Observer"
        },
        {
          "id": "confirm",
          "label": "Human Confirmation",
          "role": "Human"
        },
        {
          "id": "speaker",
          "label": "Speaker Output",
          "role": "Tool"
        }
      ],
      "keyEdges": [
        {
          "from": "stt",
          "to": "core",
          "label": "transcripción final"
        },
        {
          "from": "core",
          "to": "tts",
          "label": "tokens en streaming"
        },
        {
          "from": "barge_in",
          "to": "tts",
          "label": "cancelar al interrumpir"
        },
        {
          "from": "stt",
          "to": "confirm",
          "label": "baja confianza del ASR"
        }
      ]
    },
    "techStack": {
      "required": [
        "Un motor de STT en streaming (Whisper / faster-whisper, Deepgram o una API de voz en tiempo real)",
        "Un núcleo de agente con capacidad de streaming (Claude u otro LLM con streaming de tokens)",
        "Un motor de TTS en streaming (ElevenLabs, una API de TTS en tiempo real o un modelo local)",
        "WebSockets (o WebRTC) para audio full-duplex de modo que el barge-in sea posible",
        "Un arnés de latencia que cronometra cada etapa y el turno de extremo a extremo"
      ],
      "optional": [
        "Una API unificada de speech-to-speech en tiempo real (menor latencia, menos que cablear — pero demuestra que entiendes el pipeline que oculta)",
        "Un codificador de visión / modelo multimodal para la modalidad de entrada de imagen",
        "Detección de actividad de voz (VAD) para el turnado y el barge-in"
      ],
      "avoidIfNovice": [
        "No construyas request/response (grabar-clip-completo → transcribir → responder → sintetizar-clip-completo) — el silencio muerto hace que parezca roto; transmite en streaming desde la primera etapa",
        "No ignores el barge-in — un agente que no puede ser interrumpido parece robótico y es la forma más rápida de parecer un juguete"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Línea base sin streaming",
        "goal": "Grabar → STT → agente → TTS → reproducir, de extremo a extremo. Mide la (mala) latencia de extremo a extremo. Este es el número que vas a superar.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 2 — Streaming en cada etapa",
        "goal": "STT con transcripción parcial, agente con streaming de tokens, TTS con primer audio rápido sobre WebSockets. Mide el tiempo hasta el primer audio, no solo el tiempo total.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 3 — Barge-in + turnado",
        "goal": "Barge-in impulsado por VAD que cancela el TTS cuando el usuario habla; límites de turno limpios. Esto es lo que lo hace sentir humano.",
        "durationEstimate": "3–5 noches"
      },
      {
        "phase": "Fase 4 — Compuerta de confianza + multimodal",
        "goal": "Enviar los turnos de baja confianza del ASR a un paso de confirmación; añade la modalidad de entrada de imagen y mantenla sincronizada con el turno hablado.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 5 — Presupuesto de latencia + demo",
        "goal": "Publica el presupuesto de latencia por etapa (p50/p95) y una conversación grabada que sobreviva a interrupciones y a una palabra mal escuchada.",
        "durationEstimate": "1 noche"
      }
    ],
    "evalHarness": {
      "summary": "Dos ejes que la mayoría de las demos de voz nunca miden: precisión (¿entiende y hace lo correcto?) y el reloj (¿se siente responsivo?). La tasa de error por palabra por sí sola es insuficiente — mide la latencia de extremo a extremo y el tiempo hasta el primer audio, el éxito de la tarea a pesar del ruido del ASR y la corrección del barge-in.",
      "metrics": [
        {
          "name": "Tasa de Error por Palabra (WER)",
          "what": "Tasa de error de transcripción del STT en tu conjunto de sondas de audio, incluyendo muestras con acento/ruidosas",
          "howMeasured": "WER estándar contra transcripciones de referencia en un conjunto de audio reservado",
          "targetRange": "Reporta en las divisiones limpia Y ruidosa — el número ruidoso es el honesto"
        },
        {
          "name": "Latencia de extremo a extremo (p50/p95)",
          "what": "Tiempo desde el fin del habla del usuario hasta el inicio del habla del agente (la latencia percibida)",
          "howMeasured": "Marca de tiempo en cada etapa; mide el tiempo hasta el primer audio a lo largo de muchos turnos",
          "targetRange": "Un tiempo hasta el primer audio p95 < ~1.5s se siente conversacional; reporta el desglose por etapa"
        },
        {
          "name": "Éxito de la tarea a pesar del ruido del ASR",
          "what": "% de tareas completadas correctamente incluso cuando la transcripción tiene errores",
          "howMeasured": "Ejecuta el conjunto de sondas de tarea sobre audio limpio + degradado; verifica la acción tomada",
          "targetRange": "Alto y estable a través de los niveles de ruido — una caída pronunciada significa que los errores del ASR están en cascada"
        },
        {
          "name": "Tasa de manejo de barge-in",
          "what": "% de interrupciones en las que el agente deja de hablar rápidamente y procesa la nueva entrada",
          "howMeasured": "Pruebas de interrupción con guion; mide la latencia para dejar de hablar y la recuperación",
          "targetRange": "≥ 95% de interrupciones limpias; latencia para dejar de hablar < ~300ms"
        },
        {
          "name": "Precisión de la compuerta de confirmación",
          "what": "¿Se dispara la compuerta de baja confianza en transcripciones genuinamente inciertas (no en cada turno)?",
          "howMeasured": "Verifica el disparo de la compuerta contra las etiquetas de confianza del ASR en el conjunto de sondas",
          "targetRange": "Se dispara en las verdaderamente inciertas; una compuerta que se dispara constantemente destruye la UX"
        }
      ],
      "datasetGuidance": "Tu conjunto de sondas de audio es el artefacto: muestras limpias, muestras con acento/ruidosas y escenarios de interrupción con guion, cada uno con una transcripción de referencia y una acción esperada. Súbelo al repositorio (o un manifiesto de clips + transcripciones). Los casos ruidosos + de interrupción son los que prueban que no es una demo de laboratorio."
    },
    "hitlPattern": {
      "triggers": [
        "La confianza del ASR cae por debajo de un umbral en un turno consecuente",
        "El audio (o la acción solicitada) es marcado por un clasificador de seguridad",
        "Una acción de alto riesgo está a punto de tomarse sobre una transcripción que el agente no está seguro de haber escuchado correctamente"
      ],
      "humanDecisions": [
        "Confirmar la transcripción / la comprensión del agente antes de que actúe",
        "Corregir la transcripción mal escuchada y dejar que el agente proceda",
        "Cancelar la acción y pedir al usuario que repita"
      ],
      "autoResumeRules": "En un turno consecuente de baja confianza, el agente pronuncia una breve confirmación ('¿Quisiste decir…?') en lugar de actuar a ciegas; al confirmar, procede con la transcripción corregida, y el umbral de confianza + el mal reconocimiento se registran para ajustar la compuerta con el tiempo."
    },
    "failureModes": [
      {
        "name": "Explosión de latencia",
        "trigger": "Las etapas se ejecutan de forma secuencial y sin streaming, de modo que el usuario escucha segundos de silencio muerto antes de cualquier respuesta",
        "mitigation": "Transmite en streaming en cada etapa; presupuesta la latencia por etapa y alerta ante regresiones; mide el tiempo hasta el primer audio, no solo el total",
        "severity": "high"
      },
      {
        "name": "Cascada de errores del ASR",
        "trigger": "Una palabra mal reconocida se convierte en una acción segura pero equivocada sin ninguna reconsideración",
        "mitigation": "Compuerta de confianza + confirmación en turnos consecuentes; diseña el agente para que pregunte en lugar de asumir ante baja confianza",
        "severity": "high"
      },
      {
        "name": "Sin barge-in",
        "trigger": "El agente sigue hablando por encima de un usuario que intenta interrumpirlo o corregirlo",
        "mitigation": "Barge-in impulsado por VAD que cancela el TTS en unos cientos de ms y vuelve a escuchar",
        "severity": "high"
      },
      {
        "name": "Desincronización de modalidades",
        "trigger": "El turno hablado y el contexto de imagen se refieren a momentos diferentes — el agente responde sobre un frame obsoleto",
        "mitigation": "Marca de tiempo + vincula el contexto de imagen al turno; descarta o vuelve a obtener los frames obsoletos antes de razonar",
        "severity": "medium"
      },
      {
        "name": "Inyección de prompts por canal de audio",
        "trigger": "El audio hablado o de fondo contiene instrucciones que el agente trata como comandos",
        "mitigation": "Trata las transcripciones como contenido de usuario no confiable; clasifica el audio por seguridad; protege las acciones consecuentes tras una confirmación",
        "severity": "medium"
      }
    ],
    "deployment": {
      "minimumViable": "Una demo en navegador (WebRTC/WebSocket) o en teléfono donde un revisor pueda realmente hablarle, interrumpirlo y escucharlo recuperarse — más un presupuesto de latencia por etapa publicado. Una conversación grabada es aceptable, pero la versión fuerte es que se pueda llamar en vivo.",
      "observabilityMinimum": [
        "Latencia por etapa registrada (STT / razonar / TTS) con p50/p95 + extremo a extremo",
        "WER y manejo de barge-in rastreados en el conjunto de sondas, reejecutable",
        "Tasa de disparo de la compuerta de confirmación + resultados registrados",
        "Costo por turno a través de STT + LLM + TTS rastreado (tres medidores)"
      ],
      "costCeiling": "Las APIs de STT + TTS en tiempo real facturan por segundo de audio y se acumulan más rápido que el texto — establece un tope mensual, prefiere modelos de streaming y documenta el costo por minuto de conversación."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting & Context Engineering",
        "howThisProjectShows": "El prompt del núcleo del agente maneja transcripciones ruidosas y un comportamiento de preguntar-no-asumir ante baja confianza — contexto diseñado para un canal de entrada imperfecto"
      },
      {
        "domain": "D2",
        "domainName": "Agentic Patterns",
        "howThisProjectShows": "Un bucle de turnos en streaming full-duplex con barge-in y un paso de confirmación con compuerta de confianza — no una llamada request/response"
      },
      {
        "domain": "D3",
        "domainName": "Tool Use & Integration",
        "howThisProjectShows": "Integra STT, TTS, un codificador de visión y transporte full-duplex en un único pipeline en tiempo real con un presupuesto de latencia"
      },
      {
        "domain": "D4",
        "domainName": "Evaluation & Reliability",
        "howThisProjectShows": "WER en audio limpio+ruidoso, latencia p50/p95, éxito de la tarea bajo ruido del ASR, corrección del barge-in — mide el reloj, no solo la precisión"
      },
      {
        "domain": "D5",
        "domainName": "Systems Design",
        "howThisProjectShows": "Pipeline en streaming con presupuesto de latencia, transporte full-duplex, sincronización de modalidades — diseño de sistemas en tiempo real"
      },
      {
        "domain": "D6",
        "domainName": "Safety & Governance",
        "howThisProjectShows": "Confirmación con compuerta de confianza antes de acciones consecuentes, clasificación de seguridad del audio, transcripciones tratadas como entrada no confiable"
      },
      {
        "domain": "D7",
        "domainName": "Product Thinking",
        "howThisProjectShows": "El tiempo hasta el primer audio como la métrica percibida, el barge-in como requisito de UX, disciplina de costo por minuto — construido para una conversación real, no para un clip"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Un agente de voz con un tiempo hasta el primer audio de menos de 1.5s, 96% de manejo limpio de barge-in y una compuerta de confianza del ASR que confirma antes de actuar — llamable en vivo en el navegador.",
      "metricsToHighlight": [
        "Latencia de extremo a extremo / tiempo hasta el primer audio (p50/p95) con desglose por etapa",
        "WER en audio limpio vs ruidoso",
        "Tasa de manejo de barge-in + latencia para dejar de hablar",
        "Éxito de la tarea bajo ruido del ASR (¿se mantiene correcto cuando escucha mal?)"
      ],
      "antiPattern": "NO entregues una demo de grabar-luego-responder y la llames un agente de voz — el silencio muerto es todo el problema. NO reportes solo el WER de audio limpio. NO omitas el barge-in. NO actúes sobre una transcripción de baja confianza sin una ruta de confirmación."
    },
    "repoStructure": [
      "README.md (diagrama de presupuesto de latencia, WER limpio/ruidoso, resultados de barge-in, enlace a demo en vivo)",
      "audio/ (cableado de STT + TTS en streaming, VAD, manejador de barge-in)",
      "agent/ (razonamiento central, prompt de preguntar-no-asumir ante baja confianza)",
      "vision/ (codificador de entrada de imagen + sincronización de turnos)",
      "transport/ (capa full-duplex de WebSocket/WebRTC)",
      "eval/ (audio_probes/ con transcripciones, latency_harness.py, barge_in_tests.py)",
      "deploy/ (demo en navegador, limitación de tasa)",
      "docs/latency-budget.md (p50/p95 por etapa + costo por minuto)"
    ]
  },
};

// ---------------------------------------------------------------------------
// Archetype 10 — Data & Embeddings Pipeline (the unglamorous backbone)
// ---------------------------------------------------------------------------

export const DATA_EMBEDDINGS_PIPELINE: Localized<ProjectArchetype> = {
  en: {
  slug: "data-embeddings-pipeline",
  title: "Data & Embeddings Pipeline",
  tagline:
    "The unglamorous backbone every RAG and AI system quietly depends on: reliable ingestion → chunking → embedding → vector store, with incremental freshness and observability — not a one-off load script.",
  tier: "all_access",
  estimatedBuildTime: "2–3 weekends",
  difficulty: "intermediate",
  callbackSignal: "high",

  problem: {
    headline:
      "Every RAG demo embeds a folder once and never touches it again. Real systems have sources that change, documents that get added and deleted, and an index that must stay fresh without a full re-embed every night.",
    detail:
      "The interesting engineering isn't the embedding call — it's everything around it: connectors that pull from real sources, chunking that doesn't sever ideas mid-thought, batched embedding that respects rate limits and cost, idempotent upserts so re-runs don't duplicate, incremental refresh + backfill so only changed documents are re-embedded, and observability so you can see freshness lag and dedupe rate. This is the layer that makes retrieval quality possible — and the layer most candidates skip because it isn't flashy.",
    whyItMatters:
      "Retrieval quality is capped by pipeline quality: stale, badly-chunked, or duplicated data means a great retriever still returns garbage. A candidate who builds the reliable, observable, incrementally-fresh pipeline shows they understand that RAG is a data problem first — and that's exactly the maturity teams are short on.",
  },

  architecture: {
    summary:
      "Source connectors pull documents (and detect changes since the last run). A chunker splits them with overlap and structure-awareness. An embedder batches chunks through the embedding model with rate-limit + cost control. Idempotent upserts write vectors + metadata to the vector store keyed by a content hash so re-runs don't duplicate. An incremental refresh job re-embeds only changed/new documents and tombstones deleted ones; a backfill path handles full rebuilds. Observability tracks freshness lag, throughput, and dedupe rate throughout.",
    mermaidDiagram: `graph LR
  SRC[(Source Connectors)] -->|new / changed docs| CH[Chunker — overlap + structure-aware]
  CH --> EMB[Embedder — batched + rate-limited]
  EMB --> UP[Idempotent Upsert — content-hash keyed]
  UP --> VS[(Vector Store + Metadata)]
  SRC -->|change detection| INC[Incremental Refresh / Backfill]
  INC --> CH
  VS --> OBS[Observability — freshness / throughput / dedupe]
  UP --> OBS`,
    components: [
      { id: "connectors", label: "Source Connectors", role: "Tool" },
      { id: "chunker", label: "Chunker", role: "Tool" },
      { id: "embedder", label: "Batched Embedder", role: "Tool" },
      { id: "upsert", label: "Idempotent Upsert", role: "Tool" },
      { id: "vector_store", label: "Vector Store + Metadata", role: "Store" },
      { id: "refresh", label: "Incremental Refresh / Backfill", role: "Tool" },
      { id: "observability", label: "Pipeline Observability", role: "Observer" },
    ],
    keyEdges: [
      { from: "connectors", to: "chunker", label: "changed docs only" },
      { from: "embedder", to: "upsert", label: "content-hash keyed" },
      { from: "refresh", to: "chunker", label: "re-embed changed" },
    ],
  },

  techStack: {
    required: [
      "Python 3.11+",
      "A vector store with metadata + upsert (pgvector, Qdrant, or Weaviate)",
      "An embedding model (a hosted embeddings API, or a local sentence-transformer)",
      "A chunking strategy that respects structure (headings/sentences), not blind fixed-size",
      "A small retrieval probe set to measure whether the pipeline actually helps retrieval",
    ],
    optional: [
      "An orchestrator (Prefect / Dagster / Airflow) for scheduled incremental refresh",
      "Change-data-capture or source webhooks for near-real-time freshness",
      "A reranker downstream to separate pipeline quality from retrieval quality in evals",
    ],
    avoidIfNovice: [
      "Don't blind fixed-size chunk by character count — it severs sentences and ideas; chunk on structure with overlap and measure the difference",
      "Don't re-embed the entire corpus on every run — it's slow, expensive, and hides the actual skill (incremental upsert keyed by content hash)",
    ],
  },

  buildPhases: [
    { phase: "Phase 1 — Ingest + Chunk", goal: "One connector, structure-aware chunking with overlap. Establish a retrieval probe set to measure chunk quality against.", durationEstimate: "1 weekend" },
    { phase: "Phase 2 — Batched Embed + Upsert", goal: "Batch embeddings with rate-limit/cost control; idempotent upsert keyed by content hash so re-runs don't duplicate. Measure throughput.", durationEstimate: "3–5 evenings" },
    { phase: "Phase 3 — Incremental Refresh", goal: "Change detection so only new/changed docs re-embed; tombstone deletes. Measure freshness lag from source change to index update.", durationEstimate: "1 weekend" },
    { phase: "Phase 4 — Observability", goal: "Track freshness lag, throughput, dedupe rate, and embedding cost. Build a small dashboard or structured logs.", durationEstimate: "3 evenings" },
    { phase: "Phase 5 — Eval + Backfill", goal: "Run the retrieval probe set to show chunking/freshness improves precision/recall; prove a clean full backfill path exists.", durationEstimate: "1 evening" },
  ],

  evalHarness: {
    summary:
      "Eval the pipeline by what it enables (retrieval quality on a probe set) and by its operational health (throughput, freshness, dedupe). The retrieval number is what connects this 'plumbing' to outcomes a hiring manager cares about.",
    metrics: [
      { name: "Retrieval Precision / Recall@k", what: "On a labeled probe set, do the right chunks come back for a query", howMeasured: "Probe set of queries with known relevant chunks; measure precision/recall@k", targetRange: "Show the lift from structure-aware chunking vs a naive fixed-size baseline" },
      { name: "Embedding Throughput", what: "Documents/chunks embedded per minute under batching + rate limits", howMeasured: "Time a representative batch; track chunks/min", targetRange: "Report it; the point is that batching beats one-at-a-time meaningfully" },
      { name: "Freshness Lag", what: "Time from a source document changing to the index reflecting it", howMeasured: "Modify a source doc; measure until the new content is retrievable", targetRange: "Bounded and documented (minutes for incremental; not 'next full rebuild')" },
      { name: "Dedupe Rate", what: "% of re-run content correctly recognized as unchanged and skipped (not re-embedded/duplicated)", howMeasured: "Re-run the pipeline; count skipped vs re-embedded vs duplicated", targetRange: "Near 100% of unchanged docs skipped; zero duplicate vectors" },
      { name: "Embedding Cost per 1k Chunks", what: "Dollar cost to embed a unit of corpus (the operating cost knob)", howMeasured: "Embedding-API cost / chunks embedded", targetRange: "Document it; incremental refresh should keep steady-state cost low" },
    ],
    datasetGuidance:
      "Commit a retrieval probe set: queries paired with the chunk ids that should be retrieved. It's what lets you prove a chunking or freshness change improved retrieval instead of asserting it. Also commit a small change-set scenario (add/modify/delete docs) to exercise incremental refresh.",
  },

  // No hitlPattern: this is automated data infrastructure that runs on a
  // schedule. There is no honest in-the-loop human-approval step in the
  // ingest → embed → upsert path; human judgment lives in eval, not runtime.

  failureModes: [
    { name: "Chunk-Boundary Loss", trigger: "Fixed-size chunking severs a sentence or splits a concept across chunks, so neither chunk retrieves well", mitigation: "Structure-aware chunking (headings/sentences) with overlap; measure retrieval precision vs a naive baseline", severity: "high" },
    { name: "Stale Index", trigger: "Source documents change but the index still serves the old content", mitigation: "Change detection + incremental refresh; track freshness lag and alert when it exceeds the SLO", severity: "high" },
    { name: "Duplicate Vectors on Re-run", trigger: "A non-idempotent load inserts the same content again, polluting retrieval and inflating cost", mitigation: "Upsert keyed by content hash; dedupe-rate metric catches regressions", severity: "high" },
    { name: "Embedding-Model Drift on Re-embed", trigger: "Re-embedding part of the corpus with a different model/version mixes incompatible vector spaces", mitigation: "Pin the embedding model + version in metadata; a model change forces a full, consistent backfill, not a partial mix", severity: "high" },
    { name: "Rate-Limit / Cost Blowup", trigger: "Un-batched, un-throttled embedding hits rate limits or runs up a surprising bill", mitigation: "Batch + backoff + a cost meter; never re-embed unchanged content", severity: "medium" },
  ],

  deployment: {
    minimumViable: "A one-command initial load plus a scheduled (or manually-triggerable) incremental refresh, against a real source, writing to a live vector store. A reviewer should be able to change a source doc and watch the index update.",
    observabilityMinimum: [
      "Freshness lag per source tracked (and an SLO)",
      "Throughput (chunks/min) + embedding cost per run",
      "Dedupe rate + duplicate-vector count (should be zero)",
      "Retrieval probe set re-runnable to catch quality regressions",
    ],
    costCeiling: "Steady-state cost is dominated by re-embedding — incremental refresh is the cost control. Document embedding cost per 1k chunks and the steady-state monthly cost once backfill is done.",
  },

  domainEvidence: [
    { domain: "D1", domainName: "Prompting & Context Engineering", howThisProjectShows: "Chunking + metadata design directly shapes the context a downstream LLM receives — context engineering at the data layer" },
    { domain: "D2", domainName: "Agentic Patterns", howThisProjectShows: "The pipeline is the substrate agents retrieve from; incremental refresh keeps that substrate trustworthy for any agent on top of it" },
    { domain: "D3", domainName: "Tool Use & Integration", howThisProjectShows: "Integrates source connectors, an embedding model, and a vector store with idempotent upserts — a real integration pipeline" },
    { domain: "D4", domainName: "Evaluation & Reliability", howThisProjectShows: "Retrieval precision/recall on a probe set plus operational metrics (freshness, dedupe, throughput) — the pipeline is measured, not assumed" },
    { domain: "D5", domainName: "Systems Design", howThisProjectShows: "Incremental vs full backfill, content-hash idempotency, embedding-version pinning, freshness SLO — the core data-systems design decisions" },
    { domain: "D6", domainName: "Safety & Governance", howThisProjectShows: "Provenance + version metadata per chunk, tombstoning deleted source content (so removed data stops being retrieved), embedding-model pinning" },
    { domain: "D7", domainName: "Product Thinking", howThisProjectShows: "Freshness as an SLO, cost controlled by incremental refresh, retrieval quality tied to a business outcome — treats plumbing as a product" },
  ],

  portfolioFraming: {
    oneLineDescription:
      "An incremental embeddings pipeline over [your source] — structure-aware chunking lifts recall@5, content-hash idempotent upserts (0 duplicates), and sub-5-minute freshness lag.",
    metricsToHighlight: [
      "Retrieval precision/recall@k vs a naive fixed-size-chunk baseline",
      "Freshness lag (source change → retrievable)",
      "Dedupe rate / duplicate-vector count (zero)",
      "Embedding cost per 1k chunks + steady-state monthly cost",
    ],
    antiPattern:
      "Do NOT submit a one-shot 'embed this folder' script — incremental refresh, idempotency, and freshness are the whole point. Do NOT blind fixed-size chunk. Do NOT re-embed the whole corpus every run. Do NOT mix embedding-model versions in one index.",
  },

  repoStructure: [
    "README.md (pipeline diagram, chunking-vs-baseline retrieval results, freshness + dedupe metrics)",
    "connectors/ (source pulls + change detection)",
    "chunking/ (structure_aware_chunker.py, overlap config)",
    "embedding/ (batched_embedder.py, rate_limit + cost meter)",
    "store/ (upsert.py — content-hash keyed, tombstones, version pinning)",
    "refresh/ (incremental.py, backfill.py)",
    "eval/ (retrieval_probes.jsonl, change_set_scenario/, run_eval.py)",
    "observability/ (freshness + throughput + dedupe dashboards/logs)",
  ],
  },
  es: {
    "slug": "data-embeddings-pipeline",
    "title": "Pipeline de Datos y Embeddings",
    "tagline": "La columna vertebral poco glamorosa de la que dependen silenciosamente todos los sistemas de RAG e IA: ingesta → fragmentación → embedding → almacén de vectores confiable, con actualización incremental y observabilidad — no un script de carga de una sola vez.",
    "tier": "all_access",
    "estimatedBuildTime": "2–3 fines de semana",
    "difficulty": "intermediate",
    "callbackSignal": "high",
    "problem": {
      "headline": "Cada demo de RAG embebe una carpeta una vez y nunca vuelve a tocarla. Los sistemas reales tienen fuentes que cambian, documentos que se agregan y eliminan, y un índice que debe mantenerse fresco sin un re-embedding completo cada noche.",
      "detail": "La ingeniería interesante no es la llamada de embedding — es todo lo que la rodea: conectores que extraen de fuentes reales, fragmentación que no corta ideas a la mitad, embedding por lotes que respeta límites de tasa y costo, upserts idempotentes para que las re-ejecuciones no dupliquen, actualización incremental + backfill para que solo se re-embeban los documentos que cambiaron, y observabilidad para que puedas ver el retraso de frescura y la tasa de deduplicación. Esta es la capa que hace posible la calidad de recuperación — y la capa que la mayoría de los candidatos omite porque no es llamativa.",
      "whyItMatters": "La calidad de recuperación está limitada por la calidad del pipeline: datos obsoletos, mal fragmentados o duplicados significan que incluso un gran recuperador devuelve basura. Un candidato que construye el pipeline confiable, observable e incrementalmente fresco demuestra que entiende que RAG es primero un problema de datos — y esa es exactamente la madurez que les falta a los equipos."
    },
    "architecture": {
      "summary": "Los conectores de fuente extraen documentos (y detectan cambios desde la última ejecución). Un fragmentador los divide con superposición y conciencia de estructura. Un embedder procesa los fragmentos por lotes a través del modelo de embedding con control de límites de tasa + costo. Los upserts idempotentes escriben vectores + metadatos en el almacén de vectores indexados por un hash de contenido para que las re-ejecuciones no dupliquen. Un trabajo de actualización incremental re-embeba solo los documentos cambiados/nuevos y marca con tombstone los eliminados; una ruta de backfill maneja las reconstrucciones completas. La observabilidad rastrea el retraso de frescura, el rendimiento y la tasa de deduplicación a lo largo de todo el proceso.",
      "mermaidDiagram": "graph LR\n  SRC[(Source Connectors)] -->|new / changed docs| CH[Chunker — overlap + structure-aware]\n  CH --> EMB[Embedder — batched + rate-limited]\n  EMB --> UP[Idempotent Upsert — content-hash keyed]\n  UP --> VS[(Vector Store + Metadata)]\n  SRC -->|change detection| INC[Incremental Refresh / Backfill]\n  INC --> CH\n  VS --> OBS[Observability — freshness / throughput / dedupe]\n  UP --> OBS",
      "components": [
        {
          "id": "connectors",
          "label": "Conectores de Fuente",
          "role": "Tool"
        },
        {
          "id": "chunker",
          "label": "Fragmentador",
          "role": "Tool"
        },
        {
          "id": "embedder",
          "label": "Embedder por Lotes",
          "role": "Tool"
        },
        {
          "id": "upsert",
          "label": "Upsert Idempotente",
          "role": "Tool"
        },
        {
          "id": "vector_store",
          "label": "Almacén de Vectores + Metadatos",
          "role": "Store"
        },
        {
          "id": "refresh",
          "label": "Actualización Incremental / Backfill",
          "role": "Tool"
        },
        {
          "id": "observability",
          "label": "Observabilidad del Pipeline",
          "role": "Observer"
        }
      ],
      "keyEdges": [
        {
          "from": "connectors",
          "to": "chunker",
          "label": "solo documentos cambiados"
        },
        {
          "from": "embedder",
          "to": "upsert",
          "label": "indexado por hash de contenido"
        },
        {
          "from": "refresh",
          "to": "chunker",
          "label": "re-embeber cambiados"
        }
      ]
    },
    "techStack": {
      "required": [
        "Python 3.11+",
        "Un almacén de vectores con metadatos + upsert (pgvector, Qdrant o Weaviate)",
        "Un modelo de embedding (una API de embeddings alojada, o un sentence-transformer local)",
        "Una estrategia de fragmentación que respete la estructura (encabezados/oraciones), no un tamaño fijo a ciegas",
        "Un pequeño conjunto de sondas de recuperación para medir si el pipeline realmente ayuda a la recuperación"
      ],
      "optional": [
        "Un orquestador (Prefect / Dagster / Airflow) para actualización incremental programada",
        "Change-data-capture o webhooks de fuente para frescura casi en tiempo real",
        "Un reranker aguas abajo para separar la calidad del pipeline de la calidad de recuperación en las evaluaciones"
      ],
      "avoidIfNovice": [
        "No fragmentes a ciegas por tamaño fijo según el conteo de caracteres — corta oraciones e ideas; fragmenta según la estructura con superposición y mide la diferencia",
        "No re-embebas todo el corpus en cada ejecución — es lento, costoso y oculta la habilidad real (upsert incremental indexado por hash de contenido)"
      ]
    },
    "buildPhases": [
      {
        "phase": "Fase 1 — Ingesta + Fragmentación",
        "goal": "Un conector, fragmentación consciente de la estructura con superposición. Establece un conjunto de sondas de recuperación contra el cual medir la calidad de los fragmentos.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 2 — Embedding por Lotes + Upsert",
        "goal": "Embeddings por lotes con control de límite de tasa/costo; upsert idempotente indexado por hash de contenido para que las re-ejecuciones no dupliquen. Mide el rendimiento.",
        "durationEstimate": "3–5 noches"
      },
      {
        "phase": "Fase 3 — Actualización Incremental",
        "goal": "Detección de cambios para que solo se re-embeban documentos nuevos/cambiados; tombstone en las eliminaciones. Mide el retraso de frescura desde el cambio de la fuente hasta la actualización del índice.",
        "durationEstimate": "1 fin de semana"
      },
      {
        "phase": "Fase 4 — Observabilidad",
        "goal": "Rastrea el retraso de frescura, el rendimiento, la tasa de deduplicación y el costo de embedding. Construye un pequeño panel o logs estructurados.",
        "durationEstimate": "3 noches"
      },
      {
        "phase": "Fase 5 — Evaluación + Backfill",
        "goal": "Ejecuta el conjunto de sondas de recuperación para demostrar que la fragmentación/frescura mejora la precisión/recall; demuestra que existe una ruta de backfill completa limpia.",
        "durationEstimate": "1 noche"
      }
    ],
    "evalHarness": {
      "summary": "Evalúa el pipeline por lo que habilita (calidad de recuperación en un conjunto de sondas) y por su salud operacional (rendimiento, frescura, deduplicación). El número de recuperación es lo que conecta esta 'plomería' con los resultados que le importan a un gerente de contratación.",
      "metrics": [
        {
          "name": "Precisión / Recall@k de Recuperación",
          "what": "En un conjunto de sondas etiquetado, si los fragmentos correctos regresan para una consulta",
          "howMeasured": "Conjunto de sondas de consultas con fragmentos relevantes conocidos; mide precisión/recall@k",
          "targetRange": "Muestra la mejora de la fragmentación consciente de la estructura vs una línea base ingenua de tamaño fijo"
        },
        {
          "name": "Rendimiento de Embedding",
          "what": "Documentos/fragmentos embebidos por minuto bajo procesamiento por lotes + límites de tasa",
          "howMeasured": "Cronometra un lote representativo; rastrea fragmentos/min",
          "targetRange": "Repórtalo; el punto es que el procesamiento por lotes supera significativamente al de uno en uno"
        },
        {
          "name": "Retraso de Frescura",
          "what": "Tiempo desde que un documento de la fuente cambia hasta que el índice lo refleja",
          "howMeasured": "Modifica un documento de la fuente; mide hasta que el nuevo contenido sea recuperable",
          "targetRange": "Acotado y documentado (minutos para incremental; no 'la próxima reconstrucción completa')"
        },
        {
          "name": "Tasa de Deduplicación",
          "what": "% de contenido re-ejecutado correctamente reconocido como sin cambios y omitido (no re-embebido/duplicado)",
          "howMeasured": "Re-ejecuta el pipeline; cuenta omitidos vs re-embebidos vs duplicados",
          "targetRange": "Cerca del 100% de los documentos sin cambios omitidos; cero vectores duplicados"
        },
        {
          "name": "Costo de Embedding por cada 1k Fragmentos",
          "what": "Costo en dólares para embeber una unidad de corpus (la palanca del costo operativo)",
          "howMeasured": "Costo de la API de embedding / fragmentos embebidos",
          "targetRange": "Documéntalo; la actualización incremental debe mantener bajo el costo en estado estable"
        }
      ],
      "datasetGuidance": "Confirma un conjunto de sondas de recuperación: consultas emparejadas con los ids de fragmentos que deberían recuperarse. Es lo que te permite demostrar que un cambio de fragmentación o frescura mejoró la recuperación en lugar de solo afirmarlo. También confirma un pequeño escenario de conjunto de cambios (agregar/modificar/eliminar documentos) para ejercitar la actualización incremental."
    },
    "failureModes": [
      {
        "name": "Pérdida en el Límite del Fragmento",
        "trigger": "La fragmentación de tamaño fijo corta una oración o divide un concepto entre fragmentos, por lo que ninguno recupera bien",
        "mitigation": "Fragmentación consciente de la estructura (encabezados/oraciones) con superposición; mide la precisión de recuperación vs una línea base ingenua",
        "severity": "high"
      },
      {
        "name": "Índice Obsoleto",
        "trigger": "Los documentos de la fuente cambian pero el índice sigue sirviendo el contenido antiguo",
        "mitigation": "Detección de cambios + actualización incremental; rastrea el retraso de frescura y alerta cuando supere el SLO",
        "severity": "high"
      },
      {
        "name": "Vectores Duplicados en Re-ejecución",
        "trigger": "Una carga no idempotente inserta el mismo contenido de nuevo, contaminando la recuperación e inflando el costo",
        "mitigation": "Upsert indexado por hash de contenido; la métrica de tasa de deduplicación detecta regresiones",
        "severity": "high"
      },
      {
        "name": "Deriva del Modelo de Embedding en el Re-embedding",
        "trigger": "Re-embeber parte del corpus con un modelo/versión diferente mezcla espacios de vectores incompatibles",
        "mitigation": "Fija el modelo de embedding + versión en los metadatos; un cambio de modelo fuerza un backfill completo y consistente, no una mezcla parcial",
        "severity": "high"
      },
      {
        "name": "Explosión de Límite de Tasa / Costo",
        "trigger": "El embedding sin lotes ni limitación alcanza los límites de tasa o genera una factura sorpresa",
        "mitigation": "Lotes + backoff + un medidor de costo; nunca re-embeber contenido sin cambios",
        "severity": "medium"
      }
    ],
    "deployment": {
      "minimumViable": "Una carga inicial de un comando más una actualización incremental programada (o activable manualmente), contra una fuente real, escribiendo en un almacén de vectores en vivo. Un revisor debería poder cambiar un documento de la fuente y ver el índice actualizarse.",
      "observabilityMinimum": [
        "Retraso de frescura por fuente rastreado (y un SLO)",
        "Rendimiento (fragmentos/min) + costo de embedding por ejecución",
        "Tasa de deduplicación + conteo de vectores duplicados (debe ser cero)",
        "Conjunto de sondas de recuperación re-ejecutable para detectar regresiones de calidad"
      ],
      "costCeiling": "El costo en estado estable está dominado por el re-embedding — la actualización incremental es el control de costos. Documenta el costo de embedding por cada 1k fragmentos y el costo mensual en estado estable una vez completado el backfill."
    },
    "domainEvidence": [
      {
        "domain": "D1",
        "domainName": "Prompting & Context Engineering",
        "howThisProjectShows": "El diseño de fragmentación + metadatos moldea directamente el contexto que recibe un LLM aguas abajo — ingeniería de contexto en la capa de datos"
      },
      {
        "domain": "D2",
        "domainName": "Agentic Patterns",
        "howThisProjectShows": "El pipeline es el sustrato del que los agentes recuperan; la actualización incremental mantiene ese sustrato confiable para cualquier agente que esté encima"
      },
      {
        "domain": "D3",
        "domainName": "Tool Use & Integration",
        "howThisProjectShows": "Integra conectores de fuente, un modelo de embedding y un almacén de vectores con upserts idempotentes — un pipeline de integración real"
      },
      {
        "domain": "D4",
        "domainName": "Evaluation & Reliability",
        "howThisProjectShows": "Precisión/recall de recuperación en un conjunto de sondas más métricas operacionales (frescura, deduplicación, rendimiento) — el pipeline se mide, no se asume"
      },
      {
        "domain": "D5",
        "domainName": "Systems Design",
        "howThisProjectShows": "Incremental vs backfill completo, idempotencia por hash de contenido, fijación de versión de embedding, SLO de frescura — las decisiones centrales de diseño de sistemas de datos"
      },
      {
        "domain": "D6",
        "domainName": "Safety & Governance",
        "howThisProjectShows": "Metadatos de procedencia + versión por fragmento, tombstoning del contenido de fuente eliminado (para que los datos removidos dejen de recuperarse), fijación del modelo de embedding"
      },
      {
        "domain": "D7",
        "domainName": "Product Thinking",
        "howThisProjectShows": "La frescura como un SLO, el costo controlado por la actualización incremental, la calidad de recuperación atada a un resultado de negocio — trata la plomería como un producto"
      }
    ],
    "portfolioFraming": {
      "oneLineDescription": "Un pipeline de embeddings incremental sobre [tu fuente] — la fragmentación consciente de la estructura eleva el recall@5, upserts idempotentes por hash de contenido (0 duplicados), y un retraso de frescura de menos de 5 minutos.",
      "metricsToHighlight": [
        "Precisión/recall@k de recuperación vs una línea base de fragmentación de tamaño fijo ingenua",
        "Retraso de frescura (cambio de fuente → recuperable)",
        "Tasa de deduplicación / conteo de vectores duplicados (cero)",
        "Costo de embedding por cada 1k fragmentos + costo mensual en estado estable"
      ],
      "antiPattern": "NO envíes un script de una sola vez de 'embeber esta carpeta' — la actualización incremental, la idempotencia y la frescura son todo el punto. NO fragmentes a ciegas por tamaño fijo. NO re-embebas todo el corpus en cada ejecución. NO mezcles versiones del modelo de embedding en un mismo índice."
    },
    "repoStructure": [
      "README.md (diagrama del pipeline, resultados de recuperación fragmentación-vs-línea-base, métricas de frescura + deduplicación)",
      "connectors/ (extracciones de fuente + detección de cambios)",
      "chunking/ (structure_aware_chunker.py, configuración de superposición)",
      "embedding/ (batched_embedder.py, rate_limit + medidor de costo)",
      "store/ (upsert.py — indexado por hash de contenido, tombstones, fijación de versión)",
      "refresh/ (incremental.py, backfill.py)",
      "eval/ (retrieval_probes.jsonl, change_set_scenario/, run_eval.py)",
      "observability/ (paneles/logs de frescura + rendimiento + deduplicación)"
    ]
  },
};

// ---------------------------------------------------------------------------
// All archetypes, in order
// ---------------------------------------------------------------------------

export const PROJECT_ARCHETYPES: Localized<ProjectArchetype>[] = [
  RAG_AGENT,
  MULTI_AGENT_RESEARCHER,
  HITL_WORKFLOW_AGENT,
  EVAL_HARNESS,
  MCP_SERVER,
  AGENTIC_CODING_TOOL,
  CONVERSATIONAL_ASSISTANT,
  FINE_TUNING_ADAPTATION,
  VOICE_MULTIMODAL_AGENT,
  DATA_EMBEDDINGS_PIPELINE,
];

// PROJECT_ARCHETYPES elements are Localized<ProjectArchetype>; slug is a
// locale-invariant routing identifier (never translated), so lookups match
// on `.en.slug`. Returns the RESOLVED ProjectArchetype (not the Localized
// wrapper) for the given locale (default "en") -- this is the signature
// choice that requires zero changes at existing call sites that don't yet
// pass a locale, while still letting locale-aware callers resolve content
// in one step instead of unwrapping twice.
export const getArchetypeBySlug = (
  slug: string,
  locale: string = "en",
): ProjectArchetype | undefined => {
  const match = PROJECT_ARCHETYPES.find((a) => a.en.slug === slug);
  return match ? L(match, locale) : undefined;
};

// ---------------------------------------------------------------------------
// Prescriptive GitHub README template (Free tier shows outline; All Access shows full template)
// ---------------------------------------------------------------------------

export interface ReadmeSection {
  number: number;
  title: string;
  purpose: string;
  required: boolean;
  tierGate: TierGate; // free shows outline/purpose; all_access shows the full template body
  freeSummary: string; // what free-tier users see
  templateBody: string; // full template — all_access only
  rationale: string; // why this section exists at all
  commonMistakes: string[];
}

export const README_SECTIONS: Localized<ReadmeSection[]> = {
  en: [
  {
    number: 1,
    title: "One-Line Hook + Banner",
    purpose: "A hiring manager reads the first 15 seconds. Hook or lose.",
    required: true,
    tierGate: "free",
    freeSummary:
      "First line states the outcome in one sentence. Include: what the system does, key metric, link to live demo.",
    templateBody: `# [Project Name]

> [One sentence: what this does + the ONE headline metric + demo URL]
> Example: "Agentic RAG over 5,000 AI safety papers — 84% grounded-answer rate, p95 3.2s. [Live demo](https://demo.url) · [Traces](https://langfuse.url)"

![Status badge](...) ![Evals badge](...) ![License](...)

**TL;DR in 3 bullets:**
- **Problem:** [one line]
- **Approach:** [one line — name the pattern, not the framework]
- **Result:** [one line — the metric + link to eval table below]
`,
    rationale: "The first 100 words decide whether anyone reads further. Bury the lede and lose the reader.",
    commonMistakes: [
      "Starting with 'This project explores...'",
      "Burying the demo link in paragraph 5",
      "Leading with the tech stack instead of the outcome",
    ],
  },
  {
    number: 2,
    title: "Architecture Diagram",
    purpose: "A Mermaid diagram beats 500 words of description. No diagram = no signal.",
    required: true,
    tierGate: "free",
    freeSummary:
      "Mermaid diagram showing agents, tools, data stores, and human gates. One diagram, not three.",
    templateBody: `## Architecture

\`\`\`mermaid
graph LR
  U[User] --> P[Planner]
  P --> R[Retriever]
  R --> S[Synthesizer]
  S --> G[Groundedness Check]
  G -->|pass| A[Answer]
  G -->|fail| H[HITL]
  S --> T[(Traces)]
\`\`\`

**Key design decisions:**
1. **Why [pattern X] over [alternative Y]:** [one sentence]
2. **Why [tool Z]:** [one sentence]
3. **What's NOT in scope:** [one sentence — shows discipline]
`,
    rationale: "Senior engineers read diagrams before prose. The presence of a diagram is itself a signal.",
    commonMistakes: [
      "Screenshot of a hand-drawn diagram (looks lazy)",
      "3 overlapping diagrams that don't reconcile",
      "A diagram with no labeled edges",
    ],
  },
  {
    number: 3,
    title: "Evaluation Results Table",
    purpose: "The credibility engine. Numbers beat adjectives. A README without an eval table is a science-fair project.",
    required: true,
    tierGate: "all_access",
    freeSummary:
      "(All Access) Full table with metrics, baseline, current, target, and link to eval runner.",
    templateBody: `## Evaluation

Eval set: \`eval/queries.jsonl\` (30 hand-labeled queries across 5 categories). Re-runnable via \`make eval\`.

| Metric | Baseline | Current | Target | How measured |
|---|---|---|---|---|
| Grounded answer rate | 58% | 84% | 85% | Ragas faithfulness + hand-check |
| Citation precision | 72% | 91% | 90% | Hand-check random 20 |
| Refusal on out-of-corpus | 40% | 100% | 100% | Adversarial set |
| p50 latency | 2.1s | 2.8s | < 4s | Langfuse |
| p95 latency | 4.8s | 7.1s | < 10s | Langfuse |
| Cost per query | $0.02 | $0.04 | < $0.05 | Langfuse cost tracking |

**Eval methodology:** [2–3 sentences on how you picked the queries, who graded, how you handle disagreement]

**Traces (public):** [Langfuse dashboard URL]

**Known eval set weaknesses:** [honest 2–3 bullets — showing awareness beats hiding]
`,
    rationale: "The eval table is the single highest-signal artifact in the repo. Show it or don't claim the outcome.",
    commonMistakes: [
      "Numbers without a methodology paragraph — looks like vibes",
      "Only success cases, no baseline — no lift to measure",
      "No honest 'known weaknesses' — a sign of naivety",
    ],
  },
  {
    number: 4,
    title: "Failure Modes & Safety",
    purpose: "Senior engineers know what can break. Juniors claim it won't.",
    required: true,
    tierGate: "all_access",
    freeSummary:
      "(All Access) Named failure modes with trigger + mitigation + severity. 3–5 items minimum.",
    templateBody: `## Failure Modes

| Mode | Trigger | Mitigation | Severity |
|---|---|---|---|
| Confident hallucination | Retrieved context tangentially related | Groundedness check + refusal pattern + citation rule | Critical |
| Prompt injection via docs | Retrieved doc contains 'Ignore previous...' | Content in tagged XML; system prompt treats tags as untrusted | High |
| Over-retrieval | Planner decomposes simple query into 8 sub-queries | Sub-query cap of 3; decomposition-needed gate | Medium |

**Security posture:** [2–3 sentences — threat model, scoped credentials, audit log, etc.]
`,
    rationale: "Demonstrates D6 (Safety & Governance) concretely. The #1 missing section in weak portfolios.",
    commonMistakes: [
      "Vague 'we use responsible AI practices' paragraph",
      "No severity grading — hiring manager can't tell if you understand blast radius",
      "Missing prompt-injection mitigation on any RAG or tool-use system",
    ],
  },
  {
    number: 5,
    title: "HITL / Human Gates",
    purpose: "Production agents have humans in the loop. Say where, when, and why.",
    required: true,
    tierGate: "all_access",
    freeSummary:
      "(All Access) Triggers, human decisions, auto-resume rules.",
    templateBody: `## Human-in-the-Loop

**Triggers:**
- [Specific signal, not 'low confidence' — e.g., 'groundedness score < 0.6']
- [Specific signal]
- [Specific signal]

**Human decisions:**
- Approve as-is
- Approve with modification
- Reject and replan / decline

**What happens after:**
[2–3 sentences on learning loop, audit logging, user notification]

**Approval UI:** [Screenshot + Loom link OR description]
`,
    rationale: "The HITL section separates production-minded candidates from those who demo happy paths only.",
    commonMistakes: [
      "'Users can review outputs' — meaningless; where, when, with what UI?",
      "HITL only on explicit user request — misses the safety-triggered case",
      "No audit of approval decisions",
    ],
  },
  {
    number: 6,
    title: "Deployment & Observability",
    purpose: "If it's not deployed with traces, it's a notebook.",
    required: true,
    tierGate: "all_access",
    freeSummary:
      "(All Access) Deployment target, observability stack, cost ceiling, public dashboard link.",
    templateBody: `## Deployment

- **Live demo:** [URL with rate limit or auth]
- **Host:** Fly.io / Railway / Modal / [...]
- **Cost ceiling:** $[N]/month, enforced by [guard mechanism]
- **CI:** GitHub Actions runs eval harness on every PR ([badge])

## Observability

- **Tracing:** [Langfuse / LangSmith / OTel + viewer] — [public dashboard URL]
- **Metrics tracked:** cost, latency, grounded-answer rate, HITL escalation rate
- **Alerts:** [what triggers them, who sees them]

**Sample traces:** [screenshot or Loom]
`,
    rationale: "Deployed + observable = taken seriously. Notebook + claims = junior signal.",
    commonMistakes: [
      "'Deploy with `docker compose up`' — that's local, not deployed",
      "No public trace link — the credibility tell is trace visibility",
      "No cost ceiling documented — signals no production instinct",
    ],
  },
  {
    number: 7,
    title: "What's Next / Honest Limitations",
    purpose: "The 'what I'd do next' section is where senior candidates distinguish themselves.",
    required: true,
    tierGate: "free",
    freeSummary:
      "3–5 specific next iterations + honest limitations. Shows judgment.",
    templateBody: `## What's Next

1. [Specific, measurable iteration — 'Add reranker to raise grounded-answer rate from 84% → 90%']
2. [Specific]
3. [Specific]

## Honest Limitations

- [Specific thing the system does NOT do well + why it's out of scope today]
- [Specific]
- [Specific]

**Why I didn't build X:** [one paragraph — shows scope discipline]
`,
    rationale: "A portfolio project without honest limitations signals naivety. Listing them signals judgment.",
    commonMistakes: [
      "'More features coming soon' — meaningless",
      "No limitations listed — either you don't know them or you're hiding them, both are bad signals",
      "Limitations that are actually strengths in disguise — interviewer will notice",
    ],
  },
  ],
  es: [
    {
      "number": 1,
      "title": "Gancho de una línea + Banner",
      "purpose": "Un responsable de contratación lee los primeros 15 segundos. Engancha o piérdelo.",
      "required": true,
      "tierGate": "free",
      "freeSummary": "La primera línea expresa el resultado en una sola oración. Incluye: qué hace el sistema, la métrica clave y el enlace a la demo en vivo.",
      "templateBody": "# [Nombre del proyecto]\n\n> [Una oración: qué hace + la ÚNICA métrica destacada + URL de la demo]\n> Ejemplo: \"Agentic RAG sobre 5,000 artículos de seguridad de IA — 84% de tasa de respuestas fundamentadas, p95 3.2s. [Demo en vivo](https://demo.url) · [Trazas](https://langfuse.url)\"\n\n![Status badge](...) ![Evals badge](...) ![License](...)\n\n**TL;DR en 3 viñetas:**\n- **Problema:** [una línea]\n- **Enfoque:** [una línea — nombra el patrón, no el framework]\n- **Resultado:** [una línea — la métrica + enlace a la tabla de evaluación de abajo]\n",
      "rationale": "Las primeras 100 palabras deciden si alguien sigue leyendo. Entierra lo importante y pierdes al lector.",
      "commonMistakes": [
        "Empezar con 'Este proyecto explora...'",
        "Enterrar el enlace de la demo en el párrafo 5",
        "Empezar con el stack tecnológico en lugar del resultado"
      ]
    },
    {
      "number": 2,
      "title": "Diagrama de arquitectura",
      "purpose": "Un diagrama de Mermaid vale más que 500 palabras de descripción. Sin diagrama = sin señal.",
      "required": true,
      "tierGate": "free",
      "freeSummary": "Diagrama de Mermaid que muestra agentes, herramientas, almacenes de datos y controles humanos. Un diagrama, no tres.",
      "templateBody": "## Arquitectura\n\n```mermaid\ngraph LR\n  U[User] --> P[Planner]\n  P --> R[Retriever]\n  R --> S[Synthesizer]\n  S --> G[Groundedness Check]\n  G -->|pass| A[Answer]\n  G -->|fail| H[HITL]\n  S --> T[(Traces)]\n```\n\n**Decisiones de diseño clave:**\n1. **Por qué [patrón X] en lugar de [alternativa Y]:** [una oración]\n2. **Por qué [herramienta Z]:** [una oración]\n3. **Qué NO está en el alcance:** [una oración — demuestra disciplina]\n",
      "rationale": "Los ingenieros senior leen diagramas antes que prosa. La presencia de un diagrama es en sí misma una señal.",
      "commonMistakes": [
        "Captura de pantalla de un diagrama dibujado a mano (parece flojo)",
        "3 diagramas superpuestos que no concuerdan",
        "Un diagrama sin conexiones etiquetadas"
      ]
    },
    {
      "number": 3,
      "title": "Tabla de resultados de evaluación",
      "purpose": "El motor de credibilidad. Los números vencen a los adjetivos. Un README sin tabla de evaluación es un proyecto de feria de ciencias.",
      "required": true,
      "tierGate": "all_access",
      "freeSummary": "(All Access) Tabla completa con métricas, línea base, valor actual, objetivo y enlace al ejecutor de evaluaciones.",
      "templateBody": "## Evaluación\n\nConjunto de evaluación: `eval/queries.jsonl` (30 consultas etiquetadas a mano en 5 categorías). Reejecutable con `make eval`.\n\n| Métrica | Línea base | Actual | Objetivo | Cómo se mide |\n|---|---|---|---|---|\n| Tasa de respuestas fundamentadas | 58% | 84% | 85% | Fidelidad de Ragas + verificación manual |\n| Precisión de citas | 72% | 91% | 90% | Verificación manual de 20 al azar |\n| Rechazo fuera del corpus | 40% | 100% | 100% | Conjunto adversarial |\n| Latencia p50 | 2.1s | 2.8s | < 4s | Langfuse |\n| Latencia p95 | 4.8s | 7.1s | < 10s | Langfuse |\n| Costo por consulta | $0.02 | $0.04 | < $0.05 | Seguimiento de costos de Langfuse |\n\n**Metodología de evaluación:** [2–3 oraciones sobre cómo elegiste las consultas, quién calificó, cómo gestionas los desacuerdos]\n\n**Trazas (públicas):** [URL del panel de Langfuse]\n\n**Debilidades conocidas del conjunto de evaluación:** [2–3 viñetas honestas — mostrar conciencia vence a ocultarlas]\n",
      "rationale": "La tabla de evaluación es el artefacto de mayor señal en el repositorio. Muéstrala o no reclames el resultado.",
      "commonMistakes": [
        "Números sin un párrafo de metodología — parece intuición",
        "Solo casos de éxito, sin línea base — no hay mejora que medir",
        "Sin 'debilidades conocidas' honestas — señal de ingenuidad"
      ]
    },
    {
      "number": 4,
      "title": "Modos de fallo y seguridad",
      "purpose": "Los ingenieros senior saben qué puede romperse. Los junior afirman que no pasará.",
      "required": true,
      "tierGate": "all_access",
      "freeSummary": "(All Access) Modos de fallo nombrados con disparador + mitigación + severidad. Mínimo 3–5 elementos.",
      "templateBody": "## Modos de fallo\n\n| Modo | Disparador | Mitigación | Severidad |\n|---|---|---|---|\n| Alucinación segura de sí misma | Contexto recuperado relacionado de forma tangencial | Verificación de fundamentación + patrón de rechazo + regla de citas | Crítica |\n| Inyección de prompt vía documentos | El documento recuperado contiene 'Ignora lo anterior...' | Contenido en XML etiquetado; el system prompt trata las etiquetas como no confiables | Alta |\n| Sobrerecuperación | El planificador descompone una consulta simple en 8 subconsultas | Límite de 3 subconsultas; control de 'descomposición necesaria' | Media |\n\n**Postura de seguridad:** [2–3 oraciones — modelo de amenazas, credenciales de alcance limitado, registro de auditoría, etc.]\n",
      "rationale": "Demuestra D6 (Seguridad y Gobernanza) de forma concreta. La sección número 1 que falta en portafolios débiles.",
      "commonMistakes": [
        "Párrafo vago del tipo 'usamos prácticas de IA responsable'",
        "Sin clasificación de severidad — el responsable de contratación no puede saber si entiendes el radio de impacto",
        "Falta la mitigación de inyección de prompt en cualquier sistema de RAG o uso de herramientas"
      ]
    },
    {
      "number": 5,
      "title": "HITL / Controles humanos",
      "purpose": "Los agentes en producción tienen humanos en el bucle. Di dónde, cuándo y por qué.",
      "required": true,
      "tierGate": "all_access",
      "freeSummary": "(All Access) Disparadores, decisiones humanas, reglas de reanudación automática.",
      "templateBody": "## Human-in-the-Loop\n\n**Disparadores:**\n- [Señal específica, no 'baja confianza' — p. ej., 'puntuación de fundamentación < 0.6']\n- [Señal específica]\n- [Señal específica]\n\n**Decisiones humanas:**\n- Aprobar tal cual\n- Aprobar con modificación\n- Rechazar y replanificar / declinar\n\n**Qué ocurre después:**\n[2–3 oraciones sobre el bucle de aprendizaje, registro de auditoría, notificación al usuario]\n\n**UI de aprobación:** [Captura de pantalla + enlace de Loom O descripción]\n",
      "rationale": "La sección de HITL separa a los candidatos con mentalidad de producción de aquellos que solo demuestran los caminos felices.",
      "commonMistakes": [
        "'Los usuarios pueden revisar las salidas' — sin sentido; ¿dónde, cuándo, con qué UI?",
        "HITL solo por solicitud explícita del usuario — se pierde el caso disparado por seguridad",
        "Sin auditoría de las decisiones de aprobación"
      ]
    },
    {
      "number": 6,
      "title": "Despliegue y observabilidad",
      "purpose": "Si no está desplegado con trazas, es un notebook.",
      "required": true,
      "tierGate": "all_access",
      "freeSummary": "(All Access) Objetivo de despliegue, stack de observabilidad, tope de costos, enlace al panel público.",
      "templateBody": "## Despliegue\n\n- **Demo en vivo:** [URL con límite de tasa o autenticación]\n- **Host:** Fly.io / Railway / Modal / [...]\n- **Tope de costos:** $[N]/mes, aplicado por [mecanismo de protección]\n- **CI:** GitHub Actions ejecuta el arnés de evaluación en cada PR ([badge])\n\n## Observabilidad\n\n- **Trazado:** [Langfuse / LangSmith / OTel + visor] — [URL del panel público]\n- **Métricas rastreadas:** costo, latencia, tasa de respuestas fundamentadas, tasa de escalamiento HITL\n- **Alertas:** [qué las dispara, quién las ve]\n\n**Trazas de ejemplo:** [captura de pantalla o Loom]\n",
      "rationale": "Desplegado + observable = te toman en serio. Notebook + afirmaciones = señal de junior.",
      "commonMistakes": [
        "'Despliega con `docker compose up`' — eso es local, no desplegado",
        "Sin enlace público a las trazas — la señal de credibilidad es la visibilidad de las trazas",
        "Sin tope de costos documentado — indica falta de instinto de producción"
      ]
    },
    {
      "number": 7,
      "title": "Qué sigue / Limitaciones honestas",
      "purpose": "La sección de 'qué haría a continuación' es donde los candidatos senior se distinguen.",
      "required": true,
      "tierGate": "free",
      "freeSummary": "3–5 próximas iteraciones específicas + limitaciones honestas. Demuestra criterio.",
      "templateBody": "## Qué sigue\n\n1. [Iteración específica y medible — 'Añadir un reranker para subir la tasa de respuestas fundamentadas de 84% → 90%']\n2. [Específica]\n3. [Específica]\n\n## Limitaciones honestas\n\n- [Algo específico que el sistema NO hace bien + por qué está fuera del alcance hoy]\n- [Específica]\n- [Específica]\n\n**Por qué no construí X:** [un párrafo — demuestra disciplina de alcance]\n",
      "rationale": "Un proyecto de portafolio sin limitaciones honestas señala ingenuidad. Enumerarlas señala criterio.",
      "commonMistakes": [
        "'Más funciones próximamente' — sin sentido",
        "Ninguna limitación listada — o no las conoces o las estás ocultando, ambas son malas señales",
        "Limitaciones que en realidad son fortalezas disfrazadas — el entrevistador lo notará"
      ]
    }
  ],
};

// ---------------------------------------------------------------------------
// Deployment & Observability Checklist (All Access)
// ---------------------------------------------------------------------------

export interface ChecklistItem {
  id: string;
  category: "deployment" | "observability" | "safety" | "cost" | "documentation";
  item: string;
  rationale: string;
  gate: "must-have" | "should-have" | "nice-to-have";
}

export const DEPLOYMENT_CHECKLIST: Localized<ChecklistItem[]> = {
  en: [
  // Deployment
  { id: "d1", category: "deployment", item: "Public demo URL reachable without a custom clone step", rationale: "A hiring manager won't clone your repo to evaluate. If they can't try it in 30 seconds, they don't.", gate: "must-have" },
  { id: "d2", category: "deployment", item: "Dockerfile or equivalent, reproducible on a clean machine", rationale: "Reproducibility is a basic production signal.", gate: "must-have" },
  { id: "d3", category: "deployment", item: "CI runs eval harness on PR", rationale: "Shows you treat eval as a first-class quality gate, not a one-time artifact.", gate: "must-have" },
  { id: "d4", category: "deployment", item: "Rate limiting on public demo", rationale: "Prevents cost runaway + shows production instinct.", gate: "must-have" },
  { id: "d5", category: "deployment", item: "Separate dev / prod credentials with scoped permissions", rationale: "Principle of least privilege — signals D6 maturity.", gate: "should-have" },

  // Observability
  { id: "o1", category: "observability", item: "Traces visible on a public dashboard (Langfuse / LangSmith / Jaeger / Honeycomb)", rationale: "The single highest-signal observability artifact in 2026.", gate: "must-have" },
  { id: "o2", category: "observability", item: "Cost per request tracked and visible", rationale: "Production agents that can't report cost don't make it to production.", gate: "must-have" },
  { id: "o3", category: "observability", item: "p50 / p95 latency per step, not just end-to-end", rationale: "End-to-end latency hides which agent is the bottleneck.", gate: "must-have" },
  { id: "o4", category: "observability", item: "Error rate tracked with error categories", rationale: "'20% error rate' is less useful than 'groundedness-check rejection 15%, tool timeout 4%, other 1%.'", gate: "should-have" },
  { id: "o5", category: "observability", item: "Sample traces screenshotted in README", rationale: "Not everyone will click through to Langfuse; show the artifact inline.", gate: "should-have" },

  // Safety
  { id: "s1", category: "safety", item: "Prompt-injection mitigation documented for every untrusted-content surface", rationale: "If your system reads retrieved docs, user inputs, or emails, injection is a live threat.", gate: "must-have" },
  { id: "s2", category: "safety", item: "HITL gates on irreversible actions (for workflow agents)", rationale: "No HITL on irreversible = cosplay safety.", gate: "must-have" },
  { id: "s3", category: "safety", item: "Refusal behavior tested with adversarial eval set", rationale: "Graceful refusal is a skill you can only claim if you tested it adversarially.", gate: "must-have" },
  { id: "s4", category: "safety", item: "Audit log for action-taking agents, tamper-resistant", rationale: "Without audit you can't reason about what the agent has done.", gate: "should-have" },

  // Cost
  { id: "c1", category: "cost", item: "Monthly cost ceiling documented + enforced", rationale: "Shows production instinct. Also protects your wallet on a public demo.", gate: "must-have" },
  { id: "c2", category: "cost", item: "Cheaper model (Haiku / Mini) used in loop-heavy steps (grader, classifier, rewriter)", rationale: "Cost discipline separates seniors from intermediates.", gate: "should-have" },
  { id: "c3", category: "cost", item: "Prompt caching or batching used where applicable", rationale: "Shows awareness of API-level cost optimization.", gate: "nice-to-have" },

  // Documentation
  { id: "doc1", category: "documentation", item: "README follows the 7-section template", rationale: "Consistency makes scanning faster for reviewers.", gate: "must-have" },
  { id: "doc2", category: "documentation", item: "Architecture diagram in Mermaid (not an image)", rationale: "Mermaid renders on GitHub and is editable — image diagrams rot.", gate: "must-have" },
  { id: "doc3", category: "documentation", item: "Eval methodology paragraph explains how queries were picked and graded", rationale: "Numbers without methodology read as fabricated.", gate: "must-have" },
  { id: "doc4", category: "documentation", item: "'What I'd do next' and 'Honest limitations' sections present", rationale: "Signals judgment and scope discipline.", gate: "should-have" },
  { id: "doc5", category: "documentation", item: "Loom / video walkthrough linked (2–5 min)", rationale: "Asynchronous walkthroughs beat README reads for busy reviewers.", gate: "nice-to-have" },
  ],
  es: [
    {
      "id": "d1",
      "category": "deployment",
      "item": "URL de demo pública accesible sin un paso de clonación personalizado",
      "rationale": "Un responsable de contratación no clonará tu repo para evaluarlo. Si no puede probarlo en 30 segundos, no lo hace.",
      "gate": "must-have"
    },
    {
      "id": "d2",
      "category": "deployment",
      "item": "Dockerfile o equivalente, reproducible en una máquina limpia",
      "rationale": "La reproducibilidad es una señal básica de producción.",
      "gate": "must-have"
    },
    {
      "id": "d3",
      "category": "deployment",
      "item": "CI ejecuta el arnés de evaluación en cada PR",
      "rationale": "Demuestra que tratas la evaluación como una barrera de calidad de primer nivel, no como un artefacto único.",
      "gate": "must-have"
    },
    {
      "id": "d4",
      "category": "deployment",
      "item": "Limitación de tasa en la demo pública",
      "rationale": "Previene el descontrol de costos + demuestra instinto de producción.",
      "gate": "must-have"
    },
    {
      "id": "d5",
      "category": "deployment",
      "item": "Credenciales separadas de dev / prod con permisos acotados",
      "rationale": "Principio de mínimo privilegio: señala madurez de D6.",
      "gate": "should-have"
    },
    {
      "id": "o1",
      "category": "observability",
      "item": "Trazas visibles en un panel público (Langfuse / LangSmith / Jaeger / Honeycomb)",
      "rationale": "El artefacto de observabilidad con mayor señal en 2026.",
      "gate": "must-have"
    },
    {
      "id": "o2",
      "category": "observability",
      "item": "Costo por solicitud rastreado y visible",
      "rationale": "Los agentes de producción que no pueden reportar el costo no llegan a producción.",
      "gate": "must-have"
    },
    {
      "id": "o3",
      "category": "observability",
      "item": "Latencia p50 / p95 por paso, no solo de extremo a extremo",
      "rationale": "La latencia de extremo a extremo oculta cuál agente es el cuello de botella.",
      "gate": "must-have"
    },
    {
      "id": "o4",
      "category": "observability",
      "item": "Tasa de error rastreada con categorías de error",
      "rationale": "'Tasa de error del 20%' es menos útil que 'rechazo de verificación de fundamentación 15%, timeout de herramienta 4%, otros 1%.'",
      "gate": "should-have"
    },
    {
      "id": "o5",
      "category": "observability",
      "item": "Capturas de trazas de muestra en el README",
      "rationale": "No todos harán clic para ir a Langfuse; muestra el artefacto en línea.",
      "gate": "should-have"
    },
    {
      "id": "s1",
      "category": "safety",
      "item": "Mitigación de inyección de prompts documentada para cada superficie de contenido no confiable",
      "rationale": "Si tu sistema lee documentos recuperados, entradas de usuario o correos, la inyección es una amenaza activa.",
      "gate": "must-have"
    },
    {
      "id": "s2",
      "category": "safety",
      "item": "Barreras HITL en acciones irreversibles (para agentes de flujo de trabajo)",
      "rationale": "Sin HITL en lo irreversible = seguridad de fachada.",
      "gate": "must-have"
    },
    {
      "id": "s3",
      "category": "safety",
      "item": "Comportamiento de rechazo probado con un conjunto de evaluación adversarial",
      "rationale": "El rechazo elegante es una habilidad que solo puedes reclamar si la probaste de forma adversarial.",
      "gate": "must-have"
    },
    {
      "id": "s4",
      "category": "safety",
      "item": "Registro de auditoría para agentes que ejecutan acciones, resistente a manipulaciones",
      "rationale": "Sin auditoría no puedes razonar sobre lo que ha hecho el agente.",
      "gate": "should-have"
    },
    {
      "id": "c1",
      "category": "cost",
      "item": "Techo de costo mensual documentado + aplicado",
      "rationale": "Demuestra instinto de producción. También protege tu billetera en una demo pública.",
      "gate": "must-have"
    },
    {
      "id": "c2",
      "category": "cost",
      "item": "Modelo más económico (Haiku / Mini) usado en pasos con muchos bucles (evaluador, clasificador, reescritor)",
      "rationale": "La disciplina de costos separa a los seniors de los intermedios.",
      "gate": "should-have"
    },
    {
      "id": "c3",
      "category": "cost",
      "item": "Caché de prompts o procesamiento por lotes usado donde corresponda",
      "rationale": "Demuestra conciencia de la optimización de costos a nivel de API.",
      "gate": "nice-to-have"
    },
    {
      "id": "doc1",
      "category": "documentation",
      "item": "El README sigue la plantilla de 7 secciones",
      "rationale": "La consistencia acelera la revisión para quienes evalúan.",
      "gate": "must-have"
    },
    {
      "id": "doc2",
      "category": "documentation",
      "item": "Diagrama de arquitectura en Mermaid (no una imagen)",
      "rationale": "Mermaid se renderiza en GitHub y es editable; los diagramas en imagen se degradan.",
      "gate": "must-have"
    },
    {
      "id": "doc3",
      "category": "documentation",
      "item": "El párrafo de metodología de evaluación explica cómo se eligieron y calificaron las consultas",
      "rationale": "Los números sin metodología se leen como inventados.",
      "gate": "must-have"
    },
    {
      "id": "doc4",
      "category": "documentation",
      "item": "Presencia de las secciones 'Qué haría a continuación' y 'Limitaciones honestas'",
      "rationale": "Señala criterio y disciplina de alcance.",
      "gate": "should-have"
    },
    {
      "id": "doc5",
      "category": "documentation",
      "item": "Recorrido en Loom / video enlazado (2–5 min)",
      "rationale": "Los recorridos asincrónicos superan a la lectura del README para revisores ocupados.",
      "gate": "nice-to-have"
    }
  ],
};

// ---------------------------------------------------------------------------
// Lovable Portfolio Site Prompt (Track A — All Access)
// ---------------------------------------------------------------------------

export interface LovablePromptSection {
  title: string;
  description: string;
  promptBlock: string;
}

export interface LovablePortfolioPromptContent {
  overview: string;
  sections: LovablePromptSection[];
  adminSchemaTables: string[];
  deploymentNotes: string[];
}

export const LOVABLE_PORTFOLIO_PROMPT: Localized<LovablePortfolioPromptContent> = {
  en: {
  overview:
    "Paste these prompt blocks into Lovable sequentially. Each block builds one surface of the portfolio. The goal is a dark-theme AI portfolio site with an Honest Fit Assessment chatbot trained on your career history, a JD analyzer, and a candidate-only admin panel for context management.",
  sections: [
    {
      title: "Block 1 — Foundation",
      description: "Project scaffold, dark theme, routing.",
      promptBlock: `Create a personal AI portfolio site with:
- Dark theme (background #0A0E1A, accent #00D9A3 for green signal, muted text #9CA3AF)
- Routes: / (hero), /experience, /projects, /skills, /jd-analyzer, /chat, /admin (auth-gated)
- React + Vite + Tailwind + shadcn/ui
- Supabase for auth (magic link) and Postgres for candidate context storage
- Serif headings (Fraunces), sans body (Inter)
- Footer with: GitHub link, LinkedIn, email, "Built with AI — see how" modal that shows the architecture`,
    },
    {
      title: "Block 2 — Hero + Experience",
      description: "The above-the-fold pitch + work history with AI context toggles.",
      promptBlock: `Build the hero section with:
- H1: candidate name + current role (pulled from admin panel)
- One-sentence positioning line (admin-editable)
- Three pill badges: seniority band, domain focus, availability
- Primary CTA: "Start Honest Fit Assessment" → opens AI chat drawer
- Secondary CTA: "Analyze a Job Description" → /jd-analyzer

Build the Experience section with:
- Timeline of roles (admin-editable)
- Each role: company, title, dates, 3 outcome bullets
- "AI Context" toggle per role that reveals internal notes (only visible when chat agent quotes them; not shown to public visitors)
- Recognized-company logos where applicable (admin uploads)`,
    },
    {
      title: "Block 3 — Skills Matrix",
      description: "Honest, calibrated skills with Strong / Moderate / Gap bands.",
      promptBlock: `Build the Skills Matrix as a 3-column layout:
- Column 1: Strong (green, pulled from admin)
- Column 2: Moderate (yellow)
- Column 3: Gaps / Learning (gray)
- Each skill links to evidence: a project, a repo, or "reviewing now" for gaps
- Filter by domain: Agentic AI / MLOps / Full-Stack / Product
- Honest-fit principle: the Gaps column is a feature, not a bug. Hiring managers trust calibrated candidates.`,
    },
    {
      title: "Block 4 — Projects with Metrics",
      description: "Project cards that actually show metrics, not marketing.",
      promptBlock: `Build the Projects section as cards. Each card shows:
- Project title + one-line hook
- Architecture diagram thumbnail (Mermaid rendered)
- ONE headline metric (eval score, parallelism gain, cost — chosen by candidate)
- Three tech-stack tags max
- Link to GitHub repo + live demo + Loom walkthrough + Langfuse traces (if applicable)
- "Why this matters" 2-sentence explanation linked to a 7-domain evidence rollup

Admin panel lets candidate add projects with all above fields + evidence mapping to D1–D7.`,
    },
    {
      title: "Block 5 — JD Analyzer",
      description: "Paste a JD, get an honest fit breakdown.",
      promptBlock: `Build /jd-analyzer:
- Large textarea: "Paste a job description"
- Button: "Analyze fit"
- On submit, call Supabase Edge Function that sends JD + candidate's structured context to Claude/GPT
- Response renders as:
  * Strong matches (3–5 bullets)
  * Moderate matches (2–4 bullets)
  * Genuine gaps (1–3 bullets — honest, not hidden)
  * Recommended talking points for interview (3)
  * A numerical "Honest Fit Score" 0–100 with methodology link
- Every claim must cite a candidate-context source ID — no hallucinated experience
- System prompt enforces: "If the JD asks for something NOT in the candidate's context, say so explicitly. Do not invent."`,
    },
    {
      title: "Block 6 — AI Chat Drawer (Honest Fit Assessment)",
      description: "The differentiator — a chatbot that refuses to lie about the candidate.",
      promptBlock: `Build a slide-in drawer chat interface triggered from hero + nav:
- Opening message: "I'm [name]'s portfolio agent. I'll answer honestly about fit, including gaps. What role are you considering?"
- Every response grounds answers in structured candidate context (experience, skills, projects) stored in Supabase
- System prompt explicitly forbids: inventing experience, inflating years, hiding gaps
- Responses include inline citations: "[Role at Acme, 2022–2024]" clickable → opens that role
- Refusal pattern: "I don't have evidence that [name] has done X. Want me to connect you with [name] to ask directly?"
- Rate limited (Supabase Edge Function with Upstash)
- Every conversation logged (candidate-visible, not public) so candidate can see what recruiters are asking`,
    },
    {
      title: "Block 7 — Candidate Admin Panel",
      description: "The back-office where the candidate owns their own ground truth.",
      promptBlock: `Build /admin (Supabase auth-gated, single-user):
- Section 1 — Identity: name, current role, positioning line, seniority band
- Section 2 — Experience: CRUD list of roles with outcome bullets + AI context notes
- Section 3 — Skills: CRUD list with Strong/Moderate/Gap band + evidence link
- Section 4 — Projects: CRUD list with all fields from Block 4 + 7-domain evidence
- Section 5 — Narratives: free-text career story, values, what I'm looking for (fed to chat agent)
- Section 6 — JD Analyzer Settings: override weights (e.g., 'years of experience is less important to me than pattern recognition')
- Section 7 — Chat History: read-only view of recruiter conversations with the agent
- Section 8 — Deploy: "Publish changes" button that invalidates Edge Function cache`,
    },
    {
      title: "Block 8 — Integration & Deploy",
      description: "Supabase wiring, Edge Functions, deployment.",
      promptBlock: `Wire the integrations:
- Supabase tables: profiles, experiences, skills, projects, narratives, chat_messages, jd_analyses
- Supabase Edge Function: /analyze-jd (uses ANTHROPIC_API_KEY or OPENAI_API_KEY from secrets)
- Supabase Edge Function: /chat (streaming, uses candidate_id to load full context)
- Row-level security: public can read profiles/experiences/skills/projects; chat_messages readable only by candidate
- Deploy to Vercel or Lovable hosting
- Custom domain setup docs in README
- Cost ceiling: $20/month with rate limiting`,
    },
  ],
  adminSchemaTables: [
    "profiles(id, name, current_role, positioning_line, seniority_band, availability, created_at)",
    "experiences(id, profile_id, company, title, start_date, end_date, outcome_bullets[], ai_context_notes, display_order)",
    "skills(id, profile_id, name, domain, band: 'strong'|'moderate'|'gap', evidence_link, display_order)",
    "projects(id, profile_id, title, hook, architecture_diagram_url, headline_metric, tech_tags[], github_url, demo_url, loom_url, traces_url, why_it_matters, domain_evidence: jsonb)",
    "narratives(id, profile_id, type: 'story'|'values'|'looking_for', body_markdown)",
    "chat_messages(id, profile_id, session_id, role: 'user'|'assistant', content, cited_sources: jsonb, created_at)",
    "jd_analyses(id, profile_id, jd_text, strong_matches: jsonb, moderate_matches: jsonb, gaps: jsonb, fit_score, created_at)",
  ],
  deploymentNotes: [
    "Use Supabase Edge Functions — cheaper than spinning up a dedicated backend for LLM calls",
    "Store ANTHROPIC_API_KEY / OPENAI_API_KEY in Supabase secrets, never in client code",
    "Row-level security on chat_messages is non-negotiable — recruiter conversations are sensitive context",
    "Rate limit the chat + JD analyzer aggressively (10 requests / hour / IP) to prevent cost runaway from public exposure",
    "Use Haiku or GPT-4o-mini for the chat (cost); reserve Sonnet/Opus for JD analyzer (stakes)",
    "Log every LLM call's cost + latency — you'll want this for your own resume",
  ],
  },
  es: {
    "overview": "Pega estos bloques de prompt en Lovable de forma secuencial. Cada bloque construye una superficie del portafolio. El objetivo es un sitio de portafolio de IA con tema oscuro, con un chatbot de Honest Fit Assessment entrenado con tu historial profesional, un analizador de descripciones de puesto y un panel de administración exclusivo para el candidato para la gestión de contexto.",
    "sections": [
      {
        "title": "Bloque 1 — Fundamentos",
        "description": "Andamiaje del proyecto, tema oscuro, enrutamiento.",
        "promptBlock": "Create a personal AI portfolio site with:\n- Dark theme (background #0A0E1A, accent #00D9A3 for green signal, muted text #9CA3AF)\n- Routes: / (hero), /experience, /projects, /skills, /jd-analyzer, /chat, /admin (auth-gated)\n- React + Vite + Tailwind + shadcn/ui\n- Supabase for auth (magic link) and Postgres for candidate context storage\n- Serif headings (Fraunces), sans body (Inter)\n- Footer with: GitHub link, LinkedIn, email, \"Built with AI — see how\" modal that shows the architecture"
      },
      {
        "title": "Bloque 2 — Hero + Experiencia",
        "description": "El pitch visible sin desplazamiento + historial laboral con conmutadores de contexto de IA.",
        "promptBlock": "Build the hero section with:\n- H1: candidate name + current role (pulled from admin panel)\n- One-sentence positioning line (admin-editable)\n- Three pill badges: seniority band, domain focus, availability\n- Primary CTA: \"Start Honest Fit Assessment\" → opens AI chat drawer\n- Secondary CTA: \"Analyze a Job Description\" → /jd-analyzer\n\nBuild the Experience section with:\n- Timeline of roles (admin-editable)\n- Each role: company, title, dates, 3 outcome bullets\n- \"AI Context\" toggle per role that reveals internal notes (only visible when chat agent quotes them; not shown to public visitors)\n- Recognized-company logos where applicable (admin uploads)"
      },
      {
        "title": "Bloque 3 — Matriz de habilidades",
        "description": "Habilidades honestas y calibradas con bandas Fuerte / Moderada / Brecha.",
        "promptBlock": "Build the Skills Matrix as a 3-column layout:\n- Column 1: Strong (green, pulled from admin)\n- Column 2: Moderate (yellow)\n- Column 3: Gaps / Learning (gray)\n- Each skill links to evidence: a project, a repo, or \"reviewing now\" for gaps\n- Filter by domain: Agentic AI / MLOps / Full-Stack / Product\n- Honest-fit principle: the Gaps column is a feature, not a bug. Hiring managers trust calibrated candidates."
      },
      {
        "title": "Bloque 4 — Proyectos con métricas",
        "description": "Tarjetas de proyecto que muestran métricas reales, no marketing.",
        "promptBlock": "Build the Projects section as cards. Each card shows:\n- Project title + one-line hook\n- Architecture diagram thumbnail (Mermaid rendered)\n- ONE headline metric (eval score, parallelism gain, cost — chosen by candidate)\n- Three tech-stack tags max\n- Link to GitHub repo + live demo + Loom walkthrough + Langfuse traces (if applicable)\n- \"Why this matters\" 2-sentence explanation linked to a 7-domain evidence rollup\n\nAdmin panel lets candidate add projects with all above fields + evidence mapping to D1–D7."
      },
      {
        "title": "Bloque 5 — Analizador de descripciones de puesto",
        "description": "Pega una descripción de puesto y obtén un desglose honesto de adecuación.",
        "promptBlock": "Build /jd-analyzer:\n- Large textarea: \"Paste a job description\"\n- Button: \"Analyze fit\"\n- On submit, call Supabase Edge Function that sends JD + candidate's structured context to Claude/GPT\n- Response renders as:\n  * Strong matches (3–5 bullets)\n  * Moderate matches (2–4 bullets)\n  * Genuine gaps (1–3 bullets — honest, not hidden)\n  * Recommended talking points for interview (3)\n  * A numerical \"Honest Fit Score\" 0–100 with methodology link\n- Every claim must cite a candidate-context source ID — no hallucinated experience\n- System prompt enforces: \"If the JD asks for something NOT in the candidate's context, say so explicitly. Do not invent.\""
      },
      {
        "title": "Bloque 6 — Cajón de chat de IA (Honest Fit Assessment)",
        "description": "El diferenciador: un chatbot que se niega a mentir sobre el candidato.",
        "promptBlock": "Build a slide-in drawer chat interface triggered from hero + nav:\n- Opening message: \"I'm [name]'s portfolio agent. I'll answer honestly about fit, including gaps. What role are you considering?\"\n- Every response grounds answers in structured candidate context (experience, skills, projects) stored in Supabase\n- System prompt explicitly forbids: inventing experience, inflating years, hiding gaps\n- Responses include inline citations: \"[Role at Acme, 2022–2024]\" clickable → opens that role\n- Refusal pattern: \"I don't have evidence that [name] has done X. Want me to connect you with [name] to ask directly?\"\n- Rate limited (Supabase Edge Function with Upstash)\n- Every conversation logged (candidate-visible, not public) so candidate can see what recruiters are asking"
      },
      {
        "title": "Bloque 7 — Panel de administración del candidato",
        "description": "El back-office donde el candidato es dueño de su propia verdad fundamental.",
        "promptBlock": "Build /admin (Supabase auth-gated, single-user):\n- Section 1 — Identity: name, current role, positioning line, seniority band\n- Section 2 — Experience: CRUD list of roles with outcome bullets + AI context notes\n- Section 3 — Skills: CRUD list with Strong/Moderate/Gap band + evidence link\n- Section 4 — Projects: CRUD list with all fields from Block 4 + 7-domain evidence\n- Section 5 — Narratives: free-text career story, values, what I'm looking for (fed to chat agent)\n- Section 6 — JD Analyzer Settings: override weights (e.g., 'years of experience is less important to me than pattern recognition')\n- Section 7 — Chat History: read-only view of recruiter conversations with the agent\n- Section 8 — Deploy: \"Publish changes\" button that invalidates Edge Function cache"
      },
      {
        "title": "Bloque 8 — Integración y despliegue",
        "description": "Cableado de Supabase, Edge Functions, despliegue.",
        "promptBlock": "Wire the integrations:\n- Supabase tables: profiles, experiences, skills, projects, narratives, chat_messages, jd_analyses\n- Supabase Edge Function: /analyze-jd (uses ANTHROPIC_API_KEY or OPENAI_API_KEY from secrets)\n- Supabase Edge Function: /chat (streaming, uses candidate_id to load full context)\n- Row-level security: public can read profiles/experiences/skills/projects; chat_messages readable only by candidate\n- Deploy to Vercel or Lovable hosting\n- Custom domain setup docs in README\n- Cost ceiling: $20/month with rate limiting"
      }
    ],
    "adminSchemaTables": [
      "profiles(id, name, current_role, positioning_line, seniority_band, availability, created_at)",
      "experiences(id, profile_id, company, title, start_date, end_date, outcome_bullets[], ai_context_notes, display_order)",
      "skills(id, profile_id, name, domain, band: 'strong'|'moderate'|'gap', evidence_link, display_order)",
      "projects(id, profile_id, title, hook, architecture_diagram_url, headline_metric, tech_tags[], github_url, demo_url, loom_url, traces_url, why_it_matters, domain_evidence: jsonb)",
      "narratives(id, profile_id, type: 'story'|'values'|'looking_for', body_markdown)",
      "chat_messages(id, profile_id, session_id, role: 'user'|'assistant', content, cited_sources: jsonb, created_at)",
      "jd_analyses(id, profile_id, jd_text, strong_matches: jsonb, moderate_matches: jsonb, gaps: jsonb, fit_score, created_at)"
    ],
    "deploymentNotes": [
      "Usa Supabase Edge Functions: más económico que levantar un backend dedicado para las llamadas al LLM",
      "Almacena ANTHROPIC_API_KEY / OPENAI_API_KEY en los secrets de Supabase, nunca en el código del cliente",
      "La seguridad a nivel de fila en chat_messages es innegociable: las conversaciones con reclutadores son contexto sensible",
      "Limita la tasa del chat + analizador de descripciones de puesto de forma agresiva (10 solicitudes / hora / IP) para evitar el desbordamiento de costos por la exposición pública",
      "Usa Haiku o GPT-4o-mini para el chat (costo); reserva Sonnet/Opus para el analizador de descripciones de puesto (lo que está en juego)",
      "Registra el costo + la latencia de cada llamada al LLM: lo querrás para tu propio currículum"
    ]
  },
};

// ---------------------------------------------------------------------------
// Exemplar persona — Jordan Martinez
// ---------------------------------------------------------------------------

export const EXEMPLAR_PERSONA: Localized<{
  name: string;
  title: string;
  positioning: string;
  band: string;
  availability: string;
  focus: string;
  exemplarProjects: string[];
  strongSkills: string[];
  moderateSkills: string[];
  gapsInProgress: string[];
}> = {
  en: {
  name: "Jordan Martinez",
  title: "Staff AI Engineer",
  positioning:
    "Agentic systems engineer who's shipped production multi-agent research tooling and HITL workflow agents. Calibrated on what LLMs can and can't do reliably.",
  band: "Staff",
  availability: "Open to Staff / Principal AI Engineer roles — remote US-preferred",
  focus: "Multi-agent orchestration · Agentic RAG · Safety-gated workflow automation",
  exemplarProjects: [
    "Agentic RAG over AI safety papers — 84% grounded, Langfuse traces public",
    "Multi-agent researcher (Anthropic LeadResearcher pattern) — 4.2/5 completeness, 3.8× parallelism gain",
    "HITL GitHub workflow agent — 0% approval-gate miss on 30-intent adversarial eval set",
  ],
  strongSkills: [
    "LangGraph / custom asyncio orchestration",
    "Agentic RAG design",
    "OpenTelemetry + Langfuse for LLM tracing",
    "Prompt-injection defense",
    "HITL pattern design",
    "Claude API (Sonnet + Haiku cost-tiering)",
  ],
  moderateSkills: [
    "Model fine-tuning (LoRA)",
    "Multi-tenant infra",
    "Voice agents",
  ],
  gapsInProgress: [
    "On-device LLM inference (reviewing MLX)",
    "Formal verification of agent behavior",
  ],
  },
  es: {
    "name": "Jordan Martinez",
    "title": "Staff AI Engineer",
    "positioning": "Ingeniero de sistemas agénticos que ha lanzado a producción herramientas de investigación multiagente y agentes de flujos de trabajo con HITL. Calibrado sobre lo que los LLM pueden y no pueden hacer de forma confiable.",
    "band": "Staff",
    "availability": "Abierto a roles de Staff / Principal AI Engineer — remoto, preferentemente en EE. UU.",
    "focus": "Orquestación multiagente · Agentic RAG · Automatización de flujos de trabajo con controles de seguridad",
    "exemplarProjects": [
      "Agentic RAG sobre artículos de seguridad de IA — 84 % fundamentado, trazas de Langfuse públicas",
      "Investigador multiagente (patrón LeadResearcher de Anthropic) — 4.2/5 en exhaustividad, ganancia de paralelismo de 3.8×",
      "Agente de flujo de trabajo HITL en GitHub — 0 % de fallos en la compuerta de aprobación sobre un conjunto adversarial de evaluación de 30 intenciones"
    ],
    "strongSkills": [
      "LangGraph / orquestación asyncio personalizada",
      "Diseño de Agentic RAG",
      "OpenTelemetry + Langfuse para trazado de LLM",
      "Defensa contra inyección de prompts",
      "Diseño de patrones HITL",
      "Claude API (escalonamiento de costos con Sonnet + Haiku)"
    ],
    "moderateSkills": [
      "Ajuste fino de modelos (LoRA)",
      "Infraestructura multiinquilino",
      "Agentes de voz"
    ],
    "gapsInProgress": [
      "Inferencia de LLM en dispositivo (revisando MLX)",
      "Verificación formal del comportamiento de agentes"
    ]
  },
};
